import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Sparkles, Plus, X, Loader2, Video, TrendingUp, FileText, CheckCircle, AlertCircle, BookOpen, ClipboardList, Users, MessageSquare, Edit, Trash2, Share2, Search, ArrowUp, ArrowDown, LogOut, User, Settings, ExternalLink, CreditCard, Calendar, Shield, MessageCircle, Star, HelpCircle, Navigation, UserCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import GuidedTour from '../components/GuidedTour';
import OnboardingCelebration from '../components/OnboardingCelebration';
import CountdownDisplay from '../components/CountdownDisplay';
import ActiveJobsBanner from '../components/ActiveJobsBanner';
import VideoPreviewModal from '../components/VideoPreviewModal';
import ThemeToggle from '../components/ThemeToggle';
import { useTheme } from '../contexts/ThemeContext';

/** Generated trend tags shown on cards (trend_elements only — not type/anchor/hashtags) */
function extractTrendTagsForFilter(trend) {
  const tags = new Set();
  if (!trend) return [];
  let els = trend.trend_elements;
  if (typeof els === 'string') {
    try {
      els = JSON.parse(els);
    } catch (e) {
      els = null;
    }
  }
  if (!Array.isArray(els)) return [];
  els.forEach((el) => {
    if (el == null) return;
    const s =
      typeof el === 'string'
        ? el.trim()
        : typeof el === 'object' && el != null && (el.label != null || el.name != null)
          ? String(el.label ?? el.name).trim()
          : String(el).trim();
    if (s) tags.add(s);
  });
  return Array.from(tags).filter(Boolean);
}

function readTrendFiltersFromSearchParams(sp) {
  const legacyTags = sp.get('tags');
  let tags = sp.getAll('tags').filter(Boolean);
  if (tags.length === 0 && legacyTags) {
    tags = legacyTags.split(',').map((s) => s.trim()).filter(Boolean);
  }
  let creators = sp.getAll('creators').filter(Boolean);
  const legacyCreator = sp.get('creator');
  if (creators.length === 0 && legacyCreator && legacyCreator.trim()) {
    creators = [legacyCreator.trim()];
  }
  return {
    from: sp.get('from') || '',
    to: sp.get('to') || '',
    tags,
    creators
  };
}

/** Start of local calendar day for YYYY-MM-DD */
function parseYmdStartMs(ymd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  const t = new Date(`${ymd}T00:00:00`).getTime();
  return Number.isNaN(t) ? null : t;
}

/** End of local calendar day for YYYY-MM-DD */
function parseYmdEndMs(ymd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  const t = new Date(`${ymd}T23:59:59.999`).getTime();
  return Number.isNaN(t) ? null : t;
}

/** Same logic as in-component display helper; used for trend list creator filter. */
function extractCreatorUsername(video) {
  if (!video) return 'unknown';
  if (video.apify_metadata) {
    try {
      const meta =
        typeof video.apify_metadata === 'string' ? JSON.parse(video.apify_metadata) : video.apify_metadata;
      const username =
        meta?.authorMeta?.uniqueId ||
        meta?.authorMeta?.name ||
        meta?.author?.username ||
        meta?.username;
      if (username) return String(username);
    } catch (e) {
      /* ignore */
    }
  }
  if (video.apify_author_username) return String(video.apify_author_username);
  const urlMatch = video.source_url?.match(/@([^/?]+)/);
  if (urlMatch) return urlMatch[1];
  return 'unknown';
}

const TrendSpotter = () => {
  const { user, logout, getEffectiveUserEmail, actAsEmail, setActAsEmail, isAdmin, ADMIN_VIEW_AS_EMAILS, ADMIN_VIEW_ALL } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize view from URL parameter, default to 'trends'
  const [view, setView] = useState(() => {
    const viewParam = searchParams.get('view');
    if (viewParam === 'library' || viewParam === 'trends' || viewParam === 'board') {
      return viewParam;
    }
    return 'trends';
  });
  const [libraryVideos, setLibraryVideos] = useState([]);
  const [libraryPagination, setLibraryPagination] = useState({ page: 1, hasMore: false, loadingMore: false, total: 0 });
  const [libraryLoadAttempted, setLibraryLoadAttempted] = useState(false); // true after first fetch so we show samples only when user has none
  const [sampleVideos, setSampleVideos] = useState([]); // 5 sample viral videos from different trends (Swipe File inspiration)
  const [selectedVideos, setSelectedVideos] = useState(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [trends, setTrends] = useState([]);
  const [selectedTrend, setSelectedTrend] = useState(null);
  const [trendVideos, setTrendVideos] = useState([]); // All videos for the current trend
  const [trendTopVideos, setTrendTopVideos] = useState({}); // Map of trend ID to top 3 videos
  const [briefs, setBriefs] = useState([]);
  const [selectedBrief, setSelectedBrief] = useState(null);
  const [showBriefModal, setShowBriefModal] = useState(false);
  const [showBriefDetailModal, setShowBriefDetailModal] = useState(false);
  const [briefModalMode, setBriefModalMode] = useState('create'); // 'create' or 'review'
  const [draggedBrief, setDraggedBrief] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOverStatus, setDragOverStatus] = useState(null);
  const [dragOverBriefId, setDragOverBriefId] = useState(null);
  const justDraggedRef = useRef(false);
  const [showManualAddModal, setShowManualAddModal] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [showNewTrendModal, setShowNewTrendModal] = useState(false);
  const [showVideoSelectionModal, setShowVideoSelectionModal] = useState(false);
  const [showBriefVideoSelectionModal, setShowBriefVideoSelectionModal] = useState(false);
  const hasScrolledToFirstVideoRef = useRef(false);
  const [videoSelectionMode, setVideoSelectionMode] = useState(null); // 'new-trend' or { type: 'add-to-trend', trendId: number }
  const [selectedVideosForModal, setSelectedVideosForModal] = useState(new Set());
  const [manualLinks, setManualLinks] = useState('');
  const [trendName, setTrendName] = useState('');
  const [isEditingTrendName, setIsEditingTrendName] = useState(false);
  const [regenerateFeedback, setRegenerateFeedback] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeJobs, setActiveJobs] = useState([]); // Track running jobs with individual timers
  const [jobPollingInterval, setJobPollingInterval] = useState(null);
  const [jobTimers, setJobTimers] = useState(new Map()); // Map of jobId -> { startTime, estimatedSeconds, elapsedSeconds }
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [isEditingAngle, setIsEditingAngle] = useState(false);
  const [isEditingShots, setIsEditingShots] = useState(false);
  const [isEditingCaption, setIsEditingCaption] = useState(false);
  const [isEditingSoundLink, setIsEditingSoundLink] = useState(false);
  const [editingTemplateField, setEditingTemplateField] = useState(null); // 'text_overlay' | 'people_characters' | 'locations' | 'voiceover_script' | 'sound_effects'
  const [commentInputRef, setCommentInputRef] = useState(null);
  const [generatedIdeas, setGeneratedIdeas] = useState([]);
  const [userLimits, setUserLimits] = useState({ videoCount: 0, ideaCount: 0, hasPaid: false, exceeded: false });
  const [showGeneratedIdeasModal, setShowGeneratedIdeasModal] = useState(false);
  const [showCreateIdeaModal, setShowCreateIdeaModal] = useState(false); // Popup to generate ideas from trend card (same workflow as detail page)
  const [ideaTimer, setIdeaTimer] = useState(null); // Timer for idea generation (seconds remaining)
  const [trendTimer, setTrendTimer] = useState(null); // Timer for trend generation (seconds remaining)
  const [videoTimer, setVideoTimer] = useState(null); // Timer for video upload (seconds remaining)
  const [selectedIdeasForBoard, setSelectedIdeasForBoard] = useState(new Set());
  const [editingIdeaField, setEditingIdeaField] = useState(null); // { ideaId: string, field: 'angle' | 'shots' | 'caption' | 'notes' }
  const [showBrandSettingsModal, setShowBrandSettingsModal] = useState(false);
  /** 'auto' when opened by 3rd+ login nudge; null when opened from menu */
  const [brandSettingsModalSource, setBrandSettingsModalSource] = useState(null);
  const [populatingBrandSettings, setPopulatingBrandSettings] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showViewAsMenu, setShowViewAsMenu] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loadingSubscriptionStatus, setLoadingSubscriptionStatus] = useState(false);
  const [creatingCheckout, setCreatingCheckout] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [videoPreview, setVideoPreview] = useState(null); // { videoUrl, creator, platform }
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [sharePermission, setSharePermission] = useState('view');
  const [shareError, setShareError] = useState('');
  const [boardFilter, setBoardFilter] = useState('all'); // 'all' | 'mine' | 'shared'
  const [showIdeaLimitModal, setShowIdeaLimitModal] = useState(false); // popup when user hits limit and clicks New Brief
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [favoriteFeatures, setFavoriteFeatures] = useState([]);
  const [topAsks, setTopAsks] = useState('');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [tagInput, setTagInput] = useState(''); // For tag input in brief modal
  const [selectedTags, setSelectedTags] = useState([]); // Selected tags for filtering
  const [tagSearch, setTagSearch] = useState(''); // Search input for tags
  const [trendSortBy, setTrendSortBy] = useState('trending'); // 'trending' | 'recent' | 'videos' | 'views'
  const [trendStatusFilter, setTrendStatusFilter] = useState('all'); // 'all' | 'approved' | 'pending' (for review/approve)
  const [trendSearchQuery, setTrendSearchQuery] = useState('');
  const [trendTagFilters, setTrendTagFilters] = useState(() => readTrendFiltersFromSearchParams(searchParams).tags);
  const [trendDateFrom, setTrendDateFrom] = useState(() => readTrendFiltersFromSearchParams(searchParams).from);
  const [trendDateTo, setTrendDateTo] = useState(() => readTrendFiltersFromSearchParams(searchParams).to);
  const [trendCreatorFilters, setTrendCreatorFilters] = useState(() => readTrendFiltersFromSearchParams(searchParams).creators);
  /** Narrows the creator checkbox list (search within prepopulated names). */
  const [trendCreatorListSearch, setTrendCreatorListSearch] = useState('');
  const [trendTagFilterMenuOpen, setTrendTagFilterMenuOpen] = useState(false);
  const [trendCreatorFilterMenuOpen, setTrendCreatorFilterMenuOpen] = useState(false);
  const trendTagFilterWrapRef = useRef(null);
  const trendCreatorFilterWrapRef = useRef(null);
  /** Full library snapshot for trend tab tag/creator options and metrics (paginated fetch of all rows). */
  const [libraryVideosForTrendFilters, setLibraryVideosForTrendFilters] = useState([]);
  const [trendExampleVideos, setTrendExampleVideos] = useState([]); // Top performing videos from associated trend
  const [selectedBriefVideos, setSelectedBriefVideos] = useState([]); // Videos selected for this brief
  const [loadingTrendExamples, setLoadingTrendExamples] = useState(false);
  // Use ref to track if any dropdown is open to prevent polling from interfering
  const hasOpenDropdownRef = useRef(false);
  const showTourRef = useRef(false);
  const videoSelectionModalScrollRef = useRef(null);
  const paymentSuccessProcessedRef = useRef(false); // Track if payment success message was already shown
  const createIdeaModalAutoStartedRef = useRef(false);
  
  // Guided Tour State
  const [showTour, setShowTour] = useState(false);
  const tourStorageKey = 'trendSpotter_tour_completed';
  const onboardingStepKey = 'trendSpotter_onboarding_step';
  const onboardingStageKey = 'trendSpotter_onboarding_stage'; // 1-6, only advance on real action
  const welcomeModalShownKey = 'trendSpotter_welcome_shown';
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [userPreferences, setUserPreferences] = useState({
    tourCompleted: false,
    welcomeModalShown: false
  });
  const [onboardingStep, setOnboardingStep] = useState(0); // legacy, kept for Guide "replay"
  const [onboardingUnlockedStage, setOnboardingUnlockedStage] = useState(1); // 1-5 = current stage, 6 = all done
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationStage, setCelebrationStage] = useState(0);
  const generatedIdeasSourceRef = useRef(null); // 'trend' | 'single-video'
  const [loadingPreferences, setLoadingPreferences] = useState(true);
  const onboardingUnlockedStageRef = useRef(1);
  useEffect(() => {
    onboardingUnlockedStageRef.current = onboardingUnlockedStage;
  }, [onboardingUnlockedStage]);
  
  // Keep ref in sync with showTour state for use in intervals
  useEffect(() => {
    showTourRef.current = showTour;
  }, [showTour]);
  
  // Filters for library
  const [filters, setFilters] = useState({
    status: 'all',
    platform: 'all',
    trend: 'all',
    creator: '',
    hashtag: ''
  });

  // Brand settings state
  const [brandSettings, setBrandSettings] = useState({
    brand_name: '',
    brand_description: '',
    target_audience: '',
    content_themes: '',
    social_platform: 'tiktok',
    social_handle: ''
  });

  // Sorting state - default to latest added (created_at DESC)
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Use effective user email (allows admin "view as" another account). '' = admin "All" (see everyone's data).
  const userId = (() => { const e = getEffectiveUserEmail(); return (e !== null && e !== undefined) ? e : (user?.email || user?.id?.toString() || null); })();

  const brandSettingsRef = useRef(brandSettings);
  brandSettingsRef.current = brandSettings;
  const brandSettingsAutosaveTimerRef = useRef(null);
  const brandSnapshotAtModalOpenRef = useRef(null);

  // Ref to track if view change is from user interaction (prevents circular updates)
  const isUserInteractionRef = useRef(false);
  // Ref to track if view change is from tour (prevents data reloading)
  const isTourViewChangeRef = useRef(false);
  // Ref so polling (e.g. after add-to-library) always calls latest loadLibrary
  const loadLibraryRef = useRef(() => {});

  // Onboarding: 5-stage game. Only advance when user does the real action.
  const tourSteps = useMemo(() => [
    {
      target: '[data-tour="brand-settings-button"]',
      title: 'Stage 1: Brand details',
      content: 'Set up your brand name, description, target audience, and content themes so we can generate briefs that fit your brand. Open the user menu and click Brand Settings.',
      requiredView: 'board',
      onBeforeStep: () => {
        if (view !== 'board') {
          setView('board');
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.set('view', 'board');
          setSearchParams(newSearchParams, { replace: true });
        }
        if (!showUserMenu) setShowUserMenu(true);
      },
    },
    {
      target: '[data-tour="create-ideas-button"]',
      title: 'Stage 2: Generate a brief from trends',
      content: 'Click "Generate brief for [your brand]" on any trend card to generate content briefs based on what\'s working. Then pick an idea and add it to your board.',
      requiredView: 'trends',
      onBeforeStep: () => {
        if (view !== 'trends') {
          setView('trends');
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.set('view', 'trends');
          setSearchParams(newSearchParams, { replace: true });
        }
      },
    },
    {
      target: '[data-tour="add-videos-button"]',
      title: 'Stage 3: Save inspiration videos',
      content: 'Your Swipe File is where you save videos from TikTok and Instagram. Use "Add videos" to paste URLs, or install our Chrome extension to sync bookmarks. These become your inspiration library.',
      requiredView: 'library',
    },
    {
      target: '[data-tour="single-video-create-idea"]',
      title: 'Stage 4: Generate a brief from a video',
      content: 'Turn one video into a brief: in the Swipe File, each video row has a "Generate brief" button—click it to create brand-focused briefs from that video alone, no trend needed.',
      requiredView: 'library',
    },
    {
      target: '[data-tour="new-idea-button"]',
      title: 'Stage 5: Manage the Content Board',
      content: 'The Board is where all your briefs live. Create new ideas, move them through phases (Ideas → Script → Shot list → etc.), and share with your team. Use "New Brief" to start from scratch or from a trend.',
      requiredView: 'board',
      onBeforeStep: () => {
        if (view !== 'board') {
          setView('board');
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.set('view', 'board');
          setSearchParams(newSearchParams, { replace: true });
        }
      },
    },
  ], [showUserMenu, view, searchParams, setSearchParams]);

  // Stable steps reference for GuidedTour to prevent blink when parent re-renders (e.g. countdown/job timers)
  const activeTourSteps = useMemo(
    () =>
      onboardingUnlockedStage >= 1 && onboardingUnlockedStage <= 5
        ? tourSteps.slice(onboardingUnlockedStage - 1, onboardingUnlockedStage)
        : tourSteps.slice(0, 1),
    [onboardingUnlockedStage, tourSteps]
  );

  // Fetch user preferences from API
  const fetchUserPreferences = useCallback(async () => {
    if (!userId) return;
    
    try {
      setLoadingPreferences(true);
      const response = await fetch('/api/user/preferences', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.preferences) {
          setUserPreferences(data.preferences);
          const stage = Math.min(6, Math.max(1, parseInt(data.preferences.onboardingStage, 10) || 1));
          setOnboardingUnlockedStage(stage);
          // Also sync to localStorage as fallback
          if (data.preferences.tourCompleted) {
            localStorage.setItem(`${tourStorageKey}_${userId}`, 'true');
          }
          if (data.preferences.welcomeModalShown) {
            localStorage.setItem(`${welcomeModalShownKey}_${userId}`, 'true');
          }
        }
      } else {
        // If API fails, fallback to localStorage
        const hasCompletedTour = localStorage.getItem(`${tourStorageKey}_${userId}`) === 'true';
        const hasSeenWelcome = localStorage.getItem(`${welcomeModalShownKey}_${userId}`) === 'true';
        setUserPreferences({
          tourCompleted: hasCompletedTour,
          welcomeModalShown: hasSeenWelcome
        });
      }
    } catch (error) {
      console.error('Error fetching user preferences:', error);
      // Fallback to localStorage on error
      const hasCompletedTour = localStorage.getItem(`${tourStorageKey}_${userId}`) === 'true';
      const hasSeenWelcome = localStorage.getItem(`${welcomeModalShownKey}_${userId}`) === 'true';
      setUserPreferences({
        tourCompleted: hasCompletedTour,
        welcomeModalShown: hasSeenWelcome
      });
    } finally {
      setLoadingPreferences(false);
    }
  }, [userId, tourStorageKey, welcomeModalShownKey]);

  // Update user preferences in API
  const updateUserPreferences = useCallback(async (updates) => {
    if (!userId) return;
    
    try {
      const response = await fetch('/api/user/preferences', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates)
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.preferences) {
          setUserPreferences(prev => ({ ...prev, ...data.preferences }));
          if (data.preferences.onboardingStage !== undefined) {
            const stage = Math.min(6, Math.max(1, parseInt(data.preferences.onboardingStage, 10) || 1));
            setOnboardingUnlockedStage(stage);
          }
          // Also sync to localStorage as fallback
          if (updates.tourCompleted !== undefined) {
            localStorage.setItem(`${tourStorageKey}_${userId}`, updates.tourCompleted ? 'true' : 'false');
          }
          if (updates.welcomeModalShown !== undefined) {
            localStorage.setItem(`${welcomeModalShownKey}_${userId}`, updates.welcomeModalShown ? 'true' : 'false');
          }
        }
      } else {
        // If API fails, fallback to localStorage
        if (updates.tourCompleted !== undefined) {
          localStorage.setItem(`${tourStorageKey}_${userId}`, updates.tourCompleted ? 'true' : 'false');
        }
        if (updates.welcomeModalShown !== undefined) {
          localStorage.setItem(`${welcomeModalShownKey}_${userId}`, updates.welcomeModalShown ? 'true' : 'false');
        }
        // Update local state even if API fails
        setUserPreferences(prev => ({ ...prev, ...updates }));
      }
    } catch (error) {
      console.error('Error updating user preferences:', error);
      // Fallback to localStorage on error
      if (updates.tourCompleted !== undefined) {
        localStorage.setItem(`${tourStorageKey}_${userId}`, updates.tourCompleted ? 'true' : 'false');
      }
      if (updates.welcomeModalShown !== undefined) {
        localStorage.setItem(`${welcomeModalShownKey}_${userId}`, updates.welcomeModalShown ? 'true' : 'false');
      }
      // Update local state even if API fails
      setUserPreferences(prev => ({ ...prev, ...updates }));
    }
  }, [userId, tourStorageKey, welcomeModalShownKey]);

  // Fetch preferences on mount
  useEffect(() => {
    if (userId) {
      fetchUserPreferences();
      const saved = localStorage.getItem(`${onboardingStepKey}_${userId}`);
      const step = saved !== null ? Math.min(5, Math.max(0, parseInt(saved, 10) || 0)) : 0;
      setOnboardingStep(Number.isNaN(step) ? 0 : step);
      // onboardingUnlockedStage is loaded from API in fetchUserPreferences (DB, cross-device)
    }
  }, [userId, fetchUserPreferences, onboardingStepKey]);

  // Check if user should see welcome modal on first visit
  useEffect(() => {
    if (!userId || loadingPreferences) return;
    
    if (!userPreferences.welcomeModalShown) {
      const timer = setTimeout(() => {
        setShowWelcomeModal(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [userId, userPreferences.welcomeModalShown, loadingPreferences]);

  // Onboarding guide: do not auto-show on visit; user can open via Guide button. Default view stays Trends.
  // (Previously auto-showed tour after 1.5s, which also switched view to board for Stage 1.)
  // useEffect(() => {
  //   if (!userId || loadingPreferences) return;
  //   if (!userPreferences.welcomeModalShown || onboardingUnlockedStage > 5) return;
  //   const timer = setTimeout(() => setShowTour(true), 1500);
  //   return () => clearTimeout(timer);
  // }, [userId, userPreferences.welcomeModalShown, loadingPreferences, onboardingUnlockedStage]);

  // Complete a stage (only when user does the real action). Show celebration; advance to next stage only when user clicks Continue.
  const completeOnboardingStage = useCallback((stageNum) => {
    if (stageNum < 1 || stageNum > 5) return;
    if (onboardingUnlockedStageRef.current !== stageNum) return;
    setShowCelebration(true);
    setCelebrationStage(stageNum);
    setShowTour(false);
    if (showUserMenu) setShowUserMenu(false);
  }, [showUserMenu]);

  const persistBrandSettings = useCallback(
    async (payload, { silent = false } = {}) => {
      if (!userId) return false;
      try {
        const response = await fetch('/api/trend-spotter/brand-settings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId, ...payload })
        });
        const result = await response.json();
        if (result.success) {
          if (!silent) {
            toast.success('Brand settings saved!');
            completeOnboardingStage(1);
          }
          return true;
        }
        toast.error(result.error || 'Failed to save brand settings');
        return false;
      } catch (error) {
        console.error('Error saving brand settings:', error);
        toast.error('Failed to save brand settings');
        return false;
      }
    },
    [userId, completeOnboardingStage]
  );

  const maybeAutoOpenBrandSettingsPrompt = useCallback((settings) => {
    if (!userId || userId === '') return;
    const name = (settings?.brand_name || '').trim();
    const desc = (settings?.brand_description || '').trim();
    if (name && desc) return;
    const accountKey = String(userId).trim().toLowerCase();
    const k = `showstop_brand_settings_login_count_${accountKey}`;
    const count = parseInt(localStorage.getItem(k) || '0', 10);
    if (count < 3) return;
    if (sessionStorage.getItem('showstop_brand_settings_prompt_dismissed') === '1') return;
    setBrandSettingsModalSource('auto');
    setShowBrandSettingsModal(true);
  }, [userId]);

  const closeBrandSettingsModal = useCallback(
    async (opts = {}) => {
      const { skipDismissFlag = false, skipFlush = false } = opts;
      if (!skipFlush) {
        if (brandSettingsAutosaveTimerRef.current) {
          clearTimeout(brandSettingsAutosaveTimerRef.current);
          brandSettingsAutosaveTimerRef.current = null;
        }
        const baseline = brandSnapshotAtModalOpenRef.current;
        if (showBrandSettingsModal && baseline != null) {
          const current = JSON.stringify(brandSettingsRef.current);
          if (current !== baseline) {
            await persistBrandSettings(brandSettingsRef.current, { silent: true });
          }
        }
      }
      brandSnapshotAtModalOpenRef.current = null;
      const s = brandSettingsRef.current;
      const incomplete = !s.brand_name?.trim() || !s.brand_description?.trim();
      if (!skipDismissFlag && incomplete) {
        sessionStorage.setItem('showstop_brand_settings_prompt_dismissed', '1');
      }
      setBrandSettingsModalSource(null);
      setShowBrandSettingsModal(false);
    },
    [showBrandSettingsModal, persistBrandSettings]
  );

  const handleBrandSettingsDone = useCallback(async () => {
    if (brandSettingsAutosaveTimerRef.current) {
      clearTimeout(brandSettingsAutosaveTimerRef.current);
      brandSettingsAutosaveTimerRef.current = null;
    }
    const ok = await persistBrandSettings(brandSettingsRef.current, { silent: false });
    if (ok) {
      await closeBrandSettingsModal({ skipDismissFlag: true, skipFlush: true });
    }
  }, [persistBrandSettings, closeBrandSettingsModal]);

  useEffect(() => {
    if (!showBrandSettingsModal) return;
    brandSnapshotAtModalOpenRef.current = JSON.stringify(brandSettingsRef.current);
  }, [showBrandSettingsModal]);

  useEffect(() => {
    if (!showBrandSettingsModal || !userId) return;
    const baseline = brandSnapshotAtModalOpenRef.current;
    if (baseline == null) return;
    const current = JSON.stringify(brandSettings);
    if (current === baseline) return;
    if (brandSettingsAutosaveTimerRef.current) clearTimeout(brandSettingsAutosaveTimerRef.current);
    brandSettingsAutosaveTimerRef.current = setTimeout(async () => {
      brandSettingsAutosaveTimerRef.current = null;
      const ok = await persistBrandSettings(brandSettingsRef.current, { silent: true });
      if (ok) {
        brandSnapshotAtModalOpenRef.current = JSON.stringify(brandSettingsRef.current);
      }
    }, 850);
    return () => {
      if (brandSettingsAutosaveTimerRef.current) {
        clearTimeout(brandSettingsAutosaveTimerRef.current);
        brandSettingsAutosaveTimerRef.current = null;
      }
    };
  }, [brandSettings, showBrandSettingsModal, userId, persistBrandSettings]);

  const handleCelebrationContinue = useCallback((stageJustCompleted) => {
    setShowCelebration(false);
    const next = Math.min(5, stageJustCompleted + 1);
    onboardingUnlockedStageRef.current = next;
    setOnboardingUnlockedStage(next);
    if (userId) {
      updateUserPreferences({
        onboardingStage: next,
        ...(next > 5 ? { tourCompleted: true } : {})
      });
    }
    if (next > 5) return;
    const nextStep = tourSteps[next - 1];
    if (nextStep && nextStep.requiredView) {
      setView(nextStep.requiredView);
      setSearchParams(prev => {
        const p = new URLSearchParams(prev);
        p.set('view', nextStep.requiredView);
        return p;
      }, { replace: true });
    }
    setTimeout(() => setShowTour(true), 500);
  }, [userId, updateUserPreferences, tourSteps, setView, setSearchParams]);

  // Tour Handlers (1 step per session: advance step and close) — legacy for "replay" via Guide
  const persistOnboardingStep = useCallback((step) => {
    if (userId) {
      localStorage.setItem(`${onboardingStepKey}_${userId}`, String(step));
    }
  }, [userId, onboardingStepKey]);

  const handleTourComplete = () => {
    setShowTour(false);
    if (showUserMenu) setShowUserMenu(false);
    // Do not advance stage or move to next task — only closing the tip. User advances by completing the stage task, then clicking Continue on the celebration.
  };

  const handleTourClose = () => {
    setShowTour(false);
    if (showUserMenu) setShowUserMenu(false);
  };

  const startTour = () => {
    setShowTour(true);
  };

  const handleWelcomeModalClose = () => {
    if (userId) {
      updateUserPreferences({ welcomeModalShown: true });
    }
    setShowWelcomeModal(false);
  };

  const handleWelcomeModalStartTour = () => {
    handleWelcomeModalClose();
    // Start tour after a short delay
    setTimeout(() => {
      startTour();
    }, 300);
  };

  // Sync view state with URL parameter on mount and when URL changes (from browser back/forward)
  useEffect(() => {
    // Skip if this change was triggered by user interaction
    if (isUserInteractionRef.current) {
      isUserInteractionRef.current = false;
      return;
    }

    const viewParam = searchParams.get('view');
    if (viewParam === 'library' || viewParam === 'trends' || viewParam === 'board') {
      if (viewParam !== view) {
        setView(viewParam);
      }
    } else if (!viewParam) {
      // If no view param, default to trends and update URL (preserve trendId etc.)
      if (view !== 'trends') {
        setView('trends');
      }
      const next = new URLSearchParams(searchParams);
      next.set('view', 'trends');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Update URL when view changes (only if not already in sync). Preserve trendId and other params.
  useEffect(() => {
    const currentViewParam = searchParams.get('view');
    if (currentViewParam !== view) {
      const next = new URLSearchParams(searchParams);
      next.set('view', view);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const viewRef = useRef(view);
  viewRef.current = view;

  const scrollPageToTop = useCallback(() => {
    if (typeof window === 'undefined') return;
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    } catch {
      window.scrollTo(0, 0);
    }
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  // Trend detail: scroll after paint + after URL / video layout (scroll anchoring was pulling away from top)
  useEffect(() => {
    if (view !== 'trend-detail' || !selectedTrend?.id) return;
    let cancelled = false;
    const run = () => {
      if (!cancelled) scrollPageToTop();
    };
    const id0 = requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });
    const t50 = window.setTimeout(run, 50);
    const t200 = window.setTimeout(run, 200);
    return () => {
      cancelled = true;
      cancelAnimationFrame(id0);
      window.clearTimeout(t50);
      window.clearTimeout(t200);
    };
  }, [view, selectedTrend?.id, scrollPageToTop]);

  // Trends tab: keep date / tags / creator filters in sync with URL (shareable + digest deep links)
  useEffect(() => {
    const p = readTrendFiltersFromSearchParams(searchParams);
    setTrendDateFrom((prev) => (prev === p.from ? prev : p.from));
    setTrendDateTo((prev) => (prev === p.to ? prev : p.to));
    setTrendCreatorFilters((prev) => {
      const pc = p.creators;
      if (prev.length === pc.length && prev.every((t, i) => t === pc[i])) return prev;
      return pc;
    });
    setTrendTagFilters((prev) => {
      if (prev.length === p.tags.length && prev.every((t, i) => t === p.tags[i])) return prev;
      return p.tags;
    });
  }, [searchParams]);

  useEffect(() => {
    if (view !== 'trends') return;
    const from = trendDateFrom.trim();
    const to = trendDateTo.trim();
    const tagsSorted = [...trendTagFilters].sort();
    const creatorsSorted = [...trendCreatorFilters].sort();
    setSearchParams(
      (prev) => {
        const prevTags = prev.getAll('tags').sort();
        const prevCreators = prev.getAll('creators').sort();
        if (
          (prev.get('view') || '') === 'trends' &&
          (prev.get('from') || '') === from &&
          (prev.get('to') || '') === to &&
          prevTags.length === tagsSorted.length &&
          prevTags.every((t, i) => t === tagsSorted[i]) &&
          prevCreators.length === creatorsSorted.length &&
          prevCreators.every((c, i) => c === creatorsSorted[i])
        ) {
          return prev;
        }
        const next = new URLSearchParams(prev);
        next.set('view', 'trends');
        if (from) next.set('from', from);
        else next.delete('from');
        if (to) next.set('to', to);
        else next.delete('to');
        next.delete('tags');
        trendTagFilters.forEach((t) => next.append('tags', t));
        next.delete('creator');
        next.delete('creators');
        trendCreatorFilters.forEach((c) => next.append('creators', c));
        return next;
      },
      { replace: true }
    );
  }, [view, trendDateFrom, trendDateTo, trendTagFilters, trendCreatorFilters, setSearchParams]);

  // Open a specific trend when landing with ?trendId= (e.g. from digest or shared page CTA)
  const trendIdFromUrlRef = useRef(null);
  useEffect(() => {
    const trendIdParam = searchParams.get('trendId');
    if (!trendIdParam) return;
    const trendId = parseInt(trendIdParam, 10);
    if (Number.isNaN(trendId)) return;

    const trend = trends.find(t => t.id === trendId);
    if (trend) {
      setView('trends');
      setSelectedTrend(trend);
      setTrendSearchQuery(trend.trend_name || '');
      trendIdFromUrlRef.current = null;
      const next = new URLSearchParams(searchParams);
      next.delete('trendId');
      next.set('view', 'trends');
      setSearchParams(next, { replace: true });
      return;
    }

    // Trend not in loaded list (e.g. local DB, or shared trend from another user): fetch from share endpoint
    if (trendIdFromUrlRef.current === trendId) return;
    trendIdFromUrlRef.current = trendId;
    let cancelled = false;
    fetch(`/api/trend-spotter/share/${trendId}`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled || !data.success || !data.template) {
          trendIdFromUrlRef.current = null;
          return;
        }
        const t = data.template;
        const trendObj = {
          id: t.id,
          trend_name: t.trend_name,
          hook: t.hook,
          template: t.template,
          why_it_works: t.why_it_works,
          status: t.status,
          trend_type: t.trend_type,
          created_at: t.created_at,
          user_id: t.user_id,
          savedCount: (data.topVideos && data.topVideos.length) || 0
        };
        setTrends((prev) => (prev.some((x) => x.id === trendId) ? prev : [...prev, trendObj]));
        setView('trends');
        setSelectedTrend(trendObj);
        setTrendSearchQuery(t.trend_name || '');
        trendIdFromUrlRef.current = null;
        const next = new URLSearchParams(searchParams);
        next.delete('trendId');
        next.set('view', 'trends');
        setSearchParams(next, { replace: true });
      })
      .catch(() => { trendIdFromUrlRef.current = null; });
    return () => { cancelled = true; };
  }, [trends, searchParams, setSearchParams]);

  // Initial load + reload when account (userId) changes (e.g. admin "View as" switch). userId '' = admin "All".
  useEffect(() => {
    if (userId === null || userId === undefined) return;
    
    // Clear previous account's data so UI and load guards don't show/skip stale data
    setLibraryVideos([]);
    setLibraryPagination({ page: 1, hasMore: false, loadingMore: false, total: 0 });
    setTrends([]);
    setTrendTopVideos({});
    setBriefs([]);
    setSelectedTrend(null);
    setSelectedBrief(null);
    setSampleVideos([]);
    setLibraryVideosForTrendFilters([]);
    
    // Force reload for new account (bypasses "already loaded" guards in loadLibrary/loadBriefs)
    loadLibrary(true);
    loadTrends(false); // Don't load top videos on initial load
    loadBriefs(true);
    loadBrandSettings();
    checkActiveJobs();
    checkUserLimits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]); // Only run when userId changes

  // Load 5 sample viral videos (one per trend) for Swipe File inspiration
  const loadSampleVideos = useCallback(async () => {
    try {
      const response = await fetch(`/api/trend-spotter/sample-videos?userId=${encodeURIComponent(userId || '')}`);
      const result = await response.json();
      if (result.success && Array.isArray(result.videos)) {
        setSampleVideos(result.videos.map(v => ({ ...v, is_sample: true })));
      }
    } catch (error) {
      console.error('Error loading sample videos:', error);
    }
  }, [userId]);

  /** Load every library row for this user (paginated on the server) — used for trend filters and trend metrics. */
  const loadLibraryForTrendFilters = useCallback(async () => {
    if (userId === null || userId === undefined || userId === '') return;
    try {
      const all = [];
      let page = 1;
      const pageSize = 500;
      let hasMore = true;
      while (hasMore) {
        const response = await fetch(
          `/api/trend-spotter/library?userId=${encodeURIComponent(userId)}&page=${page}&limit=${pageSize}`
        );
        const result = await response.json();
        if (!result.success || !Array.isArray(result.videos)) break;
        all.push(...result.videos);
        hasMore = result.pagination?.hasMore === true;
        page += 1;
        if (page > 400) break; // safety: up to 200k videos
      }
      setLibraryVideosForTrendFilters(all);
    } catch (error) {
      console.error('Error loading full library for trend filters:', error);
    }
  }, [userId]);

  const libraryScrollRef = useRef(null);

  // Trends tab: close tag/creator filter popovers on outside click or Escape
  useEffect(() => {
    const onPointerDown = (e) => {
      const el = e.target;
      if (trendTagFilterWrapRef.current?.contains(el)) return;
      if (trendCreatorFilterWrapRef.current?.contains(el)) return;
      setTrendTagFilterMenuOpen(false);
      setTrendCreatorFilterMenuOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setTrendTagFilterMenuOpen(false);
        setTrendCreatorFilterMenuOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  // Scroll detection for lazy loading library videos
  useEffect(() => {
    if (view !== 'library' || !libraryPagination.hasMore || libraryPagination.loadingMore) return;

    const handleScroll = () => {
      const container = libraryScrollRef.current;
      if (!container) return;
      
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;
      
      // Load more when user is within 300px of bottom
      if (scrollTop + clientHeight >= scrollHeight - 300) {
        loadMoreLibrary();
      }
    };

    const container = libraryScrollRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
    }
    
    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, libraryPagination.hasMore, libraryPagination.loadingMore]);

  // Load view-specific data when switching views (optimized to avoid unnecessary calls). userId '' = admin All.
  useEffect(() => {
    if (userId === null || userId === undefined) return;
    
    // Skip data loading if view change is from tour
    if (isTourViewChangeRef.current) {
      isTourViewChangeRef.current = false;
      return;
    }

    // Load view-specific data (only if not already cached)
    if (view === 'library') {
      // Always load user's library when on Swipe File so uploaded videos show (not just 5 samples)
      loadLibrary(true);
      loadSampleVideos(); // Load 5 sample viral videos for inspiration (shown only when user has no videos)
      // Only check active jobs if not in tour (to prevent extra refreshes during tour)
      if (!showTour) {
        checkActiveJobs(); // Check for active jobs on library view
      }
    } else if (view === 'trends') {
      loadLibraryForTrendFilters();
      // Swipe File total for subtitle (load once if never loaded)
      if (libraryPagination.total === 0 && libraryVideos.length === 0) {
        loadLibrary(true);
      }
      // Always refresh templates (full tag list); load top videos when missing
      const needsTopVideos = trends.length === 0 || trends.some((trend) => !trendTopVideos[trend.id]);
      loadTrends(needsTopVideos);
    } else if (view === 'board') {
      // Only reload briefs if they're empty (cached data is available)
      if (briefs.length === 0) {
        loadBriefs();
      }
      checkUserLimits(); // Check user limits when switching to board view
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, view, loadSampleVideos]); // Run when view changes

  // Load subscription status function
  const loadSubscriptionStatus = useCallback(async () => {
    setLoadingSubscriptionStatus(true);
    try {
      const response = await fetch('/api/payment/status', {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setSubscriptionStatus(data);
      } else {
        toast.error(data.error || 'Failed to load subscription status');
      }
    } catch (error) {
      console.error('Error fetching subscription status:', error);
      toast.error('Failed to load subscription status');
    } finally {
      setLoadingSubscriptionStatus(false);
    }
  }, []);

  // Handle programmatic checkout
  const handleSubscribe = useCallback(async () => {
    if (!user) {
      toast.error('Please log in to subscribe');
      return;
    }

    setCreatingCheckout(true);
    try {
      const response = await fetch('/api/payment/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include' // Important: sends session cookie for authentication
      });

      const result = await response.json();
      if (result.success && result.url) {
        // Redirect to Stripe Checkout
        // The checkout session includes:
        // - customer_email: user.email (pre-filled)
        // - metadata.user_id: user.id (for webhook linking)
        // - client_reference_id: user.id (backup linking)
        window.location.href = result.url;
      } else {
        toast.error(result.error || 'Failed to create checkout session');
        setCreatingCheckout(false);
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Failed to create checkout session');
      setCreatingCheckout(false);
    }
  }, [user]);

  // Check for payment success redirect and verify payment
  // This must be after loadSubscriptionStatus is defined
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus === 'success') {
      // Prevent duplicate processing
      if (paymentSuccessProcessedRef.current) {
        return;
      }
      paymentSuccessProcessedRef.current = true;
      
      // Payment completed - wait for webhook to process, then check status
      // Don't show duplicate messages - webhook handles the notification
      console.log('✅ Payment success detected, waiting for webhook to process...');
      
      // Wait for webhook to process, then check status (silently)
      setTimeout(async () => {
        await loadSubscriptionStatus();
        // checkUserLimits is defined later, but it's safe to call here
        if (typeof checkUserLimits === 'function') {
          await checkUserLimits();
        }
        // Only show success if payment was actually processed (hasPaid is true)
        // This prevents duplicate messages if webhook already processed it
        const statusResponse = await fetch('/api/payment/status', { credentials: 'include' });
        const statusData = await statusResponse.json();
        if (statusData.success && statusData.hasPaid) {
          toast.success('Payment verified! Your subscription is now active.');
        }
      }, 3000); // Give webhook time to process
      
      // Remove payment parameter from URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('payment');
      setSearchParams(newSearchParams, { replace: true });
    } else if (paymentStatus === 'cancelled') {
      toast.error('Payment was cancelled');
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('payment');
      setSearchParams(newSearchParams, { replace: true });
    } else {
      // Reset the ref when payment parameter is not present
      paymentSuccessProcessedRef.current = false;
    }
    // Only depend on searchParams, not loadSubscriptionStatus to prevent duplicate runs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Load subscription status when modal opens
  useEffect(() => {
    if (showSubscriptionModal) {
      // Load subscription status
      loadSubscriptionStatus();
    }
  }, [showSubscriptionModal, loadSubscriptionStatus]);

  // Initialize sharePermission when share modal opens
  useEffect(() => {
    if (showShareModal && selectedBrief) {
      const userPermission = getUserPermission(selectedBrief);
      if (userPermission === 'view') {
        setSharePermission('view');
      } else if (userPermission === 'comment') {
        setSharePermission('view'); // Default to view, but can also share with comment
      } else if (userPermission === 'edit') {
        setSharePermission('view'); // Default to view, but can also share with edit
      } else {
        setSharePermission('view'); // Owner can share with any, default to view
      }
    }
  }, [showShareModal, selectedBrief]);

  // Fetch trend example videos and selected brief videos when brief detail modal opens
  useEffect(() => {
    const fetchVideos = async () => {
      if (!showBriefDetailModal || !selectedBrief || !userId) {
        setTrendExampleVideos([]);
        setSelectedBriefVideos([]);
        return;
      }

      // Fetch trend example videos if brief has a trend
      const trendId = selectedBrief.trend_id ? parseInt(selectedBrief.trend_id) : null;
      if (trendId) {
        setLoadingTrendExamples(true);
        try {
          const response = await fetch(`/api/trend-spotter/trends/${trendId}/top-videos?userId=${encodeURIComponent(userId)}`);
          const result = await response.json();
          if (result.success) {
            setTrendExampleVideos(result.videos || []);
          } else {
            console.error('Error fetching trend examples:', result.error);
            setTrendExampleVideos([]);
          }
        } catch (error) {
          console.error('Error fetching trend examples:', error);
          setTrendExampleVideos([]);
        } finally {
          setLoadingTrendExamples(false);
        }
      } else {
        setTrendExampleVideos([]);
      }

      // Fetch selected brief videos
      const videoIds = Array.isArray(selectedBrief.video_ids) ? selectedBrief.video_ids : [];
      if (videoIds.length > 0) {
        try {
          // Fetch all library videos and filter by IDs
          const response = await fetch(`/api/trend-spotter/library?userId=${encodeURIComponent(userId)}&page=1&limit=1000`);
          const result = await response.json();
          if (result.success) {
            // Filter to only get videos that match the selected IDs
            const selectedVideos = result.videos.filter(v => videoIds.includes(v.id));
            setSelectedBriefVideos(selectedVideos);
          } else {
            setSelectedBriefVideos([]);
          }
        } catch (error) {
          console.error('Error fetching selected brief videos:', error);
          setSelectedBriefVideos([]);
        }
      } else {
        setSelectedBriefVideos([]);
      }
    };

    fetchVideos();
  }, [showBriefDetailModal, selectedBrief, userId]);

  // Load all videos for the selected trend
  useEffect(() => {
    if (selectedTrend && selectedTrend.id) {
      loadTrendVideos(selectedTrend.id);
    } else {
      setTrendVideos([]);
    }
  }, [selectedTrend?.id, userId]);

  // Auto-scroll behavior when modal opens (only on initial open, not when selecting new videos)
  useEffect(() => {
    if (showVideoSelectionModal && videoSelectionModalScrollRef.current) {
      // Reset the ref when modal opens
      hasScrolledToFirstVideoRef.current = false;
      
      // Wait for DOM to render
      const timeoutId = setTimeout(() => {
        if (!videoSelectionModalScrollRef.current || hasScrolledToFirstVideoRef.current) return;
        
        const container = videoSelectionModalScrollRef.current;
        
        // If there are pre-selected videos, scroll to first selected at the top
        if (selectedVideosForModal.size > 0) {
          const firstSelectedId = Array.from(selectedVideosForModal)[0];
          const selectedElement = container.querySelector(`[data-video-id="${firstSelectedId}"]`);
          
          if (selectedElement) {
            // Use requestAnimationFrame to ensure layout is complete
            requestAnimationFrame(() => {
              // Get bounding rects for precise calculation
              const containerRect = container.getBoundingClientRect();
              const elementRect = selectedElement.getBoundingClientRect();
              
              // Calculate the scroll position needed
              // Current scroll + (element's current position - container's top)
              const scrollPosition = container.scrollTop + (elementRect.top - containerRect.top);
              
              // Scroll to position the element at the very top of the visible area
              container.scrollTo({
                top: scrollPosition,
                behavior: 'smooth'
              });
              
              // Mark that we've done the initial scroll
              hasScrolledToFirstVideoRef.current = true;
            });
            return;
          }
        }
        
        // For new trends with no pre-selected videos, scroll to top
        container.scrollTo({
          top: 0,
          behavior: 'instant'
        });
        hasScrolledToFirstVideoRef.current = true;
      }, 150); // Give time for modal to fully render
      
      return () => clearTimeout(timeoutId);
    } else {
      // Reset when modal closes
      hasScrolledToFirstVideoRef.current = false;
    }
  }, [showVideoSelectionModal, videoSelectionMode]); // Removed selectedVideosForModal from dependencies

  // Poll for active jobs when on library view
  useEffect(() => {
    // Don't start polling if tour is active
    if (showTour) {
      // Clear any existing polling when tour starts
      if (jobPollingInterval) {
        clearInterval(jobPollingInterval);
        setJobPollingInterval(null);
      }
      return;
    }
    
    if (view === 'library' && userId) {
      // Start polling for active jobs, but only if no dropdown is open
      const interval = setInterval(() => {
        // Skip polling if a dropdown is open or tour is active to prevent re-renders
        // Use ref to get current value (not closure value)
        if (!hasOpenDropdownRef.current && !showTourRef.current) {
          checkActiveJobs();
        }
      }, 5000); // Poll every 5 seconds
      
      setJobPollingInterval(interval);
      
      return () => {
        if (interval) clearInterval(interval);
      };
    } else {
      // Stop polling when not on library view
      if (jobPollingInterval) {
        clearInterval(jobPollingInterval);
        setJobPollingInterval(null);
      }
    }
  }, [view, userId, showTour]);

  // Check user limits
  const checkUserLimits = async () => {
    if (!userId) {
      console.log('⚠️ checkUserLimits: No userId provided');
      return;
    }
    try {
      console.log('🔍 Checking user limits for userId:', userId);
      const response = await fetch(`/api/trend-spotter/user-limits?userId=${encodeURIComponent(userId)}`);
      const result = await response.json();
      console.log('📊 User limits response:', result);
      if (result.success && result.limits) {
        console.log('✅ Setting user limits:', result.limits);
        setUserLimits(result.limits);
      } else {
        console.warn('⚠️ User limits response not successful:', result);
      }
    } catch (error) {
      console.error('❌ Error checking user limits:', error);
    }
  };

  // Helper function to format daily limit message
  const getDailyLimitMessage = (limits) => {
    if (!limits.ideaLimitExceeded && !(limits.reason === 'idea_limit')) {
      return null;
    }
    
    const ideaCount = limits.ideaCount || 0;
    const limit = limits.ideaLimit || 5;
    const limitResetsAt = limits.limitResetsAt;
    
    let resetTime = 'tomorrow';
    if (limitResetsAt) {
      const resetDate = new Date(limitResetsAt);
      const now = new Date();
      const hoursUntilReset = Math.ceil((resetDate - now) / (1000 * 60 * 60));
      if (hoursUntilReset < 24) {
        resetTime = `in ${hoursUntilReset} hour${hoursUntilReset !== 1 ? 's' : ''}`;
      }
    }
    
    return {
      text: `You've reached your daily limit of ${limit} ideas (${ideaCount}/${limit} today).`,
      resetTime: resetTime,
      limitResetsAt: limitResetsAt
    };
  };

  const GENERATE_IDEAS_TOAST_ID = 'generate-ideas-loading';

  // Run generate-ideas workflow (used by trend detail page, Create Idea popup from trend card, and single-video row "Create idea")
  const runGenerateIdeas = useCallback(async (trendIdOverride, options = {}) => {
    const { showLoadingToast = false } = options;
    const effectiveTrendId = trendIdOverride ?? selectedTrend?.id;
    if (!effectiveTrendId || !userId) return;
    if (userLimits.exceeded && (userLimits.reason === 'idea_limit' || userLimits.ideaLimitExceeded)) {
      setShowCreateIdeaModal(false);
      setShowIdeaLimitModal(true);
      return;
    }
    if (showLoadingToast) {
      toast.loading('Generating brief... (~40s)', { id: GENERATE_IDEAS_TOAST_ID, duration: Infinity });
    }
    setIsLoading(true);
    let timerInterval;
    let timerSeconds = 40;
    setIdeaTimer(timerSeconds);
    timerInterval = setInterval(() => {
      timerSeconds--;
      if (timerSeconds > 0) {
        setIdeaTimer(timerSeconds);
      } else {
        clearInterval(timerInterval);
        setIdeaTimer(null);
      }
    }, 1000);
    try {
      const response = await fetch('/api/trend-spotter/generate-ideas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trendId: effectiveTrendId, userId })
      });
      if (response.status === 403) {
        clearInterval(timerInterval);
        setIdeaTimer(null);
        if (showLoadingToast) toast.dismiss(GENERATE_IDEAS_TOAST_ID);
        setShowCreateIdeaModal(false);
        setShowIdeaLimitModal(true);
        setIsLoading(false);
        return;
      }
      const result = await response.json();
      if (result.error === 'idea_limit_exceeded' || result.requiresPayment) {
        clearInterval(timerInterval);
        setIdeaTimer(null);
        if (showLoadingToast) toast.dismiss(GENERATE_IDEAS_TOAST_ID);
        setShowCreateIdeaModal(false);
        setShowIdeaLimitModal(true);
        setIsLoading(false);
        return;
      }
      if (result.success && result.ideas) {
        clearInterval(timerInterval);
        setIdeaTimer(null);
        if (showLoadingToast) toast.dismiss(GENERATE_IDEAS_TOAST_ID);
        generatedIdeasSourceRef.current = 'trend';
        setGeneratedIdeas(result.ideas.map((idea, idx) => ({ ...idea, id: `generated-${idx}` })));
        setSelectedIdeasForBoard(new Set(result.ideas.map((_, idx) => `generated-${idx}`)));
        setShowCreateIdeaModal(false);
        setShowGeneratedIdeasModal(true);
        setTimeout(() => checkUserLimits(), 1000);
      } else {
        clearInterval(timerInterval);
        setIdeaTimer(null);
        if (showLoadingToast) toast.dismiss(GENERATE_IDEAS_TOAST_ID);
        toast.error(result.error || 'Failed to generate brief');
      }
    } catch (error) {
      console.error('Error generating brief:', error);
      if (showLoadingToast) toast.dismiss(GENERATE_IDEAS_TOAST_ID);
      toast.error('Failed to generate brief');
    } finally {
      if (timerInterval) clearInterval(timerInterval);
      setIdeaTimer(null);
      setIsLoading(false);
    }
  }, [selectedTrend?.id, userId, userLimits.exceeded, userLimits.reason, userLimits.ideaLimitExceeded]);

  // Resolve trend ID from a library video (supports trends[] or legacy trend_id)
  const getVideoTrendId = (video) => {
    if (video.trends && Array.isArray(video.trends) && video.trends.length > 0) return video.trends[0].id;
    if (video.trend_id) return typeof video.trend_id === 'number' ? video.trend_id : parseInt(video.trend_id, 10);
    return null;
  };

  // Single Video > Brief: generate ideas from one reference video (when video has no trend)
  const runGenerateIdeasFromVideo = useCallback(async (video) => {
    if (!video?.id || !userId) return;
    if (userLimits.exceeded && (userLimits.reason === 'idea_limit' || userLimits.ideaLimitExceeded)) {
      setShowIdeaLimitModal(true);
      return;
    }
    toast.loading('Generating brief from video... (~40s)', { id: GENERATE_IDEAS_TOAST_ID, duration: Infinity });
    setIsLoading(true);
    let timerInterval;
    let timerSeconds = 40;
    setIdeaTimer(timerSeconds);
    timerInterval = setInterval(() => {
      timerSeconds--;
      if (timerSeconds > 0) setIdeaTimer(timerSeconds);
      else {
        setIdeaTimer(null);
        clearInterval(timerInterval);
      }
    }, 1000);
    try {
      const response = await fetch('/api/trend-spotter/generate-ideas-from-video', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ videoId: video.id, userId })
      });
      if (response.status === 403) {
        if (timerInterval) clearInterval(timerInterval);
        setIdeaTimer(null);
        toast.dismiss(GENERATE_IDEAS_TOAST_ID);
        setShowIdeaLimitModal(true);
        setIsLoading(false);
        return;
      }
      const result = await response.json();
      if (result.error === 'idea_limit_exceeded' || result.requiresPayment) {
        if (timerInterval) clearInterval(timerInterval);
        setIdeaTimer(null);
        toast.dismiss(GENERATE_IDEAS_TOAST_ID);
        setShowIdeaLimitModal(true);
        setIsLoading(false);
        return;
      }
      if (result.success && result.ideas) {
        if (timerInterval) clearInterval(timerInterval);
        setIdeaTimer(null);
        toast.dismiss(GENERATE_IDEAS_TOAST_ID);
        generatedIdeasSourceRef.current = 'single-video';
        setGeneratedIdeas(result.ideas.map((idea, idx) => ({ ...idea, id: `generated-${idx}` })));
        setSelectedIdeasForBoard(new Set(result.ideas.map((_, idx) => `generated-${idx}`)));
        setShowGeneratedIdeasModal(true);
        setTimeout(() => checkUserLimits(), 1000);
      } else {
        if (timerInterval) clearInterval(timerInterval);
        setIdeaTimer(null);
        toast.dismiss(GENERATE_IDEAS_TOAST_ID);
        toast.error(result.error || 'Failed to generate brief from video');
      }
    } catch (error) {
      console.error('Error generating brief from video:', error);
      toast.dismiss(GENERATE_IDEAS_TOAST_ID);
      toast.error('Failed to generate brief from video');
    } finally {
      if (timerInterval) clearInterval(timerInterval);
      setIdeaTimer(null);
      setIsLoading(false);
    }
  }, [userId, userLimits.exceeded, userLimits.reason, userLimits.ideaLimitExceeded]);

  // When Create Idea modal opens from trend card, start generation immediately (no extra click)
  useEffect(() => {
    if (!showCreateIdeaModal || !selectedTrend?.id || createIdeaModalAutoStartedRef.current) return;
    if (userLimits.exceeded && (userLimits.reason === 'idea_limit' || userLimits.ideaLimitExceeded)) {
      setShowCreateIdeaModal(false);
      setShowIdeaLimitModal(true);
      return;
    }
    createIdeaModalAutoStartedRef.current = true;
    runGenerateIdeas();
  }, [showCreateIdeaModal, selectedTrend?.id, userLimits.exceeded, userLimits.reason, userLimits.ideaLimitExceeded, runGenerateIdeas]);
  useEffect(() => {
    if (!showCreateIdeaModal) createIdeaModalAutoStartedRef.current = false;
  }, [showCreateIdeaModal]);

  // Check for active jobs
  const checkActiveJobs = async () => {
    if (!userId) return;
    try {
      // Fetch jobs for this user (or anonymous if userId is null)
      const response = await fetch(`/api/trend-spotter/jobs?userId=${encodeURIComponent(userId)}`);
      const result = await response.json();
      if (result.success) {
        const libraryAddJobs = result.jobs.filter(job => 
          job.type === 'library_add' && 
          (job.userId === userId || job.userId === 'anonymous' || job.userId === null || !job.userId)
        );
        const runningJobs = libraryAddJobs.filter(job => job.status === 'running');
        
        // Check if any previously running jobs have completed
        setActiveJobs(prevActiveJobs => {
          const prevRunningIds = new Set(prevActiveJobs.map(j => j.id));
          const completedJobs = libraryAddJobs.filter(job => 
            (job.status === 'completed' || job.status === 'failed') && 
            prevRunningIds.has(job.id)
          );
          
          if (completedJobs.length > 0) {
            // Clear timers for completed jobs
            setJobTimers(prev => {
              const newMap = new Map(prev);
              completedJobs.forEach(job => newMap.delete(job.id));
              return newMap;
            });
            
            // Refresh library when jobs complete
            loadLibraryRef.current?.(true); // Always use latest loadLibrary so UI updates
            loadTrends(false); // Refresh trends list (no top videos needed)
            // Refresh limits when videos are added
            checkUserLimits();
            
            completedJobs.forEach(job => {
              if (job.status === 'completed') {
                toast.success(`✅ Added ${job.videoCount || 0} video(s) to library`);
              } else {
                toast.error(`❌ Failed to add videos: ${job.error || 'Unknown error'}`);
              }
            });
          }
          
          // Update jobs with estimated time and start time
          const updatedJobs = runningJobs.map(job => {
            const existingJob = prevActiveJobs.find(j => j.id === job.id);
            const estimatedTime = job.estimatedTime || (job.videoCount || 0) * 90; // 1.5 min per video
            return {
              ...job,
              estimatedTime: estimatedTime,
              startTime: existingJob?.startTime || job.createdAt ? new Date(job.createdAt).getTime() : Date.now()
            };
          });
          
          // Initialize timers for new jobs
          setJobTimers(prev => {
            const newMap = new Map(prev);
            updatedJobs.forEach(job => {
              if (!newMap.has(job.id)) {
                newMap.set(job.id, {
                  startTime: job.startTime,
                  estimatedSeconds: job.estimatedTime,
                  elapsedSeconds: 0
                });
              }
            });
            return newMap;
          });
          
          return updatedJobs;
        });
      }
    } catch (error) {
      console.error('Error checking active jobs:', error);
    }
  };

  const loadLibrary = async (forceReload = false) => {
    if (userId === null || userId === undefined) return;
    
    // If library is already loaded and we're not forcing a reload, skip
    // This prevents unnecessary API calls when switching views
    if (!forceReload && libraryVideos.length > 0 && view === 'library') {
      return;
    }
    
    try {
      const response = await fetch(`/api/trend-spotter/library?userId=${encodeURIComponent(userId)}&page=1&limit=20`);
      const result = await response.json();
      if (result.success) {
        setLibraryVideos(result.videos);
        setLibraryPagination({
          page: 1,
          hasMore: result.pagination?.hasMore || false,
          loadingMore: false,
          total: result.pagination?.total || result.videos.length
        });
        setLibraryLoadAttempted(true); // so we only show sample videos when user's library is empty
        // Reset scroll position when force reloading
        if (forceReload && libraryScrollRef.current) {
          libraryScrollRef.current.scrollTop = 0;
        }
        loadLibraryForTrendFilters();
      }
    } catch (error) {
      console.error('Error loading library:', error);
    }
  };

  useEffect(() => {
    loadLibraryRef.current = loadLibrary;
  });

  // Load more library videos (for lazy loading)
  const loadMoreLibrary = async () => {
    if (!userId || libraryPagination.loadingMore || !libraryPagination.hasMore) return;
    
    setLibraryPagination(prev => ({ ...prev, loadingMore: true }));
    
    try {
      const nextPage = libraryPagination.page + 1;
      const response = await fetch(`/api/trend-spotter/library?userId=${encodeURIComponent(userId)}&page=${nextPage}&limit=20`);
      const result = await response.json();
      if (result.success) {
        setLibraryVideos(prev => [...prev, ...result.videos]);
        setLibraryVideosForTrendFilters((prev) => {
          const byId = new Map(prev.map((v) => [v.id, v]));
          for (const v of result.videos) {
            if (v && v.id != null && !byId.has(v.id)) byId.set(v.id, v);
          }
          return Array.from(byId.values());
        });
        setLibraryPagination({
          page: nextPage,
          hasMore: result.pagination?.hasMore || false,
          loadingMore: false,
          total: result.pagination?.total || libraryPagination.total
        });
      }
    } catch (error) {
      console.error('Error loading more library videos:', error);
      setLibraryPagination(prev => ({ ...prev, loadingMore: false }));
    }
  };

  const updateTrend = async (trendId, updates) => {
    if (!userId) return;
    try {
      const response = await fetch(`/api/trend-spotter/templates/${trendId}?userId=${encodeURIComponent(userId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      const result = await response.json();
      if (result.success) {
        // Update local state
        setTrends(prev => prev.map(t => t.id === trendId ? result.template : t));
        if (selectedTrend && selectedTrend.id === trendId) {
          setSelectedTrend(result.template);
        }
        toast.success('Trend updated');
      } else {
        toast.error(result.error || 'Failed to update trend');
      }
    } catch (error) {
      console.error('Error updating trend:', error);
      toast.error('Failed to update trend');
    }
  };

  const loadTrends = async (loadTopVideos = false) => {
    try {
      const response = await fetch(`/api/trend-spotter/templates?userId=${encodeURIComponent(userId || '')}`);
      const result = await response.json();
      if (result.success) {
        setTrends(result.templates);
        
        // Load top videos if explicitly requested
        if (loadTopVideos) {
          // Only load top videos for trends that don't already have them cached
          const topVideosMap = { ...trendTopVideos }; // Keep existing cached videos
          const trendsNeedingVideos = result.templates.filter(trend => !topVideosMap[trend.id]);
          
          // Only load if there are trends needing videos
          if (trendsNeedingVideos.length > 0) {
            // Load top videos in batches to avoid overwhelming the server
            const BATCH_SIZE = 5;
            for (let i = 0; i < trendsNeedingVideos.length; i += BATCH_SIZE) {
              const batch = trendsNeedingVideos.slice(i, i + BATCH_SIZE);
              await Promise.all(
                batch.map(async (trend) => {
                  try {
                    const topVideosResponse = await fetch(`/api/trend-spotter/trends/${trend.id}/top-videos?userId=${encodeURIComponent(userId || '')}`);
                    const topVideosResult = await topVideosResponse.json();
                    if (topVideosResult.success && topVideosResult.videos) {
                      topVideosMap[trend.id] = topVideosResult.videos.slice(0, 3); // Get top 3
                    }
                  } catch (error) {
                    console.error(`Error loading top videos for trend ${trend.id}:`, error);
                  }
                })
              );
              // Update state after each batch to show progress
              setTrendTopVideos({ ...topVideosMap });
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading trends:', error);
    }
  };

  const loadBriefs = async (forceReload = false) => {
    if (userId === null || userId === undefined) return;
    
    // If briefs are already loaded and we're not forcing a reload, skip
    // This prevents unnecessary API calls when switching views
    if (!forceReload && briefs.length > 0 && view === 'board') {
      return;
    }
    
    try {
      const response = await fetch(`/api/trend-spotter/content-board/briefs?userId=${encodeURIComponent(userId)}`);
      const result = await response.json();
      if (result.success) {
        // Ensure trend_id is properly parsed
        const briefsWithTrend = result.briefs.map(brief => ({
          ...brief,
          trend_id: brief.trend_id ? (typeof brief.trend_id === 'string' ? parseInt(brief.trend_id) : brief.trend_id) : null
        }));
        
        // Check user limits when loading briefs to show warnings if needed
        checkUserLimits();
        
        // Debug: Log brief ownership info
        const ownedCount = briefsWithTrend.filter(b => b.user_id === userId).length;
        const sharedCount = briefsWithTrend.filter(b => 
          b.user_id !== userId && 
          b.collaborators?.some(c => c.user_id === userId)
        ).length;
        console.log(`📋 Loaded ${briefsWithTrend.length} briefs: ${ownedCount} owned, ${sharedCount} shared`);
        
        setBriefs(briefsWithTrend);
      }
    } catch (error) {
      console.error('Error loading briefs:', error);
    }
  };

  const loadBrandSettings = async () => {
    if (!userId) return;
    try {
      const response = await fetch(`/api/trend-spotter/brand-settings?userId=${encodeURIComponent(userId)}`);
      const result = await response.json();
      if (!result.success) return;

      const emptyDefaults = {
        brand_name: '',
        brand_description: '',
        target_audience: '',
        content_themes: '',
        social_platform: 'tiktok',
        social_handle: ''
      };

      // API returns settings: null when there is no row — still run auto-prompt for cold-start users
      const next = result.settings
        ? {
            brand_name: result.settings.brand_name || '',
            brand_description: result.settings.brand_description || '',
            target_audience: result.settings.target_audience || '',
            content_themes: result.settings.content_themes || '',
            social_platform: result.settings.social_platform || 'tiktok',
            social_handle: result.settings.social_handle || ''
          }
        : { ...emptyDefaults };

      setBrandSettings(next);
      maybeAutoOpenBrandSettingsPrompt(next);
    } catch (error) {
      console.error('Error loading brand settings:', error);
    }
  };

  const handleManualAdd = async () => {
    // Check limits before processing
    if (userLimits.exceeded && (userLimits.reason === 'video_limit' || userLimits.videoLimitExceeded)) {
      toast.error('You have reached the free tier limit of 30 videos. Please upgrade to continue.');
      const returnPath = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/payment?reason=video_limit&return=${returnPath}`;
      return;
    }
    
    if (!manualLinks.trim()) {
      toast.error('Please paste at least one URL');
      return;
    }

    // Smart URL parsing: handle newlines, tabs, spaces, and mixed separators
    const urlList = manualLinks
      .split(/\r?\n/)
      .flatMap(line => line.split(/\t/))
      .flatMap(item => {
        if (item.trim().startsWith('http')) {
          return [item.trim()];
        }
        return item.split(/\s+/).filter(s => s.trim().startsWith('http'));
      })
      .map(url => {
        const trimmed = url.trim();
        return trimmed.replace(/[.,;:!?]+$/, '');
      })
      .filter(url => {
        return url && (url.includes('tiktok.com') || url.includes('instagram.com'));
      })
      .filter((url, index, self) => self.indexOf(url) === index);

    if (urlList.length === 0) {
      toast.error('No valid URLs found. Please paste TikTok or Instagram URLs.');
      return;
    }

    setIsLoading(true);
    // Start timer: 1.5 minutes (90 seconds) per video
    const estimatedTimeSeconds = urlList.length * 90;
    let videoTimerSeconds = estimatedTimeSeconds;
    setVideoTimer(videoTimerSeconds);
    const videoTimerInterval = setInterval(() => {
      videoTimerSeconds--;
      if (videoTimerSeconds > 0) {
        setVideoTimer(videoTimerSeconds);
      } else {
        clearInterval(videoTimerInterval);
        setVideoTimer(null);
      }
    }, 1000);
    
    try {
      const response = await fetch('/api/trend-spotter/library/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ urls: urlList, userId })
      });

      const result = await response.json();
      
      // Check for paywall error first (403 status or error flag)
      if (response.status === 403 || result.error === 'video_limit_exceeded' || result.requiresPayment) {
        clearInterval(videoTimerInterval);
        setVideoTimer(null);
        toast.error(result.message || 'Video limit exceeded');
        const returnPath = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.href = `/payment?reason=video_limit&return=${returnPath}`;
        setIsLoading(false);
        return;
      }
      
      if (result.success) {
        // Use estimated time from server if available, otherwise use calculated
        const serverEstimatedTime = result.estimatedTime || estimatedTimeSeconds;
        if (result.estimatedTime && result.estimatedTime !== estimatedTimeSeconds) {
          // Update timer if server provides different estimate
          clearInterval(videoTimerInterval);
          videoTimerSeconds = result.estimatedTime;
          setVideoTimer(videoTimerSeconds);
          const newTimerInterval = setInterval(() => {
            videoTimerSeconds--;
            if (videoTimerSeconds > 0) {
              setVideoTimer(videoTimerSeconds);
            } else {
              clearInterval(newTimerInterval);
              setVideoTimer(null);
            }
          }, 1000);
        }
        
        const minutes = Math.floor(serverEstimatedTime / 60);
        const seconds = serverEstimatedTime % 60;
        const timeDisplay = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
        toast.success(`Adding ${urlList.length} videos to library... (Est: ${timeDisplay})`);
        setManualLinks('');
        setShowManualAddModal(false);
        
        // Refresh limits after successful submission (will update after videos are added)
        setTimeout(() => checkUserLimits(), 2000);
        
        // Start tracking this job
        if (result.jobId) {
          const estimatedTime = result.estimatedTime || (result.videoCount || urlList.length) * 90; // 1.5 min per video
          const newJob = {
            id: result.jobId,
            status: 'running',
            videoCount: result.videoCount || urlList.length,
            type: 'library_add',
            estimatedTime: estimatedTime,
            startTime: Date.now()
          };
          setActiveJobs(prev => [...prev, newJob]);
          
          // Initialize timer for this job
          setJobTimers(prev => {
            const newMap = new Map(prev);
            newMap.set(result.jobId, {
              startTime: Date.now(),
              estimatedSeconds: estimatedTime,
              elapsedSeconds: 0
            });
            return newMap;
          });
          
          // Poll for job completion
          const pollJob = async () => {
            try {
              const statusResponse = await fetch(`/api/trend-spotter/jobs/${result.jobId}`);
              const statusResult = await statusResponse.json();
              if (statusResult.success && statusResult.job) {
                if (statusResult.job.status === 'completed') {
                  clearInterval(videoTimerInterval);
                  setVideoTimer(null);
                  // Clear timer for this job
                  setJobTimers(prev => {
                    const newMap = new Map(prev);
                    newMap.delete(result.jobId);
                    return newMap;
                  });
                  setActiveJobs(prev => prev.filter(j => j.id !== result.jobId));
                  loadLibraryRef.current?.(true); // Always use latest loadLibrary so UI updates
                  loadTrends(false); // Refresh trends list (no top videos needed)
                  toast.success(`✅ Added ${statusResult.job.videoCount || urlList.length} video(s) to library`);
                  completeOnboardingStage(3);
                  return true; // Stop polling
                } else if (statusResult.job.status === 'failed') {
                  clearInterval(videoTimerInterval);
                  setVideoTimer(null);
                  // Clear timer for this job
                  setJobTimers(prev => {
                    const newMap = new Map(prev);
                    newMap.delete(result.jobId);
                    return newMap;
                  });
                  setActiveJobs(prev => prev.filter(j => j.id !== result.jobId));
                  toast.error(`❌ Failed to add videos: ${statusResult.job.error || 'Unknown error'}`);
                  return true; // Stop polling
                }
              }
            } catch (error) {
              console.error('Error polling job status:', error);
            }
            return false; // Continue polling
          };
          
          // Run first poll immediately so we don't wait 2s if job is already done
          pollJob().then(stopped => {
            if (stopped) return;
            // Poll every 2 seconds
            const pollInterval = setInterval(async () => {
              const shouldStop = await pollJob();
              if (shouldStop) {
                clearInterval(pollInterval);
              }
            }, 2000);
            // Stop polling after 5 minutes
            setTimeout(() => {
              clearInterval(pollInterval);
              setJobTimers(prev => {
                const newMap = new Map(prev);
                newMap.delete(result.jobId);
                return newMap;
              });
              setActiveJobs(prev => prev.filter(j => j.id !== result.jobId));
            }, 300000);
          });
        }
      } else {
        toast.error(result.error || 'Failed to add videos');
      }
    } catch (error) {
      console.error('Error adding videos:', error);
      toast.error('Failed to add videos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSyncBookmarks = () => {
    const extensionUrl = 'https://chromewebstore.google.com/detail/trend-spotter-tiktok-inst/mbialoaibhbcocebllkmlecfmnoaeeij';
    window.open(extensionUrl, '_blank');
    toast('Opening Chrome Web Store to install the extension', { icon: 'ℹ️' });
  };

  const handleSubmitTrend = async () => {
    if (selectedVideos.size < 3) {
      toast.error('Please select at least 3 videos to create a trend');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch('/api/trend-spotter/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          videoIds: Array.from(selectedVideos),
          userId
        })
      });

      const result = await response.json();
      if (result.success) {
        toast.success('Trend submitted! It will be pending until approved.');
        setSelectedVideos(new Set());
        setIsSelectionMode(false);
        loadTrends(false); // Refresh trends list (no top videos needed)
        setView('trends');
      } else {
        toast.error(result.error || 'Failed to submit trend');
      }
    } catch (error) {
      console.error('Error submitting trend:', error);
      toast.error('Failed to submit trend');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleVideoSelection = (id) => {
    setSelectedVideos(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAddTag = () => {
    if (!tagInput.trim()) return;
    
    const newTag = tagInput.trim();
    // Ensure tags is always an array
    const currentTags = Array.isArray(selectedBrief.tags) ? selectedBrief.tags : (selectedBrief.tags ? [selectedBrief.tags] : []);
    console.log('Adding tag:', newTag, 'Current tags:', currentTags);
    
    if (!currentTags.includes(newTag)) {
      const updatedTags = [...currentTags, newTag];
      console.log('Updated tags:', updatedTags);
      setSelectedBrief({ ...selectedBrief, tags: updatedTags });
      setTagInput('');
      setShareError('');
    } else {
      setShareError('Tag already exists');
    }
  };

  const handleSaveBrief = async (briefData) => {
    // Pricing barrier is only on Generate Ideas button; adding to board is allowed.
    const briefStatus = briefData.status || 'ideas';
    // Save collaborators after brief is created
    const collaboratorsToSave = briefData.collaborators || [];
    try {
      // Remove collaborators from briefData before saving (they'll be added separately)
      const { collaborators, ...briefDataWithoutCollaborators } = briefData;
      
      // Ensure tags is an array (default to empty array if not set)
      const tagsToSave = Array.isArray(briefDataWithoutCollaborators.tags) 
        ? briefDataWithoutCollaborators.tags.filter(t => t && t.trim())
        : [];
      
      console.log('handleSaveBrief - briefData:', briefData);
      console.log('handleSaveBrief - briefDataWithoutCollaborators:', briefDataWithoutCollaborators);
      console.log('handleSaveBrief - tagsToSave:', tagsToSave);
      
      const requestBody = {
        ...briefDataWithoutCollaborators,
        tags: tagsToSave,
        userId,
        creation_source: 'manual_board'
      };
      
      console.log('Saving brief with requestBody:', JSON.stringify(requestBody, null, 2));
      
      const response = await fetch('/api/trend-spotter/content-board/briefs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      // If server returns paywall (e.g. limit enforced server-side), show same idea-limit modal as Content Board.
      if (response.status === 403) {
        let result;
        try {
          result = await response.json();
        } catch (e) {
          result = { error: 'idea_limit_exceeded', message: 'Idea limit exceeded' };
        }
        setShowIdeaLimitModal(true);
        return;
      }

      const result = await response.json();
      
      console.log('📝 Brief creation response:', { status: response.status, result });
      
      if (result.error === 'idea_limit_exceeded' || result.requiresPayment) {
        setShowIdeaLimitModal(true);
        return;
      }
      
      if (result.success) {
        // Add collaborators if any
        if (collaboratorsToSave.length > 0) {
          await Promise.all(
            collaboratorsToSave.map(collab =>
              fetch(`/api/trend-spotter/content-board/briefs/${result.brief.id}/collaborators`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  userId: collab.user_id || collab.email,
                  permission: collab.permission || 'view'
                })
              })
            )
          );
        }
        
        // Ensure trend_id is properly parsed
        const newBrief = {
          ...result.brief,
          trend_id: result.brief.trend_id ? (typeof result.brief.trend_id === 'string' ? parseInt(result.brief.trend_id) : result.brief.trend_id) : null,
          tags: Array.isArray(result.brief.tags) ? result.brief.tags : [],
          collaborators: result.brief.collaborators || []
        };
        
        // Add the new brief to the state immediately
        setBriefs(prevBriefs => {
          // Check if brief already exists (in case of duplicate)
          const exists = prevBriefs.some(b => b.id === newBrief.id);
          if (exists) {
            // Update existing brief
            return prevBriefs.map(b => b.id === newBrief.id ? newBrief : b);
          } else {
            // Add new brief at the beginning
            return [newBrief, ...prevBriefs];
          }
        });
        
        toast.success('Brief saved!');
        setShowBriefModal(false);
        setSelectedBrief(null);
        setShareEmail('');
        setShareError('');
        setTagInput('');
        // Force reload to ensure we have the latest data (including any server-side updates)
        loadBriefs(true);
        // Refresh limits after creating an idea
        if (briefStatus === 'ideas') {
          setTimeout(() => checkUserLimits(), 1000);
        }
        completeOnboardingStage(5);
      } else {
        toast.error(result.error || 'Failed to save brief');
      }
    } catch (error) {
      console.error('Error saving brief:', error);
      toast.error('Failed to save brief');
    }
  };

  const handleAddCollaborator = async (briefId, email, permission, ownerEmail = null) => {
    try {
      const response = await fetch(`/api/trend-spotter/content-board/briefs/${briefId}/collaborators`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: email,
          permission,
          ownerEmail: ownerEmail || userId
        })
      });
      const result = await response.json();
      if (result.success) {
        toast.success('Collaborator added and notified via email');
        // Reload briefs to get updated data
        await loadBriefs();
        // Fetch fresh brief data and update selectedBrief immediately
        try {
          const briefResponse = await fetch(`/api/trend-spotter/content-board/briefs?userId=${encodeURIComponent(userId)}`);
          const briefResult = await briefResponse.json();
          if (briefResult.success && briefResult.briefs) {
            const updatedBrief = briefResult.briefs.find(b => b.id === briefId);
            if (updatedBrief && selectedBrief && selectedBrief.id === briefId) {
              setSelectedBrief(updatedBrief);
            }
          }
        } catch (err) {
          console.error('Error refreshing brief:', err);
        }
      } else {
        toast.error(result.error || 'Failed to add collaborator');
      }
      return result.success;
    } catch (error) {
      console.error('Error adding collaborator:', error);
      toast.error('Failed to add collaborator');
      return false;
    }
  };

  const handleRemoveCollaborator = async (briefId, collaboratorId) => {
    try {
      const response = await fetch(`/api/trend-spotter/content-board/briefs/${briefId}/collaborators/${collaboratorId}?userId=${encodeURIComponent(userId)}`, {
        method: 'DELETE'
      });
      const result = await response.json();
      if (result.success) {
        toast.success('Collaborator removed');
        loadBriefs();
        // Update selectedBrief if it's the one being modified
        if (selectedBrief && selectedBrief.id === briefId) {
          const updatedBrief = briefs.find(b => b.id === briefId);
          if (updatedBrief) {
            setSelectedBrief(updatedBrief);
          }
        }
      } else {
        toast.error(result.error || 'Failed to remove collaborator');
      }
    } catch (error) {
      console.error('Error removing collaborator:', error);
      toast.error('Failed to remove collaborator');
    }
  };

  // Get user's permission level for a brief
  const getUserPermission = (brief) => {
    if (!brief || !userId) return 'view';
    if (brief.user_id === userId) return 'owner';
    const collab = brief.collaborators?.find(c => c.user_id === userId);
    return collab?.permission || 'view';
  };

  // Check if user can share with a specific permission level
  // Hierarchy: owner > edit > comment > view
  const canShareWithPermission = (userPermission, targetPermission) => {
    if (userPermission === 'owner') return true; // Owner can share with any permission
    if (userPermission === 'edit') {
      return targetPermission === 'edit' || targetPermission === 'view'; // Edit can share with edit or view
    }
    if (userPermission === 'comment') {
      return targetPermission === 'comment' || targetPermission === 'view'; // Comment can share with comment or view
    }
    if (userPermission === 'view') {
      return targetPermission === 'view'; // View can only share with view
    }
    return false;
  };

  const handleUpdateBriefTrend = async (briefId, trendId) => {
    try {
      // Optimistic update
      const updatedBrief = { ...selectedBrief, trend_id: trendId };
      setSelectedBrief(updatedBrief);
      setBriefs(prevBriefs => prevBriefs.map(b => b.id === briefId ? updatedBrief : b));
      
      const response = await fetch(`/api/trend-spotter/content-board/briefs/${briefId}?userId=${encodeURIComponent(userId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          trend_id: trendId
        })
      });
      
      const result = await response.json();
      if (result.success) {
        // Reload briefs to get fresh data
        loadBriefs(true);
        // Reload trend example videos if trend_id changed
        if (trendId) {
          try {
            const trendResponse = await fetch(`/api/trend-spotter/trends/${trendId}/top-videos?userId=${encodeURIComponent(userId)}`);
            const trendResult = await trendResponse.json();
            if (trendResult.success) {
              setTrendExampleVideos(trendResult.videos || []);
            } else {
              setTrendExampleVideos([]);
            }
          } catch (error) {
            console.error('Error fetching trend examples:', error);
            setTrendExampleVideos([]);
          }
        } else {
          setTrendExampleVideos([]);
        }
        toast.success('Trend updated');
      } else {
        // Revert on error
        setSelectedBrief(selectedBrief);
        setBriefs(prevBriefs => prevBriefs.map(b => b.id === briefId ? selectedBrief : b));
        toast.error(result.error || 'Failed to update trend');
      }
    } catch (err) {
      console.error('Error updating trend:', err);
      // Revert on error
      setSelectedBrief(selectedBrief);
      setBriefs(prevBriefs => prevBriefs.map(b => b.id === briefId ? selectedBrief : b));
      toast.error('Failed to update trend');
    }
  };

  const handleUpdateBriefVideos = async (briefId, videoIds) => {
    try {
      // Optimistic update
      const updatedBrief = { ...selectedBrief, video_ids: videoIds };
      setSelectedBrief(updatedBrief);
      setBriefs(prevBriefs => prevBriefs.map(b => b.id === briefId ? updatedBrief : b));
      
      const response = await fetch(`/api/trend-spotter/content-board/briefs/${briefId}?userId=${encodeURIComponent(userId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          video_ids: videoIds
        })
      });
      
      const result = await response.json();
      if (result.success) {
        // Reload briefs to get fresh data
        loadBriefs(true);
        // Reload selected brief videos
        if (videoIds && videoIds.length > 0) {
          try {
            const videoResponse = await fetch(`/api/trend-spotter/library?userId=${encodeURIComponent(userId)}&page=1&limit=1000`);
            const videoResult = await videoResponse.json();
            if (videoResult.success) {
              const selectedVideos = videoResult.videos.filter(v => videoIds.includes(v.id));
              setSelectedBriefVideos(selectedVideos);
            } else {
              setSelectedBriefVideos([]);
            }
          } catch (error) {
            console.error('Error fetching selected brief videos:', error);
            setSelectedBriefVideos([]);
          }
        } else {
          setSelectedBriefVideos([]);
        }
        toast.success('Videos updated');
      } else {
        // Revert on error
        setSelectedBrief(selectedBrief);
        setBriefs(prevBriefs => prevBriefs.map(b => b.id === briefId ? selectedBrief : b));
        toast.error(result.error || 'Failed to update videos');
      }
    } catch (err) {
      console.error('Error updating videos:', err);
      // Revert on error
      setSelectedBrief(selectedBrief);
      setBriefs(prevBriefs => prevBriefs.map(b => b.id === briefId ? selectedBrief : b));
      toast.error('Failed to update videos');
    }
  };

  const handleDeleteBrief = async (briefId, e) => {
    e.stopPropagation(); // Prevent opening the detail modal
    if (!window.confirm('Are you sure you want to delete this brief?')) {
      return;
    }
    try {
      // Optimistically remove the brief from state immediately
      setBriefs(prevBriefs => prevBriefs.filter(b => b.id !== briefId));
      
      const response = await fetch(`/api/trend-spotter/content-board/briefs/${briefId}?userId=${encodeURIComponent(userId)}`, {
        method: 'DELETE'
      });
      const result = await response.json();
      if (result.success) {
        toast.success('Brief deleted');
        // Force reload to ensure we have the latest data from server
        loadBriefs(true);
      } else {
        // If deletion failed, reload to restore the brief
        toast.error(result.error || 'Failed to delete brief');
        loadBriefs(true);
      }
    } catch (error) {
      console.error('Error deleting brief:', error);
      toast.error('Failed to delete idea');
      // If deletion failed, reload to restore the brief
      loadBriefs(true);
    }
  };

  const handlePostComment = async (inputElement) => {
    if (!inputElement || !inputElement.value.trim()) return;
    
    const commentText = inputElement.value.trim();
    try {
      const response = await fetch(`/api/trend-spotter/content-board/briefs/${selectedBrief.id}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          userName: userId, // Use user email as the user name
          commentText
        })
      });
      const result = await response.json();
      if (result.success) {
        // Update selectedBrief immediately
        const updatedBrief = {
          ...selectedBrief,
          comments: [
            ...(selectedBrief.comments || []),
            result.comment
          ]
        };
        setSelectedBrief(updatedBrief);
        // Also update briefs state optimistically
        setBriefs(prevBriefs => prevBriefs.map(b => b.id === selectedBrief.id ? updatedBrief : b));
        loadBriefs(true);
        inputElement.value = '';
        toast.success('Comment posted');
      } else {
        toast.error(result.error || 'Failed to post comment');
      }
    } catch (err) {
      console.error('Error adding comment:', err);
      toast.error('Failed to post comment');
    }
  };

  const handleDragStart = (e, brief) => {
    setDraggedBrief(brief);
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', ''); // Required for some browsers
  };

  const handleDragEnd = (e) => {
    // Mark that we just dragged to prevent click event
    justDraggedRef.current = true;
    // Small delay to allow drop event to fire first
    setTimeout(() => {
      setIsDragging(false);
      setDraggedBrief(null);
      setDragOverStatus(null);
      // Reset the flag after a short delay
      setTimeout(() => {
        justDraggedRef.current = false;
      }, 200);
    }, 100);
  };

  const handleDragEnter = (e, status) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedBrief && draggedBrief.status !== status) {
      setDragOverStatus(status);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only clear if we're leaving the drop zone (not entering a child)
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverStatus(null);
    }
  };

  const handleDragOver = (e, status) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedBrief) {
      if (draggedBrief.status !== status) {
        // Different column - allow drop
        e.dataTransfer.dropEffect = 'move';
        setDragOverStatus(status);
      } else {
        // Same column - allow reordering
        e.dataTransfer.dropEffect = 'move';
        setDragOverStatus(status);
      }
    } else {
      e.dataTransfer.dropEffect = 'none';
    }
  };

  const handleDrop = async (e, targetStatus, targetBriefId = null) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverStatus(null);
    setDragOverBriefId(null);
    
    if (!draggedBrief) return;
    
    const isSameColumn = draggedBrief.status === targetStatus;
    
    if (isSameColumn && !targetBriefId) {
      // Same column but no target - just refresh
      setIsDragging(false);
      setDraggedBrief(null);
      return;
    }
    
    // Optimistically update the UI immediately
    const originalBriefs = [...briefs];
    setBriefs(prevBriefs => {
      // Remove the brief from its current position
      const updatedBriefs = prevBriefs.filter(b => b.id !== draggedBrief.id);
      
      // Update the brief's status
      const updatedBrief = {
        ...draggedBrief,
        status: targetStatus
      };
      
      if (isSameColumn && targetBriefId) {
        // Reordering within same column - insert at target position
        const targetIndex = updatedBriefs.findIndex(b => b.id === targetBriefId);
        if (targetIndex !== -1) {
          updatedBriefs.splice(targetIndex, 0, updatedBrief);
        } else {
          updatedBriefs.push(updatedBrief);
        }
      } else {
        // Moving to different column - add to the end of target column
        updatedBriefs.push(updatedBrief);
      }
      
      return updatedBriefs;
    });
    
    // Also update selectedBrief if it's the one being moved
    if (selectedBrief && selectedBrief.id === draggedBrief.id) {
      setSelectedBrief({
        ...selectedBrief,
        status: targetStatus
      });
    }
    
    try {
      const requestBody = {
        userId,
        status: targetStatus
      };
      
      // If same column, include target brief ID for reordering
      if (isSameColumn && targetBriefId) {
        requestBody.targetBriefId = targetBriefId;
      }
      
      const response = await fetch(`/api/trend-spotter/content-board/briefs/${draggedBrief.id}?userId=${encodeURIComponent(userId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      
      const result = await response.json();
      if (result.success) {
        if (isSameColumn) {
          toast.success('Reordered');
        } else {
          toast.success(`Moved to ${targetStatus === 'ideas' ? 'Briefs' : targetStatus === 'inProgress' ? 'In Progress' : targetStatus === 'readyToPost' ? 'Ready to Post' : 'Posted'}`);
        }
        // Sync with server to get correct display_order
        loadBriefs(true);
      } else {
        // Revert on error
        setBriefs(originalBriefs);
        if (selectedBrief && selectedBrief.id === draggedBrief.id) {
          setSelectedBrief(draggedBrief);
        }
        toast.error(result.error || 'Failed to move brief');
      }
    } catch (error) {
      console.error('Error moving brief:', error);
      // Revert on error
      setBriefs(originalBriefs);
      if (selectedBrief && selectedBrief.id === draggedBrief.id) {
        setSelectedBrief(draggedBrief);
      }
      toast.error('Failed to move brief');
    } finally {
      setIsDragging(false);
      setDraggedBrief(null);
      setDragOverStatus(null);
      setDragOverBriefId(null);
    }
  };

  // Get posted date - prefer Apify date, fallback to created_at
  const getPostedDate = (video) => {
    // First check stored date
    if (video.apify_posted_date) {
      const date = new Date(video.apify_posted_date);
      if (!isNaN(date.getTime())) {
        return video.apify_posted_date;
      }
    }
    
    // Try to extract from apify_metadata JSONB
    if (video.apify_metadata) {
      const meta = typeof video.apify_metadata === 'string' 
        ? JSON.parse(video.apify_metadata) 
        : video.apify_metadata;
      
      if (meta.createTimeISO) {
        return meta.createTimeISO;
      } else if (meta.createTime) {
        return new Date(meta.createTime * 1000).toISOString();
      } else if (meta.timestamp) {
        return new Date(meta.timestamp * 1000).toISOString();
      }
    }
    
    // Fallback to created_at
    return video.created_at;
  };

  // Get Apify value - reads from apify_metadata JSONB first, then falls back to individual columns
  const getApifyValue = (video, field) => {
    // Try to get from apify_metadata JSONB first
    if (video.apify_metadata) {
      const meta = typeof video.apify_metadata === 'string' 
        ? JSON.parse(video.apify_metadata) 
        : video.apify_metadata;
      
      // Map field names to Apify metadata keys
      // Check TikTok fields first, then Instagram fields (snake_case and camelCase)
      const fieldMap = {
        views: ['playCount', 'videoPlayCount', 'videoViewCount', 'viewCount', 'views', 'view_count', 'views_count', 'video_view_count'],
        likes: ['diggCount', 'likeCount', 'likesCount', 'likes', 'like_count', 'likes_count'],
        comments: ['commentCount', 'commentsCount', 'comments', 'comment_count', 'comments_count'],
        shares: ['shareCount', 'shares', 'share_count', 'shares_count']
      };
      
      const keys = fieldMap[field] || [field];
      for (const key of keys) {
        if (meta[key] !== undefined && meta[key] !== null) {
          return meta[key];
        }
      }
    }
    
    // Fallback to individual columns
    const columnMap = {
      views: 'apify_views',
      likes: 'apify_likes',
      comments: 'apify_comments',
      shares: 'apify_shares'
    };
    
    return video[columnMap[field]] || 0;
  };

  // Parse hashtags - reads from apify_metadata JSONB first, then falls back to apify_hashtags column
  const parseHashtags = (video) => {
    let hashtags = [];
    
    // First, try to get from apify_metadata JSONB
    if (video.apify_metadata) {
      try {
        const meta = typeof video.apify_metadata === 'string' 
          ? JSON.parse(video.apify_metadata) 
          : video.apify_metadata;
        
        // Try hashtags array
        if (meta.hashtags && Array.isArray(meta.hashtags)) {
          hashtags = meta.hashtags;
        }
        
        // If no hashtags array, extract from text field
        if (hashtags.length === 0 && meta.text) {
          const text = meta.text || meta.caption || '';
          const hashtagMatches = text.match(/#[\w\u{1F300}-\u{1F9FF}]+/gu);
          if (hashtagMatches) {
            hashtags = hashtagMatches.map(tag => tag.replace('#', '').trim()).filter(Boolean);
          }
        }
      } catch (e) {
        console.error('Error parsing apify_metadata:', e);
      }
    }
    
    // Fallback to apify_hashtags column
    if (hashtags.length === 0 && video.apify_hashtags) {
      if (Array.isArray(video.apify_hashtags)) {
        hashtags = video.apify_hashtags;
      } else if (typeof video.apify_hashtags === 'string') {
        try {
          const parsed = JSON.parse(video.apify_hashtags);
          hashtags = Array.isArray(parsed) ? parsed : [parsed];
        } catch (e) {
          hashtags = [video.apify_hashtags];
        }
      }
    }
    
    // Normalize hashtags (extract names from objects, remove #, etc.)
    return hashtags.map(tag => {
      // Handle null/undefined
      if (!tag) return null;
      
      // Handle strings
      if (typeof tag === 'string') {
        const cleaned = tag.replace(/^#+/, '').trim();
        // Skip if it's an object string representation
        if (cleaned === '[object Object]' || cleaned.startsWith('{')) {
          return null;
        }
        return cleaned;
      }
      
      // Handle objects
      if (typeof tag === 'object') {
        // Try to get name first
        if (tag.name && typeof tag.name === 'string') {
          return tag.name.replace(/^#+/, '').trim();
        }
        // Try to get id
        if (tag.id && typeof tag.id === 'string') {
          return tag.id.replace(/^#+/, '').trim();
        }
        // Try to get any string property
        for (const key in tag) {
          if (typeof tag[key] === 'string' && tag[key]) {
            return tag[key].replace(/^#+/, '').trim();
          }
        }
        return null;
      }
      
      return String(tag).replace(/^#+/, '').trim();
    }).filter(Boolean);
  };

  // Sort and filter library videos
  const filteredLibraryVideos = useMemo(() => {
    let filtered = [...libraryVideos];
    
    if (filters.status === 'new') {
      // Mark videos as new if created in last 24 hours
      const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
      filtered = filtered.filter(v => new Date(v.created_at) > oneDayAgo);
    }
    
    if (filters.platform !== 'all') {
      filtered = filtered.filter(v => v.platform === filters.platform);
    }
    
    if (filters.creator) {
      filtered = filtered.filter(v => 
        v.apify_author_username?.toLowerCase().includes(filters.creator.toLowerCase())
      );
    }
    
    if (filters.hashtag) {
      const tag = filters.hashtag.replace('#', '').toLowerCase().trim();
      if (tag) {
        filtered = filtered.filter(v => {
          const vHashtags = parseHashtags(v);
          // Filter out null/undefined values and check if any hashtag matches (case-insensitive partial match)
          return vHashtags.filter(ht => ht != null).some(ht => {
            const normalizedHt = typeof ht === 'string' ? ht.toLowerCase() : String(ht).toLowerCase();
            return normalizedHt.includes(tag);
          });
        });
      }
    }
    
    if (filters.trend !== 'all') {
      filtered = filtered.filter(v => {
        if (filters.trend === 'none') {
          return !((v.trends && Array.isArray(v.trends) && v.trends.length > 0) || v.trend_id);
        }
        const trendId = parseInt(filters.trend);
        if (v.trends && Array.isArray(v.trends)) {
          return v.trends.some(t => t.id === trendId);
        }
        return v.trend_id === trendId;
      });
    }
    
    // Filter by tag search (user_tags) - real-time filtering like hashtag filter
    if (tagSearch && tagSearch.trim()) {
      const searchTag = tagSearch.trim().toLowerCase();
      filtered = filtered.filter(v => {
        const userTags = Array.isArray(v.user_tags) ? v.user_tags : [];
        // Check if any tag matches the search (case-insensitive partial match)
        return userTags.some(ut => 
          ut && ut.toLowerCase().includes(searchTag)
        );
      });
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'views':
          aValue = getApifyValue(a, 'views') || 0;
          bValue = getApifyValue(b, 'views') || 0;
          break;
        case 'likes':
          aValue = getApifyValue(a, 'likes') || 0;
          bValue = getApifyValue(b, 'likes') || 0;
          break;
        case 'comments':
          aValue = getApifyValue(a, 'comments') || 0;
          bValue = getApifyValue(b, 'comments') || 0;
          break;
        case 'shares':
          aValue = getApifyValue(a, 'shares') || 0;
          bValue = getApifyValue(b, 'shares') || 0;
          break;
        case 'posted_date':
          aValue = new Date(getPostedDate(a)).getTime();
          bValue = new Date(getPostedDate(b)).getTime();
          break;
        case 'created_at':
        default:
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
      }
      
      if (sortOrder === 'desc') {
        return bValue - aValue;
      } else {
        return aValue - bValue;
      }
    });
    
    return filtered;
  }, [libraryVideos, filters, sortBy, sortOrder, tagSearch]);

  // Combined list: show user's library when they have videos; only show 5 sample videos when we've loaded and user has none
  const libraryListToShow = useMemo(() => {
    if (libraryVideos.length > 0) return filteredLibraryVideos;
    if (!libraryLoadAttempted) return []; // wait for first load so we don't show samples instead of user's videos
    const sampleIds = new Set(sampleVideos.map(s => s.id));
    return [...sampleVideos, ...filteredLibraryVideos.filter(v => !sampleIds.has(v.id))];
  }, [sampleVideos, filteredLibraryVideos, libraryVideos.length, libraryLoadAttempted]);
  
  // Handle column header click for sorting
  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order if clicking same column
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      // Set new column and default to desc
      setSortBy(column);
      setSortOrder('desc');
    }
  };
  
  // Render sort icon
  const renderSortIcon = (column) => {
    if (sortBy !== column) return null;
    return sortOrder === 'desc' ? (
      <ArrowDown className="h-3 w-3 ml-1 inline" />
    ) : (
      <ArrowUp className="h-3 w-3 ml-1 inline" />
    );
  };

  const newCount = libraryVideos.filter(v => {
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const isNew = new Date(v.created_at) > oneDayAgo;
    // Only count as "new" if it doesn't have any trends assigned
    const hasTrends = (v.trends && Array.isArray(v.trends) && v.trends.length > 0) || v.trend_id;
    return isNew && !hasTrends;
  }).length;

  const unlabeledCount = libraryVideos.filter(v => {
    // Check if video has any trends assigned (from new trends array or legacy trend_id)
    const hasTrends = (v.trends && Array.isArray(v.trends) && v.trends.length > 0) || v.trend_id;
    return !hasTrends;
  }).length;

  // Tags come from all loaded templates (GET /templates returns the full list for the user, not paginated).
  const allTrendTagOptions = useMemo(() => {
    const s = new Set();
    trends.forEach((t) => extractTrendTagsForFilter(t).forEach((tag) => s.add(tag)));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [trends]);

  /** Creators from library videos assigned to at least one trend (for filter pick list). Uses full library snapshot when loaded. */
  const allTrendCreatorOptions = useMemo(() => {
    const s = new Set();
    const videos =
      libraryVideosForTrendFilters.length > 0 ? libraryVideosForTrendFilters : libraryVideos;
    videos.forEach((v) => {
      const hasTrend = (v.trends && Array.isArray(v.trends) && v.trends.length > 0) || v.trend_id;
      if (!hasTrend) return;
      const u = extractCreatorUsername(v);
      if (u && u !== 'unknown') s.add(u);
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [libraryVideos, libraryVideosForTrendFilters]);

  const filteredTrendCreatorOptions = useMemo(() => {
    const q = trendCreatorListSearch.trim().toLowerCase();
    if (!q) return allTrendCreatorOptions;
    return allTrendCreatorOptions.filter((c) => c.toLowerCase().includes(q));
  }, [allTrendCreatorOptions, trendCreatorListSearch]);

  const libraryVideoCountDisplay =
    libraryPagination.total > 0 ? libraryPagination.total : libraryVideos.length;

  // Calculate sorted trends with metrics (only when viewing trends tab)
  const sortedTrends = useMemo(() => {
    if (view !== 'trends') return [];

    const videosForTrends =
      libraryVideosForTrendFilters.length > 0 ? libraryVideosForTrendFilters : libraryVideos;

    const trendsWithData = trends.map(trend => {
      // Use backend-provided stats if available (for shared trends), otherwise calculate from full library snapshot when loaded
      const savedCount = trend.savedCount !== undefined ? trend.savedCount : (() => {
      const savedVideos = videosForTrends.filter(v => {
        // Check if video is assigned to this trend
        const hasTrend = (v.trends && Array.isArray(v.trends) && v.trends.some(t => t.id === trend.id)) ||
                        (v.trend_id === trend.id);
        return hasTrend;
      });
        return savedVideos.length;
      })();
      
      const totalViews = trend.totalViews !== undefined ? trend.totalViews : (() => {
        const savedVideos = videosForTrends.filter(v => {
          // Check if video is assigned to this trend
          const hasTrend = (v.trends && Array.isArray(v.trends) && v.trends.some(t => t.id === trend.id)) ||
                          (v.trend_id === trend.id);
          return hasTrend;
        });
        return savedVideos.reduce((sum, v) => {
        const views = getApifyValue(v, 'views');
        return sum + (parseInt(views) || 0);
      }, 0);
      })();
      
      // Get saved videos for trending calculation (only from user's library)
      const savedVideos = videosForTrends.filter(v => {
        // Check if video is assigned to this trend
        const hasTrend = (v.trends && Array.isArray(v.trends) && v.trends.some(t => t.id === trend.id)) ||
                        (v.trend_id === trend.id);
        return hasTrend;
      });
      
      // Calculate trending score based on recent activity
      const daysSinceCreated = Math.max(1, Math.floor((Date.now() - new Date(trend.created_at || Date.now()).getTime()) / (1000 * 60 * 60 * 24)));
      const velocityScore = (savedCount * 10 + totalViews / 100000) / daysSinceCreated;
      const isTrending = velocityScore > 5 || savedVideos.some(v => 
        new Date(v.created_at) > new Date(Date.now() - 48 * 60 * 60 * 1000)
      );
      
      // Calculate latest video post date for this trend
      const latestVideoDate = savedVideos.length > 0
        ? Math.max(...savedVideos.map(v => {
            const postDate = getPostedDate(v);
            return postDate ? new Date(postDate).getTime() : 0;
          }))
        : 0;
      
      return {
        ...trend,
        savedCount,
        savedVideos,
        totalViews,
        velocityScore,
        isTrending,
        latestVideoDate // Store latest video post date for sorting
      };
    });
    
    // Sort trends based on selected option
    const sorted = [...trendsWithData].sort((a, b) => {
      switch (trendSortBy) {
        case 'recent':
          return (b.latestVideoDate || 0) - (a.latestVideoDate || 0);
        case 'videos':
          return b.savedCount - a.savedCount;
        case 'views':
          return b.totalViews - a.totalViews;
        case 'trending':
        default:
          if (a.isTrending && !b.isTrending) return -1;
          if (!a.isTrending && b.isTrending) return 1;
          return b.velocityScore - a.velocityScore;
      }
    });
    // Filter by status (approved / pending) for easier review
    let filtered = sorted;
    if (trendStatusFilter === 'approved') filtered = filtered.filter(t => t.status === 'approved');
    if (trendStatusFilter === 'pending') filtered = filtered.filter(t => t.status === 'pending');
    const q = (trendSearchQuery || '').trim().toLowerCase();
    if (q) filtered = filtered.filter(t => (t.trend_name || '').toLowerCase().includes(q));

    const fromMs = parseYmdStartMs(trendDateFrom.trim());
    const toMs = parseYmdEndMs(trendDateTo.trim());
    if (fromMs != null || toMs != null) {
      filtered = filtered.filter((t) => {
        const created = new Date(t.created_at || 0).getTime();
        if (fromMs != null && created < fromMs) return false;
        if (toMs != null && created > toMs) return false;
        return true;
      });
    }

    if (trendTagFilters.length > 0) {
      const selected = trendTagFilters.map((s) => s.trim().toLowerCase()).filter(Boolean);
      filtered = filtered.filter((t) => {
        const trendTags = extractTrendTagsForFilter(t).map((tag) => tag.toLowerCase());
        return selected.every((sel) => trendTags.includes(sel));
      });
    }

    if (trendCreatorFilters.length > 0) {
      const selected = trendCreatorFilters.map((c) => c.trim().toLowerCase()).filter(Boolean);
      filtered = filtered.filter((t) =>
        selected.some((sel) =>
          (t.savedVideos || []).some((v) => extractCreatorUsername(v).toLowerCase() === sel)
        )
      );
    }

    return filtered;
  }, [
    trends,
    libraryVideos,
    libraryVideosForTrendFilters,
    trendSortBy,
    trendStatusFilter,
    trendSearchQuery,
    trendTagFilters,
    trendDateFrom,
    trendDateTo,
    trendCreatorFilters,
    view
  ]);

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(0) + 'K';
    return num?.toString() || '0';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Helper function to extract sound ID from video metadata and generate URL
  const getSoundUrl = (video) => {
    if (!video || !video.apify_metadata) return null;
    
    try {
      const meta = typeof video.apify_metadata === 'string' 
        ? JSON.parse(video.apify_metadata) 
        : video.apify_metadata;
      
      const platform = video.platform || 'tiktok';
      
      // TikTok: musicMeta.musicId
      if (platform === 'tiktok' && meta.musicMeta?.musicId) {
        return `https://www.tiktok.com/music/Originalton-${meta.musicMeta.musicId}`;
      }
      
      // Instagram: musicInfo/audio_id
      if (platform === 'instagram') {
        // Try different possible paths for Instagram audio_id
        const audioId = meta.musicInfo?.audio_id || 
                       meta.audio_id || 
                       meta.music?.audio_id ||
                       meta.musicInfo?.id;
        if (audioId) {
          return `https://www.instagram.com/reels/audio/${audioId}`;
        }
      }
      
      return null;
    } catch (e) {
      console.error('Error extracting sound URL:', e);
      return null;
    }
  };

  // Helper function to get most popular sound from trend videos
  // Fetch all videos for a trend
  const loadTrendVideos = async (trendId, opts = {}) => {
    const { skipScrollToTop = false } = opts;
    if (!trendId || !userId) return;
    try {
      const response = await fetch(`/api/trend-spotter/trends/${trendId}/videos?userId=${encodeURIComponent(userId)}`);
      const result = await response.json();
      if (result.success) {
        setTrendVideos(result.videos);
        if (!skipScrollToTop && viewRef.current === 'trend-detail') {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              scrollPageToTop();
            });
          });
        }
      }
    } catch (error) {
      console.error('Error loading trend videos:', error);
    }
  };

  const handleRemoveVideoFromTrend = async (videoId, trendId) => {
    if (!userId) return;
    
    if (!window.confirm('Are you sure you want to remove this video from the trend?')) {
      return;
    }
    
    try {
      const response = await fetch(`/api/trend-spotter/trends/${trendId}/videos/${videoId}?userId=${encodeURIComponent(userId)}`, {
        method: 'DELETE'
      });
      const result = await response.json();
      
      if (result.success) {
        toast.success('Video removed from trend');
        // Reload trend videos
        await loadTrendVideos(trendId, { skipScrollToTop: true });
        // Also reload the trend to update video count
        if (selectedTrend) {
          const trendResponse = await fetch(`/api/trend-spotter/templates?userId=${encodeURIComponent(userId)}`);
          const trendResult = await trendResponse.json();
          if (trendResult.success) {
            const updatedTrend = trendResult.templates.find(t => t.id === trendId);
            if (updatedTrend) {
              setSelectedTrend(updatedTrend);
            }
          }
        }
      } else {
        toast.error(result.error || 'Failed to remove video from trend');
      }
    } catch (error) {
      console.error('Error removing video from trend:', error);
      toast.error('Failed to remove video from trend');
    }
  };

  const getTrendSoundUrls = (trendId) => {
    // Use trendVideos if available (for shared trends), otherwise fallback to libraryVideos
    const videosToUse = trendVideos.length > 0 && selectedTrend?.id === trendId
      ? trendVideos 
      : (libraryVideos.filter(v => {
          const hasTrend = (v.trends && Array.isArray(v.trends) && v.trends.some(t => t.id === trendId)) ||
                          (v.trend_id === trendId);
          return hasTrend;
        }));
    
    if (!trendId || videosToUse.length === 0) return { tiktok: null, instagram: null };
    
    // Get videos assigned to this trend
    const trendVideosList = videosToUse;
    
    // Count sound occurrences by platform
    const soundCounts = { tiktok: {}, instagram: {} };
    
    trendVideosList.forEach(video => {
      const soundUrl = getSoundUrl(video);
      if (!soundUrl) return;
      
      const platform = video.platform || 'tiktok';
      if (soundCounts[platform]) {
        soundCounts[platform][soundUrl] = (soundCounts[platform][soundUrl] || 0) + 1;
      }
    });
    
    // Get most popular sound for each platform
    const getMostPopular = (counts) => {
      let maxCount = 0;
      let mostPopular = null;
      Object.entries(counts).forEach(([url, count]) => {
        if (count > maxCount) {
          maxCount = count;
          mostPopular = url;
        }
      });
      return mostPopular;
    };
    
    return {
      tiktok: getMostPopular(soundCounts.tiktok),
      instagram: getMostPopular(soundCounts.instagram)
    };
  };

  // Helper function to get all popular sounds from a trend with counts
  const getTrendPopularSounds = (trendId) => {
    if (!trendId || !libraryVideos.length) return [];
    
    // Get videos assigned to this trend
    const trendVideos = libraryVideos.filter(v => {
      const hasTrend = (v.trends && Array.isArray(v.trends) && v.trends.some(t => t.id === trendId)) ||
                      (v.trend_id === trendId);
      return hasTrend;
    });
    
    // Count sound occurrences with platform info
    const soundMap = new Map();
    
    trendVideos.forEach(video => {
      const soundUrl = getSoundUrl(video);
      if (!soundUrl) return;
      
      const platform = video.platform || 'tiktok';
      const key = soundUrl;
      
      if (!soundMap.has(key)) {
        soundMap.set(key, {
          url: soundUrl,
          platform: platform,
          count: 0
        });
      }
      soundMap.get(key).count++;
    });
    
    // Convert to array and sort by count (most popular first)
    const sounds = Array.from(soundMap.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 10); // Limit to top 10
    
    return sounds;
  };

  const getCreatorUsername = extractCreatorUsername;

  // Inline Trend Editor Component (supports multiple trends)
  const InlineTrendEditor = ({ video, onUpdate, trends, loadTrends, setView }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [trendLabelSearchQuery, setTrendLabelSearchQuery] = useState('');
    // Get assigned trend IDs from video.trends array (new format) or fallback to trend_id (legacy)
    const assignedTrendIds = video.trends && Array.isArray(video.trends) 
      ? video.trends.map(t => t.id).filter(Boolean)
      : (video.trend_id ? [video.trend_id] : []);
    const [selectedTrendIds, setSelectedTrendIds] = useState(new Set(assignedTrendIds));
    const [isCreatingNew, setIsCreatingNew] = useState(false);
    const [newTrendName, setNewTrendName] = useState('');
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside - persist until user explicitly clicks outside or saves
    useEffect(() => {
      if (!isEditing) {
        console.log('[TrendEditor] Not editing, skipping click-outside handler');
        hasOpenDropdownRef.current = false;
        setTrendLabelSearchQuery('');
        return;
      }
      
      console.log('[TrendEditor] Setting up click-outside handler, isEditing:', isEditing);
      hasOpenDropdownRef.current = true;
      
      const handleClickOutside = (event) => {
        console.log('[TrendEditor] Click detected:', {
          target: event.target,
          targetTag: event.target.tagName,
          targetClass: event.target.className,
          hasDropdownRef: !!dropdownRef.current
        });
        
        // Only close if click is truly outside the dropdown AND not on any button that opens it
        if (!dropdownRef.current) {
          console.log('[TrendEditor] No dropdownRef, returning');
          return;
        }
        
        const target = event.target;
        const isInsideDropdown = dropdownRef.current.contains(target);
        
        console.log('[TrendEditor] isInsideDropdown:', isInsideDropdown);
        
        // Check if clicking on the edit button that opens it
        const editButton = target.closest('button[title="Edit trends"]') || 
                          target.closest('button')?.textContent?.includes('+ Add trend');
        
        console.log('[TrendEditor] editButton found:', !!editButton);
        
        // Only close if click is outside AND not on the edit button
        if (!isInsideDropdown && !editButton) {
          console.log('[TrendEditor] Closing dropdown - click outside and not on edit button');
          hasOpenDropdownRef.current = false;
          setIsEditing(false);
          setIsCreatingNew(false);
        } else {
          console.log('[TrendEditor] NOT closing - isInsideDropdown:', isInsideDropdown, 'editButton:', !!editButton);
        }
      };
      
      // Use mousedown instead of click, and add delay to prevent immediate closing
      // This ensures the dropdown stays open when user clicks to open it
      const timeoutId = setTimeout(() => {
        console.log('[TrendEditor] Adding mousedown listener after delay');
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
      
      return () => {
        console.log('[TrendEditor] Cleaning up click-outside handler');
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isEditing]);

    const handleSaveTrends = async () => {
      try {
        const trendIdsArray = Array.from(selectedTrendIds);
        const response = await fetch(`/api/trend-spotter/library/${video.id}/trends?userId=${encodeURIComponent(userId)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ trendIds: trendIdsArray })
        });
        if (response.ok) {
          console.log('[TrendEditor] handleSaveTrends - closing after successful save');
          hasOpenDropdownRef.current = false;
          setIsEditing(false);
          setIsCreatingNew(false);
          
          // Reload library to get updated trend assignments
          await onUpdate();
          // Also reload trends to update stats (video counts)
          if (loadTrends) {
            loadTrends(false); // Refresh trends list (no top videos needed)
          }
          toast.success('Trends updated!');
        } else {
          const result = await response.json();
          toast.error(result.error || 'Failed to update trends');
        }
      } catch (error) {
        console.error('Error updating trends:', error);
        toast.error('Failed to update trends');
      }
    };

    const handleToggleTrend = (trendId) => {
      const next = new Set(selectedTrendIds);
      if (next.has(trendId)) {
        next.delete(trendId);
      } else {
        next.add(trendId);
      }
      setSelectedTrendIds(next);
    };

    const handleCreateNewTrend = (e) => {
      // Prevent event bubbling
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      
      // Close the editing dropdown first
      console.log('[TrendEditor] handleCreateNewTrend - closing to create new trend');
      hasOpenDropdownRef.current = false;
      setIsEditing(false);
      setIsCreatingNew(false);
      
      // Immediately switch to Trends tab
      setView('trends');
      
      // Pre-select the current video and set up for new trend creation
      setVideoSelectionMode('new-trend');
      setSelectedVideosForModal(new Set([video.id]));
      
      // Open modal after a short delay to ensure view has changed and dropdown is closed
      setTimeout(() => {
        setShowVideoSelectionModal(true);
      }, 300);
    };

    if (isEditing && isCreatingNew) {
      return (
        <div className="relative" ref={dropdownRef}>
          <div className="flex gap-1">
            <input
              type="text"
              value={newTrendName}
              onChange={(e) => setNewTrendName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleCreateNewTrend();
                } else if (e.key === 'Escape') {
                  setIsCreatingNew(false);
                  setNewTrendName('');
                }
              }}
              placeholder="Enter trend name..."
              autoFocus
              className={`flex-1 px-2 py-1 ${isDark ? 'bg-zinc-800 border-cyan-500' : 'bg-white border-gray-300'} border rounded text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500 ${isDark ? 'text-white' : 'text-black'}`}
            />
            <button
              onClick={handleCreateNewTrend}
              className="px-2 py-1 bg-cyan-500 hover:bg-cyan-400 text-white rounded text-xs"
            >
              ✓
            </button>
            <button
              onClick={() => {
                setIsCreatingNew(false);
                setNewTrendName('');
              }}
              className="px-2 py-1 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 rounded text-xs"
            >
              ×
            </button>
          </div>
        </div>
      );
    }

    if (isEditing) {
      return (
        <div 
          className="relative" 
          ref={dropdownRef}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div 
            className={`absolute top-full left-0 z-[9999] mt-1 w-64 ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-300'} border rounded-lg shadow-xl max-h-80 overflow-y-auto`}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {isCreatingNew ? (
              <div className={`p-2 border-b ${isDark ? 'border-zinc-700' : 'border-gray-200'}`}>
                <div className="flex gap-1">
                  <input
                    type="text"
                    value={newTrendName}
                    onChange={(e) => setNewTrendName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleCreateNewTrend();
                      } else if (e.key === 'Escape') {
                        setIsCreatingNew(false);
                        setNewTrendName('');
                      }
                    }}
                    placeholder="Enter trend name..."
                    autoFocus
                    className={`flex-1 px-2 py-1 ${isDark ? 'bg-zinc-900 border-cyan-500' : 'bg-white border-gray-300'} border rounded text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500 ${isDark ? 'text-white' : 'text-black'}`}
                  />
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleCreateNewTrend(e);
                    }}
                    className="px-2 py-1 bg-cyan-500 hover:bg-cyan-400 text-white rounded text-xs"
                  >
                    ✓
                  </button>
                  <button
                    onClick={() => {
                      setIsCreatingNew(false);
                      setNewTrendName('');
                    }}
                    className="px-2 py-1 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 rounded text-xs"
                  >
                    ×
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className={`p-2 border-b ${isDark ? 'border-zinc-700' : 'border-gray-200'} flex items-center justify-between`}>
                  <span className={`text-xs ${isDark ? 'text-zinc-400' : 'text-black'}`}>Select trends (multiple)</span>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleCreateNewTrend(e);
                    }}
                    className="px-2 py-1 text-xs bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 rounded"
                  >
                    + New
                  </button>
                </div>
                <div className={`p-2 border-b ${isDark ? 'border-zinc-700' : 'border-gray-200'}`}>
                  <input
                    type="text"
                    placeholder="Search trends..."
                    value={trendLabelSearchQuery}
                    onChange={(e) => setTrendLabelSearchQuery(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    className={`w-full px-2 py-1.5 text-sm rounded border ${isDark ? 'bg-zinc-900 border-zinc-600 text-white placeholder-zinc-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} focus:outline-none focus:ring-1 focus:ring-cyan-500`}
                  />
                </div>
                <div className="p-2 max-h-60 overflow-y-auto">
                  {trends.filter(t => t.user_id === userId || user?.isAdmin).filter(t => {
                    const q = (trendLabelSearchQuery || '').trim().toLowerCase();
                    if (!q) return true;
                    return (t.trend_name || '').toLowerCase().includes(q);
                  }).length === 0 ? (
                    <div className={`text-xs ${isDark ? 'text-zinc-400' : 'text-black'} py-2`}>
                      {trendLabelSearchQuery.trim() ? 'No trends match your search' : 'No trends available'}
                    </div>
                  ) : (
                    trends.filter(t => t.user_id === userId || user?.isAdmin).filter(t => {
                      const q = (trendLabelSearchQuery || '').trim().toLowerCase();
                      if (!q) return true;
                      return (t.trend_name || '').toLowerCase().includes(q);
                    }).map(trend => (
                      <label
                        key={trend.id}
                        className={`flex items-center gap-2 px-2 py-1.5 ${isDark ? 'hover:bg-zinc-700/50' : 'hover:bg-gray-100'} rounded cursor-pointer`}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={selectedTrendIds.has(trend.id)}
                          onChange={() => handleToggleTrend(trend.id)}
                          onClick={(e) => e.stopPropagation()}
                          className={`w-4 h-4 text-cyan-500 ${isDark ? 'bg-zinc-900 border-zinc-600' : 'bg-white border-gray-300'} border rounded focus:ring-cyan-500`}
                        />
                        <span className={`text-sm flex-1 ${isDark ? 'text-white' : 'text-black'}`}>{trend.trend_name}</span>
                      </label>
                    ))
                  )}
                </div>
                <div className={`p-2 border-t ${isDark ? 'border-zinc-700' : 'border-gray-200'} flex gap-2`}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSaveTrends();
                    }}
                    className="flex-1 px-3 py-1.5 bg-cyan-500 hover:bg-cyan-400 text-white rounded text-xs font-medium"
                  >
                    Save
                  </button>
                  <button
                    onClick={(e) => {
                      console.log('[TrendEditor] Cancel button clicked');
                      e.stopPropagation();
                      hasOpenDropdownRef.current = false;
                      setIsEditing(false);
                      setSelectedTrendIds(new Set(assignedTrendIds)); // Reset to original
                    }}
                    className={`px-3 py-1.5 ${isDark ? 'bg-zinc-700 hover:bg-zinc-600 text-zinc-300' : 'bg-gray-100 hover:bg-gray-200 text-black'} rounded text-xs`}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      );
    }

    // Get assigned trends from video.trends array or fallback to single trend_id
    const assignedTrends = video.trends && Array.isArray(video.trends) && video.trends.length > 0
      ? video.trends
      : (video.trend_id ? trends.filter(t => t.id === video.trend_id) : []);

    return (
      <div className="flex flex-wrap gap-1 items-center">
        {assignedTrends.length > 0 ? (
          <>
            {assignedTrends.map(trend => (
              <span
                key={trend.id}
                className={`px-2 py-0.5 ${isDark ? 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300' : 'bg-gray-100 hover:bg-gray-200 text-black'} rounded text-xs`}
              >
                {trend.trend_name}
              </span>
            ))}
            <button
              onClick={(e) => {
                console.log('[TrendEditor] Edit button clicked');
                e.stopPropagation();
                e.preventDefault();
                console.log('[TrendEditor] Setting isEditing to true');
                setIsEditing(true);
              }}
              className={`px-1.5 py-0.5 rounded text-xs border border-dashed ${isDark ? 'border-zinc-600 hover:border-zinc-500 text-zinc-400 hover:text-zinc-300' : 'border-gray-300 hover:border-gray-400 text-black hover:text-gray-700'}`}
              title="Edit trends"
            >
              ✎
            </button>
          </>
        ) : (
          <button
            onClick={(e) => {
              console.log('[TrendEditor] Add trend button clicked');
              e.stopPropagation();
              e.preventDefault();
              console.log('[TrendEditor] Setting isEditing to true');
              setIsEditing(true);
            }}
            className={`px-2 py-1 rounded-lg text-xs border border-dashed ${isDark ? 'border-zinc-700 hover:border-zinc-500 text-zinc-500 hover:text-zinc-300' : 'border-gray-300 hover:border-gray-400 text-black hover:text-gray-700'}`}
          >
            + Add trend
          </button>
        )}
      </div>
    );
  };

  // Inline Comments Editor Component
  const InlineCommentsEditor = ({ video, onUpdate }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [comments, setComments] = useState(video.user_comments || '');
    const commentsEditorRef = useRef(null);

    useEffect(() => {
      if (!isEditing) {
        hasOpenDropdownRef.current = false;
        return;
      }
      
      hasOpenDropdownRef.current = true;
      
      const handleClickOutside = (event) => {
        if (!commentsEditorRef.current) {
          return;
        }
        const target = event.target;
        const isInsideEditor = commentsEditorRef.current.contains(target);
        const commentsButton = target.closest('button')?.textContent?.includes('Add comments') ||
                               target.closest('button')?.textContent?.includes('Edit comments');
        
        if (!isInsideEditor && !commentsButton) {
          hasOpenDropdownRef.current = false;
          setIsEditing(false);
        }
      };
      
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
        hasOpenDropdownRef.current = false;
      };
    }, [isEditing]);

    const handleSaveComments = async () => {
      const oldComments = video.user_comments || '';
      
      // Optimistic UI update
      setLibraryVideos(prevVideos => 
        prevVideos.map(v => v.id === video.id ? { ...v, user_comments: comments } : v)
      );
      
      try {
        const response = await fetch(`/api/trend-spotter/library/${video.id}/comments?userId=${encodeURIComponent(userId)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ comments: comments || null })
        });
        if (response.ok) {
          hasOpenDropdownRef.current = false;
          setIsEditing(false);
          // Reload to ensure consistency
          onUpdate();
        } else {
          // Revert on error
          setLibraryVideos(prevVideos => 
            prevVideos.map(v => v.id === video.id ? { ...v, user_comments: oldComments } : v)
          );
          toast.error('Failed to update comments');
        }
      } catch (error) {
        console.error('Error updating comments:', error);
        // Revert on error
        setLibraryVideos(prevVideos => 
          prevVideos.map(v => v.id === video.id ? { ...v, user_comments: oldComments } : v)
        );
        toast.error('Failed to update comments');
      }
    };

    if (isEditing) {
      return (
        <div 
          className="relative min-w-[150px]" 
          ref={commentsEditorRef}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                hasOpenDropdownRef.current = false;
                setIsEditing(false);
                setComments(video.user_comments || '');
              }
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                handleSaveComments();
              }
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            placeholder="Add comments..."
            autoFocus
            rows={3}
            className={`w-full px-2 py-1 ${isDark ? 'bg-zinc-800 border-cyan-500' : 'bg-white border-gray-300'} border rounded text-xs focus:outline-none resize-none ${isDark ? 'text-white' : 'text-black'}`}
          />
          <div className="flex gap-1 mt-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleSaveComments();
              }}
              className="px-2 py-0.5 bg-cyan-500 hover:bg-cyan-400 text-white rounded text-xs"
            >
              Save
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                hasOpenDropdownRef.current = false;
                setIsEditing(false);
                setComments(video.user_comments || '');
              }}
              className={`px-2 py-0.5 ${isDark ? 'bg-zinc-700 hover:bg-zinc-600 text-zinc-300' : 'bg-gray-100 hover:bg-gray-200 text-black'} rounded text-xs`}
            >
              Cancel
            </button>
          </div>
        </div>
      );
    }

    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          hasOpenDropdownRef.current = true;
          setIsEditing(true);
        }}
        className={`text-xs ${isDark ? 'text-zinc-500' : 'text-black'} hover:text-cyan-400 transition-colors text-left w-full`}
        title="Click to add/edit comments"
      >
        {video.user_comments ? (
          <span className={`line-clamp-2 ${isDark ? 'text-white' : 'text-black'}`}>{video.user_comments}</span>
        ) : (
          <span className={`${isDark ? 'text-zinc-600' : 'text-black'} italic`}>+ Add comments</span>
        )}
      </button>
    );
  };

  // Inline Tag Editor Component
  const InlineTagEditor = ({ video, onUpdate }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [tags, setTags] = useState(video.user_tags || []);
    const [inputValue, setInputValue] = useState('');
    const tagEditorRef = useRef(null);

    // Close tag editor when clicking outside - persist until user explicitly clicks outside
    useEffect(() => {
      if (!isEditing) {
        console.log('[TagEditor] Not editing, skipping click-outside handler');
        hasOpenDropdownRef.current = false;
        return;
      }
      
      console.log('[TagEditor] Setting up click-outside handler, isEditing:', isEditing);
      hasOpenDropdownRef.current = true;
      
      const handleClickOutside = (event) => {
        console.log('[TagEditor] Click detected:', {
          target: event.target,
          targetTag: event.target.tagName,
          targetClass: event.target.className,
          hasTagEditorRef: !!tagEditorRef.current
        });
        
        // Only close if click is truly outside the tag editor AND not on any button that opens it
        if (!tagEditorRef.current) {
          console.log('[TagEditor] No tagEditorRef, returning');
          return;
        }
        
        const target = event.target;
        const isInsideEditor = tagEditorRef.current.contains(target);
        
        console.log('[TagEditor] isInsideEditor:', isInsideEditor);
        
        // Check if clicking on the button that opens it
        const tagButton = target.closest('button')?.textContent?.includes('+ Add tags') ||
                         target.closest('button')?.textContent?.includes('Add tag');
        
        console.log('[TagEditor] tagButton found:', !!tagButton);
        
        // Only close if click is outside AND not on the tag button
        if (!isInsideEditor && !tagButton) {
          console.log('[TagEditor] Closing editor - click outside and not on tag button');
          hasOpenDropdownRef.current = false;
          setIsEditing(false);
        } else {
          console.log('[TagEditor] NOT closing - isInsideEditor:', isInsideEditor, 'tagButton:', !!tagButton);
        }
      };
      
      // Use mousedown instead of click, and add delay to prevent immediate closing
      // This ensures the editor stays open when user clicks to open it
      const timeoutId = setTimeout(() => {
        console.log('[TagEditor] Adding mousedown listener after delay');
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
      
      return () => {
        console.log('[TagEditor] Cleaning up click-outside handler');
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isEditing]);

    const handleAddTag = async (tag) => {
      const newTags = [...tags, tag.trim().toLowerCase()].filter((t, i, arr) => arr.indexOf(t) === i);
      setTags(newTags);
      setInputValue('');
      
      // Optimistic UI update
      setLibraryVideos(prevVideos => 
        prevVideos.map(v => v.id === video.id ? { ...v, user_tags: newTags } : v)
      );
      
      try {
        const response = await fetch(`/api/trend-spotter/library/${video.id}/tags?userId=${encodeURIComponent(userId)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tags: newTags })
        });
        if (response.ok) {
          // Reload to ensure consistency
          onUpdate();
        } else {
          // Revert on error
          setLibraryVideos(prevVideos => 
            prevVideos.map(v => v.id === video.id ? { ...v, user_tags: tags } : v)
          );
          toast.error('Failed to update tags');
        }
      } catch (error) {
        console.error('Error updating tags:', error);
        // Revert on error
        setLibraryVideos(prevVideos => 
          prevVideos.map(v => v.id === video.id ? { ...v, user_tags: tags } : v)
        );
        toast.error('Failed to update tags');
      }
    };

    const handleRemoveTag = async (tagToRemove) => {
      const newTags = tags.filter(t => t !== tagToRemove);
      const oldTags = tags;
      setTags(newTags);
      
      // Optimistic UI update
      setLibraryVideos(prevVideos => 
        prevVideos.map(v => v.id === video.id ? { ...v, user_tags: newTags } : v)
      );
      
      try {
        const response = await fetch(`/api/trend-spotter/library/${video.id}/tags?userId=${encodeURIComponent(userId)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tags: newTags })
        });
        if (response.ok) {
          // Reload to ensure consistency
          onUpdate();
        } else {
          // Revert on error
          setLibraryVideos(prevVideos => 
            prevVideos.map(v => v.id === video.id ? { ...v, user_tags: oldTags } : v)
          );
          toast.error('Failed to update tags');
        }
      } catch (error) {
        console.error('Error updating tags:', error);
        // Revert on error
        setLibraryVideos(prevVideos => 
          prevVideos.map(v => v.id === video.id ? { ...v, user_tags: oldTags } : v)
        );
        toast.error('Failed to update tags');
      }
    };

    if (isEditing) {
      console.log('[TagEditor] Rendering editing mode, tagEditorRef:', !!tagEditorRef.current);
      return (
        <div 
          className="relative min-w-[140px]" 
          ref={tagEditorRef}
          onClick={(e) => {
            console.log('[TagEditor] Container clicked, stopping propagation');
            e.stopPropagation();
          }}
          onMouseDown={(e) => {
            console.log('[TagEditor] Container mousedown, stopping propagation');
            e.stopPropagation();
          }}
        >
          <div className="flex flex-wrap gap-1 mb-2">
            {tags.map((tag, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-cyan-500/20 text-cyan-300 rounded text-xs group"
                onClick={(e) => e.stopPropagation()}
              >
                {tag}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveTag(tag);
                  }}
                  className="hover:text-red-400 transition-colors"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && inputValue.trim()) {
                handleAddTag(inputValue);
              }
              if (e.key === 'Escape') {
                console.log('[TagEditor] Escape key pressed - closing');
                hasOpenDropdownRef.current = false;
                setIsEditing(false);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            placeholder="Add tag..."
            autoFocus
            className={`w-full px-2 py-1 ${isDark ? 'bg-zinc-800 border-cyan-500' : 'bg-white border-gray-300'} border rounded text-xs focus:outline-none ${isDark ? 'text-white' : 'text-black'}`}
          />
        </div>
      );
    }

    return (
      <button
        onClick={(e) => {
          console.log('[TagEditor] Add tags button clicked');
          e.stopPropagation();
          e.preventDefault();
          console.log('[TagEditor] Setting isEditing to true');
          setIsEditing(true);
        }}
        className="flex flex-wrap gap-1 items-center min-w-[100px] text-left"
      >
        {tags.length > 0 ? (
          <>
            {tags.slice(0, 2).map((tag, i) => (
              <span key={i} className={`px-1.5 py-0.5 ${isDark ? 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700' : 'bg-gray-100 text-black hover:bg-gray-200'} rounded text-xs transition-colors`}>
                {tag}
              </span>
            ))}
            {tags.length > 2 && (
              <span className={`text-xs ${isDark ? 'text-zinc-600' : 'text-black'}`}>+{tags.length - 2}</span>
            )}
          </>
        ) : (
          <span className={`text-xs ${isDark ? 'text-zinc-600 hover:text-zinc-400 border-zinc-700 hover:border-zinc-500' : 'text-black hover:text-gray-700 border-gray-300 hover:border-gray-400'} border border-dashed px-2 py-0.5 rounded transition-colors`}>
            + Add tags
          </span>
        )}
      </button>
    );
  };

  // Handle feedback submission
  const handleSubmitFeedback = async () => {
    if (feedbackRating === 0) {
      toast.error('Please select an overall rating');
      return;
    }

    setSubmittingFeedback(true);
    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          overallRating: feedbackRating,
          favoriteFeatures: favoriteFeatures,
          topAsks: topAsks.trim() || null
        })
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Thank you for your feedback! 🙏');
        setShowFeedbackModal(false);
        setFeedbackRating(0);
        setFavoriteFeatures([]);
        setTopAsks('');
      } else {
        toast.error(result.error || 'Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const toggleFavoriteFeature = (feature) => {
    setFavoriteFeatures(prev => 
      prev.includes(feature)
        ? prev.filter(f => f !== feature)
        : [...prev, feature]
    );
  };

  // Handle view change from user interaction
  const handleViewChange = useCallback((newView) => {
    if (newView === view) return; // Already on this view, no need to update
    
    isUserInteractionRef.current = true; // Mark as user interaction
    setView(newView);
    // URL will be updated by the useEffect hook
  }, [view]);

  // Stable callback for tour view changes so GuidedTour effect doesn't re-run every parent render
  const handleTourViewChange = useCallback((newView) => {
    isTourViewChangeRef.current = true;
    handleViewChange(newView);
  }, [handleViewChange]);

  // Navigation component
  const NavTabs = () => (
    <div className="flex items-center gap-2">
      <div className={`flex items-center gap-1 ${isDark ? 'bg-zinc-800' : 'bg-white border border-gray-200'} rounded-lg p-1`}>
        <button
          data-tour="library-tab"
          onClick={(e) => {
            e.preventDefault();
            handleViewChange('library');
          }}
          className={`px-2 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
            view === 'library' 
              ? isDark ? 'bg-zinc-700 text-white' : 'bg-gray-100 text-gray-900'
              : isDark ? 'text-zinc-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <span className="hidden sm:inline">📋 </span>Swipe File
        </button>
        <button
          data-tour="trends-tab"
          onClick={(e) => {
            e.preventDefault();
            handleViewChange('trends');
          }}
          className={`px-2 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
            view === 'trends' 
              ? isDark ? 'bg-zinc-700 text-white' : 'bg-gray-100 text-gray-900'
              : isDark ? 'text-zinc-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <span className="hidden sm:inline">📈 </span>Trends
        </button>
        <button
          data-tour="board-tab"
          onClick={(e) => {
            e.preventDefault();
            handleViewChange('board');
          }}
          className={`px-2 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
            view === 'board' 
              ? isDark ? 'bg-zinc-700 text-white' : 'bg-gray-100 text-gray-900'
              : isDark ? 'text-zinc-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <span className="hidden sm:inline">📝 </span>Board
        </button>
      </div>
      <a
        href="/slack-install"
        rel="noopener noreferrer"
        className={`inline-flex items-center justify-center p-2 rounded-lg transition-colors hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-cyan-500 flex-shrink-0 ${isDark ? 'bg-zinc-800 hover:bg-zinc-700' : 'bg-gray-200 hover:bg-gray-300'}`}
        title="Install Trend Bot in your Slack channel"
      >
        <img
          src="/slack-logo.png"
          alt="Add to Slack"
          className="h-4 w-4 object-contain"
        />
      </a>
      <button
        onClick={() => setShowFeedbackModal(true)}
        className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${isDark ? 'bg-zinc-800 hover:bg-zinc-700 text-zinc-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
        title="Share your feedback"
      >
        <MessageCircle className="h-4 w-4" />
        <span className="hidden sm:inline">Feedback</span>
      </button>
    </div>
  );

  return (
    <div className={`min-h-screen ${isDark ? 'bg-zinc-950 text-white' : 'bg-gray-50 text-gray-900'} w-full m-0 p-0`}>
      {/* Header - Always visible */}
      <div className={`border-b ${isDark ? 'border-zinc-800 bg-zinc-900/50' : 'border-gray-200 bg-white'} sticky top-0 z-30 w-full`}>
        <div className="w-full px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <div className="h-12 w-12 sm:h-14 sm:w-14 flex-shrink-0 rounded-lg overflow-hidden bg-white/10">
                <img src="/showstop-logo.png" alt="ShowStop" className="h-full w-full object-cover" />
              </div>
              <div className="flex flex-col min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs sm:text-sm font-semibold px-2 py-0.5 rounded ${(userLimits.hasPaid || userLimits.hasAccess || userLimits.first7DaysUnlimited) ? (isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700') : (isDark ? 'bg-zinc-600 text-zinc-300' : 'bg-gray-200 text-gray-700')}`}>
                    {(userLimits.hasPaid || userLimits.hasAccess || userLimits.first7DaysUnlimited) ? 'Unlimited Plan' : 'FREE Plan'}
                  </span>
                  <CountdownDisplay
                    unlimitedPeriodEndAt={userLimits.unlimitedPeriodEndAt}
                    hasPaid={userLimits.hasPaid}
                    hasAccess={userLimits.hasAccess}
                    first7DaysUnlimited={userLimits.first7DaysUnlimited}
                    isDark={isDark}
                  />
                </div>
                <h1 className={`text-lg sm:text-2xl font-bold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>Trend Spotter</h1>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
              {/* Quick Start Guide - changes based on active view */}
              <div className={`flex items-center gap-2 text-xs sm:text-sm ${isDark ? 'text-zinc-400' : 'text-gray-600'} min-w-0 flex-1`}>
                <span className={`${isDark ? 'text-cyan-400' : 'text-cyan-600'} font-semibold whitespace-nowrap`}>Quick Start:</span>
                {view === 'library' && (
                  <span className="truncate flex items-center gap-1.5">Click <span className="inline-flex items-center justify-center w-5 h-5 bg-emerald-500 rounded text-white text-[10px] font-bold ring-2 ring-emerald-400/60">+</span> or <span className="inline-flex items-center justify-center w-5 h-5 bg-cyan-500 rounded text-white ring-2 ring-cyan-400/60"><BookOpen className="h-3 w-3" /></span> to add bookmarked IG/TT videos → add tags/comments → add/create trend</span>
                )}
                {view === 'trends' && (
                  <span className="truncate">Browse trends → <span className={`${isDark ? 'text-cyan-400' : 'text-cyan-600'} font-medium`}>View details</span> → <span className={`${isDark ? 'text-cyan-400' : 'text-cyan-600'} font-medium`}>Generate brief</span> for your brand</span>
                )}
                {view === 'trend-detail' && (
                  <span className="truncate">Browse trends → <span className={`${isDark ? 'text-cyan-400' : 'text-cyan-600'} font-medium`}>View details</span> → <span className={`${isDark ? 'text-cyan-400' : 'text-cyan-600'} font-medium`}>Generate brief</span> for your brand</span>
                )}
                {view === 'board' && (
                  <span className="truncate"><span className={`${isDark ? 'text-cyan-400' : 'text-cyan-600'} font-medium`}>New Brief</span> → Edit brief (content or phase) → <span className={`${isDark ? 'text-cyan-400' : 'text-cyan-600'} font-medium`}>Share</span> with team</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <NavTabs />
              
              {/* Theme Toggle */}
              <ThemeToggle />
              
              {/* Admin: View as account — upload/manage trends, videos, briefs as selected account */}
              {isAdmin && (
                <div className="relative flex items-center gap-1">
                  {actAsEmail === ADMIN_VIEW_ALL && (
                    <span className={`hidden sm:inline text-xs ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} title="Seeing all accounts">
                      All
                    </span>
                  )}
                  {actAsEmail && actAsEmail !== 'info@showstop.ai' && actAsEmail !== ADMIN_VIEW_ALL && (
                    <span className={`hidden sm:inline text-xs ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} title={`Managing as ${actAsEmail}`}>
                      as {actAsEmail.split('@')[0]}
                    </span>
                  )}
                  <button
                    onClick={() => { setShowViewAsMenu(!showViewAsMenu); setShowUserMenu(false); }}
                    className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 ${isDark ? 'bg-zinc-800 hover:bg-zinc-700' : 'bg-gray-200 hover:bg-gray-300'} rounded-lg transition-colors`}
                    title="View as account — upload and manage trends, videos, briefs as this account; All = see everyone's data"
                  >
                    <UserCircle className="h-4 w-4" />
                    <span className="hidden sm:inline text-sm font-medium max-w-[120px] truncate">
                      {actAsEmail === ADMIN_VIEW_ALL ? 'All' : actAsEmail ? (actAsEmail === 'info@showstop.ai' ? 'Me (info@)' : actAsEmail.split('@')[0]) : 'Me (info@)'}
                    </span>
                  </button>
                  {showViewAsMenu && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowViewAsMenu(false)} />
                      <div className={`absolute right-0 mt-2 w-52 ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200'} border rounded-lg shadow-xl z-50 overflow-hidden`}>
                        <div className={`px-3 py-2 border-b ${isDark ? 'border-zinc-700' : 'border-gray-200'} text-xs font-medium ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>View as</div>
                        <div className="py-1">
                          <button
                            onClick={() => { setActAsEmail(null); setShowViewAsMenu(false); }}
                            className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 transition-colors ${!actAsEmail ? (isDark ? 'bg-zinc-700 text-cyan-400' : 'bg-cyan-50 text-cyan-700') : (isDark ? 'text-zinc-300 hover:bg-zinc-700' : 'text-gray-700 hover:bg-gray-100')}`}
                          >
                            {!actAsEmail && <CheckCircle className="h-4 w-4 flex-shrink-0" />}
                            <span className="truncate">Me (info@)</span>
                          </button>
                          <button
                            onClick={() => { setActAsEmail(ADMIN_VIEW_ALL); setShowViewAsMenu(false); }}
                            className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 transition-colors ${actAsEmail === ADMIN_VIEW_ALL ? (isDark ? 'bg-zinc-700 text-cyan-400' : 'bg-cyan-50 text-cyan-700') : (isDark ? 'text-zinc-300 hover:bg-zinc-700' : 'text-gray-700 hover:bg-gray-100')}`}
                          >
                            {actAsEmail === ADMIN_VIEW_ALL && <CheckCircle className="h-4 w-4 flex-shrink-0" />}
                            <span className="truncate">All (pending trends, briefs, videos)</span>
                          </button>
                          {ADMIN_VIEW_AS_EMAILS.filter(e => e !== 'info@showstop.ai').map((email) => (
                            <button
                              key={email}
                              onClick={() => { setActAsEmail(email); setShowViewAsMenu(false); }}
                              className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 transition-colors ${actAsEmail === email ? (isDark ? 'bg-zinc-700 text-cyan-400' : 'bg-cyan-50 text-cyan-700') : (isDark ? 'text-zinc-300 hover:bg-zinc-700' : 'text-gray-700 hover:bg-gray-100')}`}
                            >
                              {actAsEmail === email && <CheckCircle className="h-4 w-4 flex-shrink-0" />}
                              <span className="truncate">{email.split('@')[0]}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
              
              {/* Guide Button — hide when onboarding complete (stage 6) */}
              {onboardingUnlockedStage < 6 && (
                <button
                  data-tour="guide-button"
                  onClick={startTour}
                  className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 ${isDark ? 'bg-zinc-800 hover:bg-zinc-700' : 'bg-gray-200 hover:bg-gray-300'} rounded-lg transition-colors`}
                  title="User Guide"
                >
                  <Navigation className="h-4 w-4" />
                  <span className="hidden sm:inline text-sm font-medium">Guide</span>
                </button>
              )}
              
              {/* User Menu */}
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 ${isDark ? 'bg-zinc-800 hover:bg-zinc-700' : 'bg-white border border-gray-200 hover:bg-gray-50'} rounded-lg transition-colors`}
                  title="User Menu"
                >
                  <User className="h-4 w-4" />
                  <span className={`hidden sm:inline text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{user?.email?.split('@')[0] || 'User'}</span>
                </button>
                
                {showUserMenu && (
                  <>
                    {/* Backdrop to close menu */}
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setShowUserMenu(false)}
                    />
                    {/* Dropdown Menu */}
                    <div className={`absolute right-0 mt-2 w-56 ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200'} border rounded-lg shadow-xl z-50 overflow-hidden`}>
                      <div className={`px-4 py-3 border-b ${isDark ? 'border-zinc-700' : 'border-gray-200'}`}>
                        <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{user?.name || 'User'}</p>
                        <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-gray-500'} mt-1`}>{user?.email}</p>
                      </div>
                      <div className="py-1">
                        <button
                          data-tour="brand-settings-button"
                          onClick={() => {
                            setBrandSettingsModalSource(null);
                            setShowBrandSettingsModal(true);
                            setShowUserMenu(false);
                          }}
                          className={`w-full px-4 py-2 text-left text-sm ${isDark ? 'text-zinc-300 hover:bg-zinc-700' : 'text-gray-700 hover:bg-gray-100'} flex items-center gap-2 transition-colors`}
                        >
                          <Settings className="h-4 w-4" />
                          Brand Settings
                        </button>
                        <button
                          data-tour="subscription-button"
                          onClick={() => {
                            setShowUserMenu(false);
                            setShowSubscriptionModal(true);
                          }}
                          className={`w-full px-4 py-2 text-left text-sm ${isDark ? 'text-zinc-300 hover:bg-zinc-700' : 'text-gray-700 hover:bg-gray-100'} flex items-center gap-2 transition-colors`}
                        >
                          <CreditCard className="h-4 w-4" />
                          Subscription
                        </button>
                        {user?.email?.toLowerCase() === 'info@showstop.ai' && (
                          <button
                            onClick={() => {
                              setShowUserMenu(false);
                              navigate('/admin');
                            }}
                            className={`w-full px-4 py-2 text-left text-sm ${isDark ? 'text-indigo-400 hover:bg-zinc-700' : 'text-indigo-600 hover:bg-gray-100'} flex items-center gap-2 transition-colors`}
                          >
                            <Shield className="h-4 w-4" />
                            Admin Dashboard
                          </button>
                        )}
                        <button
                          onClick={() => {
                            logout();
                            setShowUserMenu(false);
                            navigate('/login');
                          }}
                          className={`w-full px-4 py-2 text-left text-sm ${isDark ? 'text-red-400 hover:bg-zinc-700' : 'text-red-600 hover:bg-gray-100'} flex items-center gap-2 transition-colors`}
                        >
                          <LogOut className="h-4 w-4" />
                          Logout
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content based on view */}
      {view === 'library' && (
        <div className="w-full px-6 py-6">
          {/* Job Status Banner — own 1s tick so parent/modals don't re-render */}
          <ActiveJobsBanner activeJobs={activeJobs} isDark={isDark} />
          {/* Swipe File Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
            <div>
              <h2 className={`text-2xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>My Saved Videos</h2>
              <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>
                {libraryVideoCountDisplay} bookmark{libraryVideoCountDisplay === 1 ? '' : 's'} synced
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-end sm:justify-end gap-2 sm:gap-3 w-full sm:w-auto">
              <div className="relative">
                <button
                  data-tour="sync-bookmarks-button"
                  onClick={() => setShowSyncModal(true)}
                  className="px-3 sm:px-4 py-2 bg-cyan-500 hover:bg-cyan-400 rounded-lg flex items-center gap-2 text-sm font-medium text-white ring-2 ring-cyan-400/60 ring-offset-2 ring-offset-transparent shadow-lg shadow-cyan-500/25 animate-[bounce_2s_ease-in-out_infinite]"
                >
                  <BookOpen className="h-4 w-4" />
                  <span className="hidden sm:inline">Sync Bookmarks</span>
                  <span className="sm:hidden">Sync</span>
                </button>
              </div>
              <div className="relative">
                <button
                  data-tour="add-videos-button"
                  onClick={() => setShowManualAddModal(true)}
                  className="px-3 sm:px-4 py-2 bg-emerald-500 hover:bg-emerald-400 rounded-lg flex items-center gap-2 text-sm font-medium text-white ring-2 ring-emerald-400/60 ring-offset-2 ring-offset-transparent shadow-lg shadow-emerald-500/25 animate-[bounce_2s_ease-in-out_infinite] [animation-delay:0.35s]"
                >
                  <Plus className="h-4 w-4" />
                  <span className="hidden sm:inline">Add Videos</span>
                  <span className="sm:hidden">Add</span>
                </button>
              </div>
            </div>
          </div>


          {/* Filters */}
          <div className="mb-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
            <div className="flex flex-wrap gap-2 sm:gap-3 flex-1">
              <select
                value={filters.status}
                onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
                className={`flex-1 sm:flex-none ${isDark ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-cyan-500 min-w-[120px]`}
              >
                <option value="all">All Status</option>
                <option value="new">🔵 New ({newCount})</option>
              </select>
              <select
                value={filters.platform}
                onChange={(e) => setFilters(f => ({ ...f, platform: e.target.value }))}
                className={`flex-1 sm:flex-none ${isDark ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-cyan-500 min-w-[140px]`}
              >
                <option value="all">All Platforms</option>
                <option value="tiktok">TikTok</option>
                <option value="instagram">Instagram</option>
              </select>
              <select
                value={filters.trend}
                onChange={(e) => setFilters(f => ({ ...f, trend: e.target.value }))}
                className={`flex-1 sm:flex-none ${isDark ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-cyan-500 min-w-[150px]`}
              >
                <option value="all">All Trends</option>
                <option value="none">No Trend</option>
                {trends.filter(t => {
                  // Only show trends owned by the user (or admin can see all)
                  return t.user_id === userId || user?.isAdmin;
                }).map(t => (
                  <option key={t.id} value={t.id}>
                    {t.trend_name} ({(() => {
                      const count = libraryVideos.filter(v => {
                        const hasTrend = (v.trends && Array.isArray(v.trends) && v.trends.some(tr => tr.id === t.id)) ||
                                        (v.trend_id === t.id);
                        return hasTrend;
                      }).length;
                      return count;
                    })()})
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={filters.creator}
                onChange={(e) => setFilters(f => ({ ...f, creator: e.target.value }))}
                placeholder="Search creator..."
                className={`flex-1 sm:flex-none ${isDark ? 'bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-cyan-500 sm:w-32 min-w-[120px]`}
              />
              <input
                type="text"
                value={filters.hashtag}
                onChange={(e) => setFilters(f => ({ ...f, hashtag: e.target.value }))}
                placeholder="#hashtag..."
                className={`flex-1 sm:flex-none ${isDark ? 'bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-cyan-500 sm:w-28 min-w-[100px]`}
              />
              <div className="relative flex-1 sm:flex-none sm:w-40 min-w-[140px]">
                <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${isDark ? 'text-zinc-500' : 'text-gray-400'}`} />
                <input
                  type="text"
                  value={tagSearch}
                  onChange={(e) => setTagSearch(e.target.value)}
                  placeholder="Search tags..."
                  className={`w-full pl-10 pr-4 py-1.5 ${isDark ? 'bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} border rounded-lg text-sm focus:outline-none focus:border-cyan-500`}
                />
                {tagSearch && (
                  <button
                    onClick={() => {
                      setTagSearch('');
                    }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end sm:ml-auto gap-3">
              <div className={`${isDark ? 'text-zinc-500' : 'text-gray-600'} text-sm whitespace-nowrap`}>
                Showing {libraryListToShow.length} video{libraryListToShow.length !== 1 ? 's' : ''}
                {libraryVideos.length === 0 && sampleVideos.length > 0 && (
                  <span> ({sampleVideos.length} sample, {filteredLibraryVideos.length} yours)</span>
                )}
              </div>
            </div>
          </div>

          {/* Video Table */}
          <div className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'} rounded-xl border overflow-hidden`}>
            <div 
              ref={libraryScrollRef}
              className="max-h-[calc(100vh-300px)] overflow-y-auto" 
              style={{ overflowY: 'auto' }}
            >
              <table className="w-full">
                <thead>
                  <tr className={`border-b ${isDark ? 'border-zinc-800' : 'border-gray-200'} text-left`}>
                    <th className={`p-2 ${isDark ? 'text-zinc-500' : 'text-black'} text-xs font-medium uppercase tracking-wider w-14`}>Video</th>
                    <th className={`p-2 ${isDark ? 'text-zinc-500' : 'text-black'} text-xs font-medium uppercase tracking-wider w-24`}>Creator</th>
                    <th 
                      className={`p-2 ${isDark ? 'text-zinc-500 hover:text-zinc-300' : 'text-black hover:text-gray-700'} text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors select-none w-20`}
                      onClick={() => handleSort('posted_date')}
                    >
                      Posted {renderSortIcon('posted_date')}
                    </th>
                    <th 
                      className={`p-2 ${isDark ? 'text-zinc-500 hover:text-zinc-300' : 'text-black hover:text-gray-700'} text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors select-none w-16`}
                      onClick={() => handleSort('views')}
                    >
                      Views {renderSortIcon('views')}
                    </th>
                    <th 
                      className={`p-2 ${isDark ? 'text-zinc-500 hover:text-zinc-300' : 'text-black hover:text-gray-700'} text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors select-none w-16`}
                      onClick={() => handleSort('likes')}
                    >
                      Likes {renderSortIcon('likes')}
                    </th>
                    <th 
                      className={`p-2 ${isDark ? 'text-zinc-500 hover:text-zinc-300' : 'text-black hover:text-gray-700'} text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors select-none w-20`}
                      onClick={() => handleSort('comments')}
                    >
                      Cmts {renderSortIcon('comments')}
                    </th>
                    <th 
                      className={`p-2 ${isDark ? 'text-zinc-500 hover:text-zinc-300' : 'text-black hover:text-gray-700'} text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors select-none w-16`}
                      onClick={() => handleSort('shares')}
                    >
                      Shares {renderSortIcon('shares')}
                    </th>
                    <th className={`p-2 ${isDark ? 'text-zinc-500' : 'text-black'} text-xs font-medium uppercase tracking-wider w-24 max-w-[8rem]`}>Caption</th>
                    <th className={`p-2 ${isDark ? 'text-zinc-500' : 'text-black'} text-xs font-medium uppercase tracking-wider w-24`}>Hashtags</th>
                      <th className="p-2 text-cyan-400 text-xs font-medium uppercase tracking-wider w-48 min-w-[10rem] bg-cyan-500/10 border-l-2 border-cyan-500/30">✎ Trend</th>
                      <th className="p-2 text-cyan-400 text-xs font-medium uppercase tracking-wider w-24 bg-cyan-500/10 border-l-2 border-cyan-500/30">✎ Tags</th>
                      <th className="p-2 text-cyan-400 text-xs font-medium uppercase tracking-wider w-28 bg-cyan-500/10 border-l-2 border-cyan-500/30">✎ Comments</th>
                      <th className="p-2 text-cyan-500 text-xs font-medium uppercase tracking-wider w-28 bg-cyan-500/15 border-l-2 border-cyan-500/40">Generate brief</th>
                      <th className={`p-2 ${isDark ? 'text-zinc-500' : 'text-black'} text-xs font-medium uppercase tracking-wider w-20`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {libraryListToShow.map((video) => (
                    <tr
                      key={video.is_sample ? `sample-${video.id}` : video.id}
                      className={`border-b transition-colors ${
                        video.is_sample
                          ? isDark ? 'border-amber-900/50 bg-amber-950/40 hover:bg-amber-950/50' : 'border-amber-200 bg-amber-50/70 hover:bg-amber-50'
                          : isDark ? 'border-zinc-800/50 hover:bg-zinc-800/30' : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <td className="p-3">
                        <button
                          onClick={() => {
                            // Get video URL from apify_metadata or video_url column
                            let videoUrl = video.video_url;
                            if (!videoUrl && video.apify_metadata) {
                              const meta = typeof video.apify_metadata === 'string' 
                                ? JSON.parse(video.apify_metadata) 
                                : video.apify_metadata;
                              videoUrl = meta.videoUrl || meta.video_url || meta.mediaUrls?.[0] || meta.webVideoUrl;
                            }
                            if (videoUrl) {
                              setVideoPreview({
                                videoUrl,
                                creator: getCreatorUsername(video),
                                platform: video.platform,
                                views: getApifyValue(video, 'views'),
                                likes: getApifyValue(video, 'likes'),
                                video: video // Include full video object for metadata
                              });
                            }
                          }}
                          className={`relative w-14 h-20 rounded-lg overflow-hidden ${isDark ? 'bg-zinc-800' : 'bg-gray-100'} hover:ring-2 ring-cyan-500/50 transition-all cursor-pointer`}
                        >
                          {(() => {
                            // Get video URL from apify_metadata or video_url column
                            let videoUrl = video.video_url;
                            if (!videoUrl && video.apify_metadata) {
                              const meta = typeof video.apify_metadata === 'string' 
                                ? JSON.parse(video.apify_metadata) 
                                : video.apify_metadata;
                              videoUrl = meta.videoUrl || meta.video_url || meta.mediaUrls?.[0] || meta.webVideoUrl;
                            }
                            
                            if (videoUrl) {
                              return (
                                <>
                                  <video
                                    src={videoUrl}
                                    className="w-full h-full object-cover"
                                    muted
                                    loop
                                    playsInline
                                    onMouseEnter={(e) => {
                                      const video = e.target;
                                      const playPromise = video.play();
                                      if (playPromise !== undefined) {
                                        playPromise.catch((error) => {
                                          // Play was interrupted or failed, ignore error
                                          // This is expected when pause() is called quickly
                                        });
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const video = e.target;
                                      // Pause the video
                                      const pausePromise = video.pause();
                                      if (pausePromise !== undefined) {
                                        pausePromise.catch(() => {
                                          // Pause error, ignore
                                        });
                                      }
                                      // Reset to beginning
                                      video.currentTime = 0;
                                    }}
                                  />
                                  <div className="absolute top-1 left-1 z-10">
                                    {video.platform === 'tiktok' ? (
                                      <div className="w-4 h-4 bg-black rounded flex items-center justify-center text-[8px] text-white">TT</div>
                                    ) : (
                                      <div className="w-4 h-4 bg-gradient-to-br from-purple-500 to-pink-500 rounded flex items-center justify-center text-[8px] text-white">IG</div>
                                    )}
                                  </div>
                                  {new Date(video.created_at) > new Date(Date.now() - 24 * 60 * 60 * 1000) && 
                                   !((video.trends && Array.isArray(video.trends) && video.trends.length > 0) || video.trend_id) && (
                                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-cyan-400 rounded-full border-2 border-zinc-950 animate-pulse z-10" />
                                  )}
                                </>
                              );
                            }
                            
                            // Fallback placeholder
                            return (
                              <>
                                <div className="w-full h-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                                  <Video className="h-6 w-6 text-white/30" />
                                </div>
                                <div className="absolute top-1 left-1">
                                  {video.platform === 'tiktok' ? (
                                    <div className="w-4 h-4 bg-black rounded flex items-center justify-center text-[8px] text-white">TT</div>
                                  ) : (
                                    <div className="w-4 h-4 bg-gradient-to-br from-purple-500 to-pink-500 rounded flex items-center justify-center text-[8px] text-white">IG</div>
                                  )}
                                </div>
                                {new Date(video.created_at) > new Date(Date.now() - 24 * 60 * 60 * 1000) && 
                                 !((video.trends && Array.isArray(video.trends) && video.trends.length > 0) || video.trend_id) && (
                                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-cyan-400 rounded-full border-2 border-zinc-950 animate-pulse" />
                                )}
                              </>
                            );
                          })()}
                        </button>
                      </td>
                      <td className="p-2">
                        <div className="flex items-center gap-1">
                          <div className="text-sm">
                            {video.platform === 'tiktok' ? '🎵' : '📸'}
                          </div>
                          <div className="min-w-0">
                            <div className={`text-xs font-medium truncate ${isDark ? 'text-white' : 'text-black'}`}>@{getCreatorUsername(video)}</div>
                                {new Date(video.created_at) > new Date(Date.now() - 24 * 60 * 60 * 1000) && 
                                 !((video.trends && Array.isArray(video.trends) && video.trends.length > 0) || video.trend_id) && (
                              <span className="text-[9px] text-cyan-400 font-medium">NEW</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className={`p-2 text-xs ${isDark ? 'text-zinc-400' : 'text-black'}`}>{formatDate(getPostedDate(video))}</td>
                      <td className={`p-2 text-xs font-medium ${isDark ? 'text-white' : 'text-black'}`}>{formatNumber(getApifyValue(video, 'views'))}</td>
                      <td className={`p-2 text-xs ${isDark ? 'text-zinc-400' : 'text-black'}`}>{formatNumber(getApifyValue(video, 'likes'))}</td>
                      <td className={`p-2 text-xs ${isDark ? 'text-zinc-400' : 'text-black'}`}>{formatNumber(getApifyValue(video, 'comments'))}</td>
                      <td className={`p-2 text-xs ${isDark ? 'text-zinc-400' : 'text-black'}`}>{formatNumber(getApifyValue(video, 'shares'))}</td>
                      <td className="p-2 w-24 max-w-[8rem]">
                        <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-black'} line-clamp-5 break-words`}>
                          {video.apify_caption || 'No caption'}
                        </p>
                      </td>
                      <td className="p-2">
                        <div className="flex flex-wrap gap-0.5">
                          {(() => {
                            const hashtags = parseHashtags(video);
                            return hashtags.length > 0 ? (
                              hashtags.slice(0, 2).map((tag, i) => (
                                <span key={i} className="text-[10px] text-cyan-400">#{tag}</span>
                              ))
                            ) : (
                              <span className={`text-[10px] ${isDark ? 'text-zinc-600' : 'text-gray-500'}`}>-</span>
                            );
                          })()}
                        </div>
                      </td>
                      <td 
                        className="p-2 relative w-48 min-w-[10rem] bg-cyan-500/5 border-l-2 border-cyan-500/30"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        {video.is_sample ? (
                          <span className={`text-xs ${isDark ? 'text-amber-400/80' : 'text-amber-700'}`}>{video.sample_trend_name || 'Sample'}</span>
                        ) : (
                          <InlineTrendEditor 
                            video={video} 
                            onUpdate={loadLibrary} 
                            trends={trends} 
                            loadTrends={loadTrends}
                            setView={setView}
                          />
                        )}
                      </td>
                      <td 
                        className="p-2 relative bg-cyan-500/5 border-l-2 border-cyan-500/30"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        {video.is_sample ? (
                          <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-gray-400'}`}>—</span>
                        ) : (
                          <InlineTagEditor video={video} onUpdate={loadLibrary} />
                        )}
                      </td>
                      <td 
                        className="p-2 relative bg-cyan-500/5 border-l-2 border-cyan-500/30"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        {video.is_sample ? (
                          <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-gray-400'}`}>—</span>
                        ) : (
                          <InlineCommentsEditor video={video} onUpdate={loadLibrary} />
                        )}
                      </td>
                      <td className="p-2 bg-cyan-500/10 border-l-2 border-cyan-500/30" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                        {video.is_sample ? (
                          <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-gray-400'}`}>—</span>
                        ) : (
                          <button
                            onClick={() => {
                              const trendId = getVideoTrendId(video);
                              if (trendId) runGenerateIdeas(trendId, { showLoadingToast: true });
                              else runGenerateIdeasFromVideo(video);
                            }}
                            disabled={userLimits.exceeded && (userLimits.reason === 'idea_limit' || userLimits.ideaLimitExceeded)}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                              !(userLimits.exceeded && (userLimits.reason === 'idea_limit' || userLimits.ideaLimitExceeded))
                                ? (isDark ? 'text-cyan-300 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/40 shadow-[0_0_12px_rgba(6,182,212,0.25)] animate-pulse' : 'text-cyan-700 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/40 shadow-[0_0_12px_rgba(6,182,212,0.2)] animate-pulse')
                                : (isDark ? 'text-zinc-500 cursor-not-allowed bg-zinc-800/50' : 'text-gray-400 cursor-not-allowed bg-gray-100')
                            }`}
                            title={getVideoTrendId(video) ? 'Generate brief from this video\'s trend' : 'Generate brief from this video (Single Video > Brief)'}
                            data-tour={!video.is_sample ? 'single-video-create-idea' : undefined}
                          >
                            <Sparkles className="h-3.5 w-3.5 flex-shrink-0" />
                            <span>Generate brief</span>
                          </button>
                        )}
                      </td>
                      <td className="p-2">
                        {video.is_sample ? (
                          <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-gray-400'}`}>—</span>
                        ) : (
                          <button
                            onClick={async () => {
                              if (window.confirm('Are you sure you want to delete this video?')) {
                                try {
                                  const response = await fetch(`/api/trend-spotter/library/${video.id}?userId=${encodeURIComponent(userId)}`, {
                                    method: 'DELETE'
                                  });
                                  const result = await response.json();
                                  if (result.success) {
                                    toast.success('Video deleted');
                                    loadLibrary(true); // Force reload after deleting video
                                    loadTrends(false); // Refresh trends list (no top videos needed)
                                  } else {
                                    toast.error(result.error || 'Failed to delete video');
                                  }
                                } catch (error) {
                                  console.error('Error deleting video:', error);
                                  toast.error('Failed to delete video');
                                }
                              }
                            }}
                            className={`p-1.5 ${isDark ? 'text-zinc-400' : 'text-gray-600'} hover:text-red-400 hover:bg-red-500/10 rounded transition-colors`}
                            title="Delete video"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Loading indicator for lazy loading */}
              {libraryPagination.loadingMore && (
                <div className="flex justify-center items-center py-8">
                  <Loader2 className="h-6 w-6 text-cyan-400 animate-spin" />
                  <span className={`ml-2 ${isDark ? 'text-zinc-400' : 'text-black'} text-sm`}>Loading more videos...</span>
                </div>
              )}
              {!libraryPagination.loadingMore && libraryPagination.hasMore && (
                <div className={`text-center py-4 ${isDark ? 'text-zinc-500' : 'text-black'} text-sm`}>
                  Scroll down to load more videos
                </div>
              )}
            </div>
          </div>

          {libraryListToShow.length === 0 && (
            <div className={`text-center py-12 ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>
              <BookOpen className="h-12 w-12 mb-4 opacity-50" />
              <p>No videos yet</p>
              <p className="text-sm mt-2">Use the <strong>Sync Bookmarks</strong> or <strong>+ Add Videos</strong> buttons above to add videos</p>
            </div>
          )}
        </div>
      )}

      {view === 'trends' && (
        <div className="w-full px-3 sm:px-6 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-4">
            <div>
              <h2 className={`text-xl sm:text-2xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>My Trends</h2>
              <p className={`${isDark ? 'text-zinc-400' : 'text-gray-500'} text-xs sm:text-sm`}>
                {trends.length} trend{trends.length === 1 ? '' : 's'} · {libraryVideoCountDisplay} saved video{libraryVideoCountDisplay === 1 ? '' : 's'}
                {(trendSearchQuery.trim() ||
                  trendTagFilters.length > 0 ||
                  trendDateFrom ||
                  trendDateTo ||
                  trendCreatorFilters.length > 0) && (
                  <span className="ml-1 opacity-80">
                    · Showing {sortedTrends.length}{' '}
                    {sortedTrends.length === 1 ? 'match' : 'matches'}
                  </span>
                )}
              </p>
            </div>
            <button
              onClick={() => {
                setVideoSelectionMode('new-trend');
                setShowVideoSelectionModal(true);
                setSelectedVideosForModal(new Set());
              }}
              className="w-full sm:w-auto px-4 py-2 bg-cyan-500 hover:bg-cyan-400 rounded-lg text-sm font-medium"
            >
              + New Trend
            </button>
          </div>

          {/* Status filter (Approved / Pending) + Sort Bar */}
          <div className={`border-b ${isDark ? 'border-zinc-800 bg-zinc-900/30' : 'border-gray-200 bg-gray-50'} mb-4 sm:mb-6`}>
            <div className="flex flex-wrap items-center gap-2 pb-2">
              <input
                type="text"
                placeholder="Search trends..."
                value={trendSearchQuery}
                onChange={(e) => setTrendSearchQuery(e.target.value)}
                className={`flex-1 min-w-[160px] max-w-xs px-3 py-2 rounded-lg text-sm border ${isDark ? 'bg-zinc-800 border-zinc-600 text-white placeholder-zinc-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} focus:outline-none focus:ring-2 focus:ring-cyan-500/50`}
              />
              <div className="flex flex-wrap items-center gap-1.5">
                <span className={`text-xs shrink-0 ${isDark ? 'text-zinc-500' : 'text-gray-600'}`}>From</span>
                <input
                  type="date"
                  value={trendDateFrom}
                  onChange={(e) => setTrendDateFrom(e.target.value)}
                  className={`px-2 py-1.5 rounded-lg text-xs border min-w-[9.5rem] ${isDark ? 'bg-zinc-800 border-zinc-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-cyan-500/50`}
                  aria-label="Filter trends created on or after"
                />
                <span className={`text-xs shrink-0 ${isDark ? 'text-zinc-500' : 'text-gray-600'}`}>To</span>
                <input
                  type="date"
                  value={trendDateTo}
                  onChange={(e) => setTrendDateTo(e.target.value)}
                  className={`px-2 py-1.5 rounded-lg text-xs border min-w-[9.5rem] ${isDark ? 'bg-zinc-800 border-zinc-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-cyan-500/50`}
                  aria-label="Filter trends created on or before"
                />
              </div>
              <div
                ref={trendTagFilterWrapRef}
                className={`relative min-w-[10rem] rounded-lg border px-2 py-1.5 ${isDark ? 'border-zinc-600 bg-zinc-800' : 'border-gray-300 bg-white'}`}
              >
                <button
                  type="button"
                  className={`w-full cursor-pointer text-xs flex items-center justify-between gap-1 text-left ${isDark ? 'text-zinc-300' : 'text-gray-800'}`}
                  onClick={() => {
                    setTrendCreatorFilterMenuOpen(false);
                    setTrendTagFilterMenuOpen((o) => !o);
                  }}
                  aria-expanded={trendTagFilterMenuOpen}
                  aria-haspopup="listbox"
                >
                  <span>
                    Tags
                    {trendTagFilters.length > 0 ? ` (${trendTagFilters.length})` : ''}
                  </span>
                  <span className="opacity-60" aria-hidden>
                    ▾
                  </span>
                </button>
                {trendTagFilterMenuOpen && (
                  <div
                    className={`absolute left-0 right-0 top-full z-30 mt-1 max-h-40 overflow-y-auto rounded-lg border p-2 shadow-lg ${isDark ? 'border-zinc-600 bg-zinc-900' : 'border-gray-200 bg-white'}`}
                    role="listbox"
                  >
                    {allTrendTagOptions.length === 0 ? (
                      <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>No tags yet</span>
                    ) : (
                      allTrendTagOptions.map((tag) => (
                        <label
                          key={tag}
                          className={`flex items-center gap-2 py-1 text-xs cursor-pointer ${isDark ? 'text-zinc-200' : 'text-gray-800'}`}
                        >
                          <input
                            type="checkbox"
                            className="rounded border-zinc-500"
                            checked={trendTagFilters.includes(tag)}
                            onChange={() => {
                              setTrendTagFilters((prev) =>
                                prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
                              );
                            }}
                          />
                          <span className="truncate">{tag}</span>
                        </label>
                      ))
                    )}
                  </div>
                )}
              </div>
              <div
                ref={trendCreatorFilterWrapRef}
                className={`relative min-w-[11rem] max-w-[16rem] rounded-lg border px-2 py-1.5 ${isDark ? 'border-zinc-600 bg-zinc-800' : 'border-gray-300 bg-white'}`}
              >
                <button
                  type="button"
                  className={`w-full cursor-pointer text-xs flex items-center justify-between gap-1 text-left ${isDark ? 'text-zinc-300' : 'text-gray-800'}`}
                  onClick={() => {
                    setTrendTagFilterMenuOpen(false);
                    setTrendCreatorFilterMenuOpen((o) => !o);
                  }}
                  aria-expanded={trendCreatorFilterMenuOpen}
                  aria-haspopup="listbox"
                >
                  <span>
                    Creators
                    {trendCreatorFilters.length > 0 ? ` (${trendCreatorFilters.length})` : ''}
                  </span>
                  <span className="opacity-60" aria-hidden>
                    ▾
                  </span>
                </button>
                {trendCreatorFilterMenuOpen && (
                  <div
                    className={`absolute left-0 right-0 top-full z-30 mt-1 max-h-52 overflow-hidden flex flex-col rounded-lg border shadow-lg ${isDark ? 'border-zinc-600 bg-zinc-900' : 'border-gray-200 bg-white'}`}
                    role="listbox"
                  >
                    <input
                      type="search"
                      placeholder="Search creators…"
                      value={trendCreatorListSearch}
                      onChange={(e) => setTrendCreatorListSearch(e.target.value)}
                      className={`w-full px-2 py-1.5 text-xs border-b shrink-0 ${isDark ? 'bg-zinc-900 border-zinc-700 text-white placeholder-zinc-500' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'} focus:outline-none focus:ring-2 focus:ring-cyan-500/50`}
                      aria-label="Search creator names"
                    />
                    <div className="max-h-36 overflow-y-auto p-2">
                      {libraryPagination.total > libraryVideos.length &&
                        libraryVideosForTrendFilters.length === 0 && (
                          <p className={`text-xs mb-1.5 ${isDark ? 'text-cyan-400/90' : 'text-cyan-700'}`}>
                            Loading full creator list…
                          </p>
                        )}
                      {allTrendCreatorOptions.length === 0 ? (
                        <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>
                          No creators (link videos to trends first)
                        </span>
                      ) : filteredTrendCreatorOptions.length === 0 ? (
                        <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>No matching names</span>
                      ) : (
                        filteredTrendCreatorOptions.map((name) => (
                          <label
                            key={name}
                            className={`flex items-center gap-2 py-1 text-xs cursor-pointer ${isDark ? 'text-zinc-200' : 'text-gray-800'}`}
                          >
                            <input
                              type="checkbox"
                              className="rounded border-zinc-500"
                              checked={trendCreatorFilters.includes(name)}
                              onChange={() => {
                                setTrendCreatorFilters((prev) =>
                                  prev.includes(name) ? prev.filter((c) => c !== name) : [...prev, name]
                                );
                              }}
                            />
                            <span className="truncate" title={name}>
                              @{name}
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              <span className={`text-xs sm:text-sm ${isDark ? 'text-zinc-500' : 'text-black'} mr-2`}>Status:</span>
              {[
                { id: 'all', label: 'All' },
                { id: 'approved', label: 'Approved' },
                { id: 'pending', label: 'Pending' }
              ].map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => setTrendStatusFilter(id)}
                  className={`px-2 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm transition-colors ${
                    trendStatusFilter === id
                      ? isDark ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/50' : 'bg-cyan-500/20 text-black border border-cyan-500/50'
                      : isDark ? 'bg-zinc-800 text-zinc-400 hover:text-white border border-transparent' : 'bg-white border border-gray-300 text-black hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2 pb-3">
              <span className={`text-xs sm:text-sm ${isDark ? 'text-zinc-500' : 'text-black'} mr-2`}>Sort by:</span>
              {[
                { id: 'trending', label: '🔥 Trending', hint: 'Hot in last 48hrs' },
                { id: 'recent', label: '🕐 Recent', hint: 'Recently created' },
                { id: 'videos', label: '📹 Videos', hint: 'Most videos' },
                { id: 'views', label: '👀 Views', hint: 'Most views' }
              ].map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => setTrendSortBy(id)}
                  className={`px-2 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm transition-colors ${
                    trendSortBy === id
                      ? isDark ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/50' : 'bg-cyan-500/20 text-black border border-cyan-500/50'
                      : isDark ? 'bg-zinc-800 text-zinc-400 hover:text-white border border-transparent' : 'bg-white border border-gray-300 text-black hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

            {/* Trends Grid */}
            <div className="space-y-4">
              {sortedTrends.length === 0 ? (
                <div className={`text-center py-12 ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>
                  <TrendingUp className="h-12 w-12 mb-4 opacity-50" />
                  <p>No trends yet</p>
                  <p className="text-sm mt-2">Select 3+ videos from Library and submit a trend</p>
                </div>
              ) : (
                sortedTrends.map(trend => (
              <div
                key={trend.id}
                className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'} rounded-2xl border overflow-hidden hover:border-cyan-500/50 transition-colors`}
              >
                <div className="p-4 sm:p-5">
                  <div className="flex flex-col sm:flex-row items-start sm:items-start justify-between mb-3 gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className={`px-2 py-1 bg-blue-500/20 ${isDark ? 'text-blue-300' : 'text-black'} rounded text-xs`}>
                          {trend.trend_type || 'Format'}
                        </span>
                        {trend.status === 'pending' && (
                          <span className={`px-2 py-1 bg-amber-500/20 ${isDark ? 'text-amber-400' : 'text-black'} rounded text-xs`}>Pending</span>
                        )}
                        {trend.status === 'approved' && (
                          <span className={`px-2 py-1 bg-green-500/20 ${isDark ? 'text-green-400' : 'text-black'} rounded text-xs`}>Approved</span>
                        )}
                        {user?.isAdmin && (trend.submitter_name || trend.submitter_email || trend.user_id) && (
                          <span className={`px-2 py-1 ${isDark ? 'bg-zinc-700 text-zinc-300' : 'bg-gray-200 text-gray-700'} rounded text-xs`} title={trend.submitter_email || trend.user_id}>
                            By {trend.submitter_name || trend.submitter_email || trend.user_id}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                        <div className="flex-1 min-w-0 order-2 sm:order-1">
                          <h3 className={`text-base sm:text-lg font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{trend.trend_name}</h3>
                          {trend.trend_summary && (
                            <p className={`text-xs mb-1.5 line-clamp-2 ${isDark ? 'text-zinc-400' : 'text-gray-600'}`} title={trend.trend_summary}>{trend.trend_summary}</p>
                          )}
                          {Array.isArray(trend.trend_elements) && trend.trend_elements.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-1.5">
                              {trend.trend_elements.slice(0, 5).map((el, i) => (
                                <span key={i} className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${isDark ? 'bg-cyan-500/20 text-cyan-300' : 'bg-cyan-100 text-cyan-800'}`}>{el}</span>
                              ))}
                            </div>
                          )}
                          {/* Visual, Audio, Hook (no text overlay subtitle) */}
                          {(trend.hook || trend.why_it_works) && (() => {
                            let hook = trend.hook;
                            if (typeof hook === 'string') {
                              try { hook = JSON.parse(hook); } catch (e) { hook = {}; }
                            }
                            const visual = hook?.visual?.trim?.();
                            const sound = hook?.sound?.trim?.();
                            const whyItWorks = (typeof trend.why_it_works === 'string' && trend.why_it_works.trim()) ? trend.why_it_works.trim() : '';
                            if (!visual && !sound && !whyItWorks) return null;
                            return (
                              <div className={`space-y-1 text-xs ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>
                                {visual && (
                                  <p className="line-clamp-2" title={visual}>
                                    <span className={`font-medium ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>Visual: </span>
                                    {visual.length > 80 ? visual.slice(0, 80) + '…' : visual}
                                  </p>
                                )}
                                {sound && (
                                  <p className="line-clamp-1" title={sound}>
                                    <span className={`font-medium ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>Sound: </span>
                                    {sound.length > 60 ? sound.slice(0, 60) + '…' : sound}
                                  </p>
                                )}
                                {whyItWorks && (
                                  <p className="line-clamp-2" title={whyItWorks}>
                                    <span className={`font-medium ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>Hook: </span>
                                    {whyItWorks.length > 100 ? whyItWorks.slice(0, 100) + '…' : whyItWorks}
                                  </p>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                        {/* Top 3 video previews */}
                        {trendTopVideos[trend.id] && trendTopVideos[trend.id].length > 0 && (
                          <div className="flex gap-2 flex-shrink-0 order-1 sm:order-2 justify-start sm:justify-end">
                            {trendTopVideos[trend.id].map((video) => {
                              let videoUrl = video.video_url;
                              if (!videoUrl && video.apify_metadata) {
                                const meta = typeof video.apify_metadata === 'string' 
                                  ? JSON.parse(video.apify_metadata) 
                                  : video.apify_metadata;
                                videoUrl = meta.videoUrl || meta.video_url || meta.mediaUrls?.[0] || meta.webVideoUrl;
                              }
                              
                              return (
                                <button
                                  key={video.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (videoUrl) {
                                      setVideoPreview({
                                        videoUrl,
                                        creator: getCreatorUsername(video),
                                        platform: video.platform,
                                        views: getApifyValue(video, 'views'),
                                        likes: getApifyValue(video, 'likes'),
                                        video: video // Include full video object for metadata
                                      });
                                    }
                                  }}
                                  className={`flex-shrink-0 w-20 h-28 sm:w-24 sm:h-32 rounded-lg overflow-hidden border ${isDark ? 'border-zinc-700 bg-zinc-800' : 'border-gray-300 bg-gray-100'} hover:border-cyan-500/50 transition-colors relative group`}
                                >
                                  {videoUrl ? (
                                    <video
                                      src={videoUrl}
                                      className="w-full h-full object-cover"
                                      muted
                                      loop
                                      playsInline
                                      onMouseEnter={(e) => {
                                        const video = e.target;
                                        const playPromise = video.play();
                                        if (playPromise !== undefined) {
                                          playPromise.catch(() => {});
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        const video = e.target;
                                        const pausePromise = video.pause();
                                        if (pausePromise !== undefined) {
                                          pausePromise.catch(() => {});
                                        }
                                        video.currentTime = 0;
                                      }}
                                    />
                                  ) : (
                                    <div className="w-full h-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                                      <Video className="h-6 w-6 text-white/30" />
                                    </div>
                                  )}
                                  <div className="absolute top-1 left-1 z-10">
                                    {video.platform === 'tiktok' ? (
                                      <div className="w-2.5 h-2.5 bg-black rounded flex items-center justify-center text-[5px] text-white">TT</div>
                                    ) : (
                                      <div className="w-2.5 h-2.5 bg-gradient-to-br from-purple-500 to-pink-500 rounded flex items-center justify-center text-[5px] text-white">IG</div>
                                    )}
                                  </div>
                                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <div className="text-[10px] text-white truncate">@{getCreatorUsername(video)}</div>
                                    <div className="text-[9px] text-zinc-300">
                                      {formatNumber(getApifyValue(video, 'views'))} views
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex sm:flex-col items-center sm:items-end gap-3 sm:gap-1 sm:text-right sm:ml-4">
                      <div>
                        <div className="text-xl sm:text-2xl font-bold text-cyan-400">{trend.savedCount || 0}</div>
                        <div className={`text-xs ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>videos</div>
                      </div>
                      {trend.totalViews > 0 && (
                        <div>
                          <div className={`text-base sm:text-lg font-medium ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>{formatNumber(trend.totalViews)}</div>
                          <div className={`text-xs ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>total views</div>
                        </div>
                      )}
                      {trend.isTrending && (
                        <span className={`inline-block px-2 py-0.5 bg-orange-500/20 ${isDark ? 'text-orange-400' : 'text-black'} rounded-full text-xs font-medium`}>
                          🔥 Trending
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className={`flex flex-col sm:flex-row border-t ${isDark ? 'border-zinc-800' : 'border-gray-200'}`}>
                  <button
                    data-tour="view-details-button"
                    onClick={async () => {
                      setSelectedTrend(trend);
                      setView('trend-detail');
                      await loadTrendVideos(trend.id);
                    }}
                    className={`flex-1 min-h-[44px] sm:min-h-0 p-3 touch-manipulation ${isDark ? 'bg-zinc-800/50 hover:bg-zinc-800 text-zinc-400 hover:text-white' : 'bg-gray-50 hover:bg-gray-100 text-gray-700 hover:text-gray-900'} transition-colors text-sm`}
                  >
                    View details
                  </button>
                  <div className={`flex-1 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 border-t sm:border-t-0 sm:border-l ${isDark ? 'border-zinc-700' : 'border-gray-200'} p-3 min-h-[44px] sm:min-h-0 ${isDark ? 'bg-cyan-500/20 ring-1 ring-cyan-500/50' : 'bg-cyan-500/15 ring-1 ring-cyan-500/40'}`}>
                    <button
                      data-tour="create-ideas-button"
                      onClick={() => {
                        setSelectedTrend(trend);
                        setShowCreateIdeaModal(true);
                      }}
                      className={`w-full font-medium text-sm text-center touch-manipulation py-1 ${isDark ? 'bg-transparent hover:opacity-90 text-cyan-300 hover:text-cyan-200' : 'bg-transparent hover:opacity-90 text-cyan-700 hover:text-cyan-800'} transition-colors animate-[bounce_2s_ease-in-out_infinite]`}
                    >
                      Generate briefs for {brandSettings.brand_name ? brandSettings.brand_name : 'your brand'}
                    </button>
                    {!brandSettings.brand_name && (
                      <span className={`text-xs ${isDark ? 'text-amber-400/90' : 'text-amber-600'}`}>
                        ⚠️ Configure your brand description in the Brand setting for better results
                      </span>
                    )}
                  </div>
                  {/* Only show Delete for trend owners (Add videos is in trend detail) */}
                  {(trend.user_id === userId || user?.isAdmin) && (
                    <>
                      <button
                        onClick={async () => {
                          if (window.confirm(`Are you sure you want to delete the trend "${trend.trend_name}"? This will remove the trend but keep the videos.`)) {
                            try {
                              const response = await fetch(`/api/trend-spotter/templates/${trend.id}?userId=${encodeURIComponent(userId)}`, {
                                method: 'DELETE'
                              });
                              const result = await response.json();
                              if (result.success) {
                                toast.success('Trend deleted');
                                loadTrends(false); // Refresh trends list (no top videos needed)
                                loadLibrary(true); // Force reload after deleting trend to update trend assignments
                              } else {
                                toast.error(result.error || 'Failed to delete trend');
                              }
                            } catch (error) {
                              console.error('Error deleting trend:', error);
                              toast.error('Failed to delete trend');
                            }
                          }
                        }}
                        className={`p-3 ${isDark ? 'bg-zinc-800/50 hover:bg-red-500/20 text-zinc-400 hover:text-red-400 border-l border-zinc-700' : 'bg-gray-50 hover:bg-red-50 text-gray-700 hover:text-red-600 border-l border-gray-200'} transition-colors text-sm`}
                        title="Delete trend"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
                ))
              )}
            </div>
          </div>
      )}

      {/* Create Idea popup: opens and starts generating immediately (no extra click) */}
      {showCreateIdeaModal && selectedTrend && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-3 sm:p-4">
          <div className={`w-full max-w-md rounded-2xl border overflow-hidden flex flex-col ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'}`}>
            <div className={`p-4 sm:p-6 border-b ${isDark ? 'border-zinc-800' : 'border-gray-200'}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Generating brief for {selectedTrend.trend_name}
                  </h2>
                  <p className={`text-sm mt-1 ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>
                    for {brandSettings.brand_name || 'your brand'}
                  </p>
                </div>
                <button
                  onClick={() => setShowCreateIdeaModal(false)}
                  className={`p-2 rounded-lg flex-shrink-0 ${isDark ? 'hover:bg-zinc-800' : 'hover:bg-gray-100'}`}
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              {(!brandSettings.brand_name || !brandSettings.brand_description) && !isLoading && (
                <p className={`text-amber-500 text-xs mt-2 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                  ⚠️ Configure your brand description in Brand setting for better results
                </p>
              )}
            </div>
            <div className={`p-4 sm:p-6 ${isDark ? 'bg-zinc-900/50' : 'bg-gray-50/50'}`}>
              {isLoading ? (
                <div className="flex flex-col items-center justify-center gap-3">
                  <Loader2 className={`h-8 w-8 animate-spin ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />
                  <span className={`text-sm font-medium ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>Generating...</span>
                  {ideaTimer !== null && (
                    <div className="w-full max-w-[200px]">
                      <div className={`flex items-center justify-between text-xs mb-1 ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>
                        <span>{ideaTimer}s remaining</span>
                        <span>{Math.round(((40 - ideaTimer) / 40) * 100)}%</span>
                      </div>
                      <div className={`w-full rounded-full h-1.5 overflow-hidden ${isDark ? 'bg-zinc-700' : 'bg-gray-200'}`}>
                        <div className="bg-cyan-500 h-full transition-all" style={{ width: `${((40 - ideaTimer) / 40) * 100}%` }} />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>
                  {userLimits.exceeded && (userLimits.reason === 'idea_limit' || userLimits.ideaLimitExceeded)
                    ? 'Close this window or upgrade to generate briefs.'
                    : 'Starting generation...'}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {view === 'trend-detail' && selectedTrend && (() => {
        // Check if user owns the trend or is admin
        const isOwner = selectedTrend.user_id === userId || user?.isAdmin;
        
        return (
        <div className="w-full px-3 sm:px-6 py-4 sm:py-6">
          {/* Header */}
          <div className="mb-4 sm:mb-6">
            <button
              onClick={() => {
                setSelectedTrend(null);
                setView('trends');
              }}
              className={`mb-3 sm:mb-4 ${isDark ? 'text-zinc-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} flex items-center gap-2 text-sm`}
            >
              <X className="h-4 w-4" />
              <span className="hidden sm:inline">Back to Trends</span>
              <span className="sm:hidden">Back</span>
            </button>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className={`px-2 py-1 bg-blue-500/20 ${isDark ? 'text-blue-300' : 'text-black'} rounded text-xs`}>
                    {selectedTrend.trend_type || 'Format'}
                  </span>
                  {selectedTrend.status === 'pending' && (
                    <span className={`px-2 py-1 bg-amber-500/20 ${isDark ? 'text-amber-400' : 'text-black'} rounded text-xs`}>Pending</span>
                  )}
                  {selectedTrend.status === 'approved' && (
                    <span className={`px-2 py-1 bg-green-500/20 ${isDark ? 'text-green-400' : 'text-black'} rounded text-xs`}>Approved</span>
                  )}
                  {user?.isAdmin && selectedTrend.status === 'pending' && (
                    <button
                      onClick={async () => {
                        try {
                          const response = await fetch(`/api/trend-spotter/approve/${selectedTrend.id}`, {
                            method: 'POST'
                          });
                          const result = await response.json();
                          if (result.success) {
                            toast.success('Trend approved successfully');
                            // Update the selected trend status
                            setSelectedTrend({ ...selectedTrend, status: 'approved' });
                            // Reload trends to update the list
                            loadTrends(false); // Refresh trends list (no top videos needed)
                          } else {
                            toast.error(result.error || 'Failed to approve trend');
                          }
                        } catch (error) {
                          console.error('Error approving trend:', error);
                          toast.error('Failed to approve trend');
                        }
                      }}
                      className="px-2 sm:px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-medium transition-colors flex items-center gap-1"
                    >
                      <CheckCircle className="h-3 w-3" />
                      <span className="hidden sm:inline">Approve</span>
                    </button>
                  )}
                </div>
                {/* Check if user owns the trend or is admin */}
                {(() => {
                  const isOwner = selectedTrend.user_id === userId || user?.isAdmin;
                  return isEditingTrendName && isOwner ? (
                    <input
                      type="text"
                      value={selectedTrend.trend_name || ''}
                      onChange={(e) => {
                        setSelectedTrend({ ...selectedTrend, trend_name: e.target.value });
                      }}
                      onBlur={async () => {
                        try {
                          await updateTrend(selectedTrend.id, { trend_name: selectedTrend.trend_name });
                          setIsEditingTrendName(false);
                        } catch (error) {
                          console.error('Error updating trend name:', error);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.target.blur();
                        } else if (e.key === 'Escape') {
                          setIsEditingTrendName(false);
                          loadTrends(false); // Refresh trends list (no top videos needed) // Reload to reset
                        }
                      }}
                      className={`text-xl sm:text-2xl font-bold mb-1 ${isDark ? 'bg-zinc-800 border-cyan-500' : 'bg-white border-gray-300'} border rounded-lg px-3 py-2 focus:outline-none focus:border-cyan-400 w-full`}
                      autoFocus
                    />
                  ) : (
                    <h1 
                      className={`text-xl sm:text-2xl font-bold mb-1 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'} ${isOwner ? 'cursor-pointer hover:text-cyan-400 transition-colors' : ''}`}
                      onClick={() => isOwner && setIsEditingTrendName(true)}
                      title={isOwner ? "Click to edit" : "Read-only (shared trend)"}
                    >
                      {selectedTrend.trend_name}
                      {isOwner && <Edit className="h-4 w-4 opacity-50" />}
                    </h1>
                  );
                })()}
                <p className={`${isDark ? 'text-zinc-400' : 'text-gray-500'} text-xs sm:text-sm`}>
                  {selectedTrend.video_count || 0} saved videos
                </p>
                {selectedTrend.trend_summary && (
                  <p className={`mt-2 text-sm ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>{selectedTrend.trend_summary}</p>
                )}
                {Array.isArray(selectedTrend.trend_elements) && selectedTrend.trend_elements.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {selectedTrend.trend_elements.map((el, i) => (
                      <span key={i} className={`px-2 py-0.5 rounded-md text-xs font-medium ${isDark ? 'bg-cyan-500/20 text-cyan-300' : 'bg-cyan-100 text-cyan-800'}`}>{el}</span>
                    ))}
                  </div>
                )}
                {selectedTrend.status === 'approved' && (
                  <button
                    type="button"
                    onClick={() => {
                      const slug = (selectedTrend.trend_name || '')
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, '-')
                        .replace(/^-|-$/g, '') || 'trend';
                      const url = `${window.location.origin}/share/trend/${selectedTrend.id}-${slug}`;
                      navigator.clipboard.writeText(url).then(() => {
                        toast.success('Share link copied! Anyone with the link can view this trend.');
                      }).catch(() => toast.error('Failed to copy link'));
                    }}
                    className={`mt-2 flex items-center gap-2 text-sm font-medium ${isDark ? 'text-cyan-400 hover:text-cyan-300' : 'text-cyan-600 hover:text-cyan-700'}`}
                    title="Copy shareable link (viewable without login)"
                  >
                    <Share2 className="h-4 w-4" />
                    <span>Share</span>
                  </button>
                )}
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
                {/* Generate brief Section - Top Right */}
                <div className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'} rounded-xl p-3 sm:p-4 border w-full sm:w-auto sm:min-w-[280px] sm:max-w-[320px]`}>
                  <div className="mb-2">
                    <h3 className={`text-sm sm:text-base font-semibold mb-0.5 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Generate brief for {brandSettings.brand_name || 'Your Brand'}
                    </h3>
                    <p className={`${isDark ? 'text-zinc-400' : 'text-gray-600'} text-xs`}>Get AI-generated variations tailored to your brand</p>
                    {(!brandSettings.brand_name || !brandSettings.brand_description) && (
                      <p className="text-amber-400 text-xs mt-1.5">
                        ⚠️ Configure your brand settings in the Brand page for better results
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => runGenerateIdeas()}
                    disabled={isLoading || (userLimits.exceeded && (userLimits.reason === 'idea_limit' || userLimits.ideaLimitExceeded))}
                    className={`w-full px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center gap-2 font-medium text-xs sm:text-sm ${
                      userLimits.exceeded && (userLimits.reason === 'idea_limit' || userLimits.ideaLimitExceeded)
                        ? 'bg-zinc-700 text-zinc-500 cursor-not-allowed'
                        : 'bg-cyan-500 hover:bg-cyan-400 disabled:bg-zinc-700 disabled:text-zinc-500'
                    }`}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <div className="flex flex-col items-center gap-1">
                        <span className="hidden sm:inline">Generating...</span>
                        <span className="sm:hidden">Generating...</span>
                          {ideaTimer !== null && (
                            <div className="w-full max-w-[200px]">
                              <div className="flex items-center justify-between text-xs mb-1">
                                <span className="text-cyan-300">{ideaTimer}s remaining</span>
                                <span className={`${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>{Math.round(((40 - ideaTimer) / 40) * 100)}%</span>
                              </div>
                              <div className={`w-full ${isDark ? 'bg-zinc-700' : 'bg-gray-200'} rounded-full h-1.5 overflow-hidden`}>
                                <div 
                                  className="bg-cyan-500 h-full transition-all duration-300"
                                  style={{ width: `${((40 - ideaTimer) / 40) * 100}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4" />
                        <span className="hidden sm:inline">Generate Variations</span>
                        <span className="sm:hidden">Generate</span>
                      </>
                    )}
                  </button>
                </div>
                {isOwner && (
                  <button
                    onClick={async () => {
                      if (window.confirm(`Are you sure you want to delete the trend "${selectedTrend.trend_name}"? This will remove the trend but keep the videos.`)) {
                        try {
                          const response = await fetch(`/api/trend-spotter/templates/${selectedTrend.id}?userId=${encodeURIComponent(userId)}`, {
                            method: 'DELETE'
                          });
                          const result = await response.json();
                          if (result.success) {
                            toast.success('Trend deleted');
                            setSelectedTrend(null);
                            setView('trends');
                            loadTrends(false); // Refresh trends list (no top videos needed)
                            loadLibrary(true); // Force reload after deleting trend to update trend assignments
                          } else {
                            toast.error(result.error || 'Failed to delete trend');
                          }
                        } catch (error) {
                          console.error('Error deleting trend:', error);
                          toast.error('Failed to delete trend');
                        }
                      }
                    }}
                    className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-xs sm:text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                    title="Delete trend"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="hidden sm:inline">Delete Trend</span>
                    <span className="sm:hidden">Delete</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Trend Metadata Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
            {/* THE HOOK */}
            <div className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'} rounded-2xl p-6 border`}>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xl">🚀</span>
                <h3 className={`text-sm font-medium ${isDark ? 'text-zinc-400' : 'text-gray-600'} uppercase tracking-wider`}>THE HOOK</h3>
              </div>
              <div className="space-y-4">
                <div>
                  <label className={`${isDark ? 'text-zinc-500' : 'text-gray-600'} text-xs mb-1 block`}>Visual</label>
                  <input
                    type="text"
                    value={(() => {
                      let hook = selectedTrend.hook;
                      if (typeof hook === 'string') {
                        try {
                          hook = JSON.parse(hook);
                        } catch (e) {
                          hook = {};
                        }
                      }
                      return hook?.visual || '';
                    })()}
                    onChange={(e) => {
                      if (!isOwner) return;
                      let hook = selectedTrend.hook;
                      if (typeof hook === 'string') {
                        try {
                          hook = JSON.parse(hook);
                        } catch (e) {
                          hook = {};
                        }
                      }
                      const updatedTrend = {
                        ...selectedTrend,
                        hook: {
                          ...hook,
                          visual: e.target.value
                        }
                      };
                      setSelectedTrend(updatedTrend);
                      // Debounce save
                      clearTimeout(window.trendUpdateTimeout);
                      window.trendUpdateTimeout = setTimeout(() => {
                        updateTrend(selectedTrend.id, { hook: updatedTrend.hook });
                      }, 1000);
                    }}
                    readOnly={!isOwner}
                    placeholder="Brief description of the first consistent visual element/action"
                    className={`w-full px-3 py-2 ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-300'} border rounded-lg text-sm ${isOwner ? 'focus:outline-none focus:border-cyan-500' : 'opacity-60 cursor-not-allowed'}`}
                  />
                </div>
                <div>
                  <label className={`${isDark ? 'text-zinc-500' : 'text-gray-600'} text-xs mb-1 block`}>Text Overlay</label>
                  <input
                    type="text"
                    value={(() => {
                      let hook = selectedTrend.hook;
                      if (typeof hook === 'string') {
                        try {
                          hook = JSON.parse(hook);
                        } catch (e) {
                          hook = {};
                        }
                      }
                      return hook?.text_overlay || '';
                    })()}
                    onChange={(e) => {
                      if (!isOwner) return;
                      let hook = selectedTrend.hook;
                      if (typeof hook === 'string') {
                        try {
                          hook = JSON.parse(hook);
                        } catch (e) {
                          hook = {};
                        }
                      }
                      const updatedTrend = {
                        ...selectedTrend,
                        hook: {
                          ...hook,
                          text_overlay: e.target.value
                        }
                      };
                      setSelectedTrend(updatedTrend);
                      clearTimeout(window.trendUpdateTimeout);
                      window.trendUpdateTimeout = setTimeout(() => {
                        updateTrend(selectedTrend.id, { hook: updatedTrend.hook });
                      }, 1000);
                    }}
                    readOnly={!isOwner}
                    placeholder="Exact or templated text overlay for the hook scene"
                    className={`w-full px-3 py-2 ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-300'} border rounded-lg text-sm ${isOwner ? 'focus:outline-none focus:border-cyan-500' : 'opacity-60 cursor-not-allowed'}`}
                  />
                </div>
                <div>
                  <label className={`${isDark ? 'text-zinc-500' : 'text-gray-600'} text-xs mb-1 block`}>Sound</label>
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={(() => {
                        let hook = selectedTrend.hook;
                        if (typeof hook === 'string') {
                          try {
                            hook = JSON.parse(hook);
                          } catch (e) {
                            hook = {};
                          }
                        }
                        return hook?.sound || '';
                      })()}
                      onChange={(e) => {
                        if (!isOwner) return;
                        let hook = selectedTrend.hook;
                        if (typeof hook === 'string') {
                          try {
                            hook = JSON.parse(hook);
                          } catch (e) {
                            hook = {};
                          }
                        }
                        const updatedTrend = {
                          ...selectedTrend,
                          hook: {
                            ...hook,
                            sound: e.target.value
                          }
                        };
                        setSelectedTrend(updatedTrend);
                        clearTimeout(window.trendUpdateTimeout);
                        window.trendUpdateTimeout = setTimeout(() => {
                          updateTrend(selectedTrend.id, { hook: updatedTrend.hook });
                        }, 1000);
                      }}
                      readOnly={!isOwner}
                      placeholder="Specific sound cue/VO line if consistent"
                      className={`w-full px-3 py-2 ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-300'} border rounded-lg text-sm ${isOwner ? 'focus:outline-none focus:border-cyan-500' : 'opacity-60 cursor-not-allowed'}`}
                    />
                    {(() => {
                      const soundUrls = getTrendSoundUrls(selectedTrend.id);
                      const hasAnySound = soundUrls.tiktok || soundUrls.instagram;
                      if (!hasAnySound) return null;
                      
                      return (
                        <div className="flex gap-2 flex-wrap">
                          {soundUrls.tiktok && (
                            <a
                              href={soundUrls.tiktok}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-cyan-400 hover:text-cyan-300 underline"
                            >
                              🎵 TikTok Sound
                            </a>
                          )}
                          {soundUrls.instagram && (
                            <a
                              href={soundUrls.instagram}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-cyan-400 hover:text-cyan-300 underline"
                            >
                              🎵 Instagram Sound
                            </a>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>

            {/* TEMPLATE */}
            <div className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'} rounded-2xl p-4 sm:p-6 border`}>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xl">📋</span>
                <h3 className={`text-sm font-medium ${isDark ? 'text-zinc-400' : 'text-gray-600'} uppercase tracking-wider`}>TEMPLATE</h3>
              </div>
              <div className="space-y-4">
                <div>
                  <label className={`${isDark ? 'text-zinc-500' : 'text-gray-600'} text-xs mb-1 block`}>Visual Structure</label>
                  <textarea
                    value={(() => {
                      let template = selectedTrend.template;
                      if (typeof template === 'string') {
                        try {
                          template = JSON.parse(template);
                        } catch (e) {
                          template = {};
                        }
                      }
                      return template?.visual || '';
                    })()}
                    onChange={(e) => {
                      if (!isOwner) return;
                      let template = selectedTrend.template;
                      if (typeof template === 'string') {
                        try {
                          template = JSON.parse(template);
                        } catch (e) {
                          template = {};
                        }
                      }
                      const updatedTrend = {
                        ...selectedTrend,
                        template: {
                          ...template,
                          visual: e.target.value
                        }
                      };
                      setSelectedTrend(updatedTrend);
                      clearTimeout(window.trendUpdateTimeout);
                      window.trendUpdateTimeout = setTimeout(() => {
                        updateTrend(selectedTrend.id, { template: updatedTrend.template });
                      }, 1000);
                    }}
                    readOnly={!isOwner}
                    placeholder="The repeatable consistent visual/action structure with [VARIABLES]"
                    rows={2}
                    className={`w-full px-3 py-2 ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-300'} border rounded-lg text-sm resize-none ${isOwner ? 'focus:outline-none focus:border-cyan-500' : 'opacity-60 cursor-not-allowed'}`}
                  />
                </div>
                <div>
                  <label className={`${isDark ? 'text-zinc-500' : 'text-gray-600'} text-xs mb-1 block`}>Text Pattern</label>
                  <textarea
                    value={(() => {
                      let template = selectedTrend.template;
                      if (typeof template === 'string') {
                        try {
                          template = JSON.parse(template);
                        } catch (e) {
                          template = {};
                        }
                      }
                      return template?.text_overlay || '';
                    })()}
                    onChange={(e) => {
                      if (!isOwner) return;
                      let template = selectedTrend.template;
                      if (typeof template === 'string') {
                        try {
                          template = JSON.parse(template);
                        } catch (e) {
                          template = {};
                        }
                      }
                      const updatedTrend = {
                        ...selectedTrend,
                        template: {
                          ...template,
                          text_overlay: e.target.value
                        }
                      };
                      setSelectedTrend(updatedTrend);
                      clearTimeout(window.trendUpdateTimeout);
                      window.trendUpdateTimeout = setTimeout(() => {
                        updateTrend(selectedTrend.id, { template: updatedTrend.template });
                      }, 1000);
                    }}
                    readOnly={!isOwner}
                    placeholder="The repeatable consistent text overlay structure with [VARIABLES]"
                    rows={2}
                    className={`w-full px-3 py-2 ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-300'} border rounded-lg text-sm resize-none ${isOwner ? 'focus:outline-none focus:border-cyan-500' : 'opacity-60 cursor-not-allowed'}`}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Why It Works + Difficulty + Hashtags */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
            {/* WHY IT WORKS */}
            <div className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'} rounded-2xl p-4 sm:p-6 border`}>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xl">💡</span>
                <h3 className={`text-sm font-medium ${isDark ? 'text-zinc-400' : 'text-gray-600'} uppercase tracking-wider`}>WHY IT WORKS</h3>
              </div>
              <input
                type="text"
                value={selectedTrend.why_it_works || ''}
                onChange={(e) => {
                  if (!isOwner) return;
                  setSelectedTrend({ ...selectedTrend, why_it_works: e.target.value });
                  clearTimeout(window.trendUpdateTimeout);
                  window.trendUpdateTimeout = setTimeout(() => {
                    updateTrend(selectedTrend.id, { why_it_works: e.target.value });
                  }, 1000);
                }}
                readOnly={!isOwner}
                placeholder="relatable, humor, irony, curiosity"
                    className={`w-full px-3 py-2 ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-300'} border rounded-lg text-sm ${isOwner ? 'focus:outline-none focus:border-cyan-500' : 'opacity-60 cursor-not-allowed'}`}
              />
            </div>

            {/* DIFFICULTY */}
            <div className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'} rounded-2xl p-4 sm:p-6 border`}>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xl">⭐</span>
                <h3 className={`text-sm font-medium ${isDark ? 'text-zinc-400' : 'text-gray-600'} uppercase tracking-wider`}>DIFFICULTY</h3>
              </div>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map(i => {
                  const difficulty = typeof selectedTrend.difficulty_to_create === 'object' 
                    ? (selectedTrend.difficulty_to_create?.score || 3) 
                    : 3;
                  return (
                    <button
                      key={i}
                      onClick={() => {
                        if (!isOwner) return;
                        const updated = {
                          ...selectedTrend,
                          difficulty_to_create: {
                            ...(typeof selectedTrend.difficulty_to_create === 'object' ? selectedTrend.difficulty_to_create : {}),
                            score: i
                          }
                        };
                        setSelectedTrend(updated);
                        updateTrend(selectedTrend.id, { difficulty_to_create: updated.difficulty_to_create });
                      }}
                      disabled={!isOwner}
                      className={`text-lg transition-colors ${i <= difficulty ? 'text-yellow-400' : isDark ? 'text-zinc-600 hover:text-yellow-400/50' : 'text-gray-400 hover:text-yellow-400/50'} ${!isOwner ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      ★
                    </button>
                  );
                })}
                <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-gray-500'} ml-2`}>
                  {['', 'Very Easy', 'Easy', 'Medium', 'Hard', 'Very Hard'][
                    typeof selectedTrend.difficulty_to_create === 'object' 
                      ? (selectedTrend.difficulty_to_create?.score || 3) 
                      : 3
                  ]}
                </span>
              </div>
            </div>

            {/* HASHTAGS */}
            <div className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'} rounded-2xl p-4 sm:p-6 border`}>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xl">#</span>
                <h3 className={`text-sm font-medium ${isDark ? 'text-zinc-400' : 'text-gray-600'} uppercase tracking-wider`}>HASHTAGS</h3>
              </div>
              <input
                type="text"
                value={
                  (() => {
                    const oc = selectedTrend.optimization_cues;
                    if (typeof oc !== 'object' || !oc) return selectedTrend.hashtags || '';
                    const kh = oc.keyHashtags;
                    if (Array.isArray(kh)) return kh.join(' ');
                    if (typeof kh === 'string') return kh;
                    return selectedTrend.hashtags || '';
                  })()
                }
                onChange={(e) => {
                  if (!isOwner) return;
                  const hashtags = e.target.value.split(' ').filter(h => h.trim());
                  const updated = {
                    ...selectedTrend,
                    optimization_cues: {
                      ...(typeof selectedTrend.optimization_cues === 'object' ? selectedTrend.optimization_cues : {}),
                      keyHashtags: hashtags
                    }
                  };
                  setSelectedTrend(updated);
                  clearTimeout(window.trendUpdateTimeout);
                  window.trendUpdateTimeout = setTimeout(() => {
                    updateTrend(selectedTrend.id, { optimization_cues: updated.optimization_cues });
                  }, 1000);
                }}
                readOnly={!isOwner}
                placeholder="#trend #hashtags"
                    className={`w-full px-3 py-2 ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-300'} border rounded-lg text-sm ${isOwner ? 'focus:outline-none focus:border-cyan-500' : 'opacity-60 cursor-not-allowed'}`}
              />
            </div>
          </div>

          {/* How to Use + Suggested Captions (new MASTER DOC fields) */}
          {(selectedTrend.how_to_use || (typeof selectedTrend.optimization_cues === 'object' && Array.isArray(selectedTrend.optimization_cues?.suggestedCaptions) && selectedTrend.optimization_cues.suggestedCaptions.length > 0)) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
              {selectedTrend.how_to_use && (
                <div className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'} rounded-2xl p-4 sm:p-6 border`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xl">📌</span>
                    <h3 className={`text-sm font-medium ${isDark ? 'text-zinc-400' : 'text-gray-600'} uppercase tracking-wider`}>HOW TO USE</h3>
                  </div>
                  <p className={`text-sm ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>{selectedTrend.how_to_use}</p>
                </div>
              )}
              {typeof selectedTrend.optimization_cues === 'object' && Array.isArray(selectedTrend.optimization_cues?.suggestedCaptions) && selectedTrend.optimization_cues.suggestedCaptions.length > 0 && (
                <div className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'} rounded-2xl p-4 sm:p-6 border`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xl">💬</span>
                    <h3 className={`text-sm font-medium ${isDark ? 'text-zinc-400' : 'text-gray-600'} uppercase tracking-wider`}>SUGGESTED CAPTIONS</h3>
                  </div>
                  <ul className={`space-y-2 text-sm ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
                    {selectedTrend.optimization_cues.suggestedCaptions.map((cap, i) => (
                      <li key={i} className="flex gap-2">
                        <span className={`flex-shrink-0 ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>{i + 1}.</span>
                        <span>"{cap}"</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Saved Videos Section */}
          {(trendVideos.length > 0 || libraryVideos.filter(v => {
            // Check if video is assigned to this trend (support both new trends array and legacy trend_id)
            return (v.trends && Array.isArray(v.trends) && v.trends.some(t => t.id === selectedTrend.id)) ||
                   (v.trend_id === selectedTrend.id);
          }).length > 0) && (
            <div className="mb-4 sm:mb-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 gap-2">
                <h3 className={`text-xs sm:text-sm font-medium ${isDark ? 'text-zinc-400' : 'text-gray-600'} uppercase tracking-wider`}>YOUR SAVED VIDEOS</h3>
                {isOwner && (
                  <button
                    onClick={() => {
                      // Find videos already assigned to this trend
                      const existingVideoIds = libraryVideos
                        .filter(v => {
                          const hasTrend = (v.trends && Array.isArray(v.trends) && v.trends.some(t => t.id === selectedTrend.id)) ||
                                         (v.trend_id === selectedTrend.id);
                          return hasTrend;
                        })
                        .map(v => v.id);
                      
                      setVideoSelectionMode({ type: 'add-to-trend', trendId: selectedTrend.id });
                      setSelectedVideosForModal(new Set(existingVideoIds));
                      setShowVideoSelectionModal(true);
                    }}
                    className="w-full sm:w-auto px-3 py-1.5 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 rounded text-xs font-medium border border-cyan-500/30"
                  >
                    + Add videos
                  </button>
                )}
              </div>
              <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-2 -mx-3 sm:mx-0 px-3 sm:px-0">
                {(trendVideos.length > 0 ? trendVideos : libraryVideos.filter(v => {
                  // Check if video is assigned to this trend (support both new trends array and legacy trend_id)
                  return (v.trends && Array.isArray(v.trends) && v.trends.some(t => t.id === selectedTrend.id)) ||
                         (v.trend_id === selectedTrend.id);
                })).map((video) => {
                  // Get video URL from apify_metadata or video_url column
                  let videoUrl = video.video_url;
                  if (!videoUrl && video.apify_metadata) {
                    const meta = typeof video.apify_metadata === 'string' 
                      ? JSON.parse(video.apify_metadata) 
                      : video.apify_metadata;
                    videoUrl = meta.videoUrl || meta.video_url || meta.mediaUrls?.[0] || meta.webVideoUrl;
                  }
                  
                  return (
                    <div key={video.id} className="flex-shrink-0">
                      <div className="relative">
                        <button
                          onClick={() => {
                            if (videoUrl) {
                              setVideoPreview({
                                videoUrl,
                                creator: getCreatorUsername(video),
                                platform: video.platform || 'tiktok',
                                views: getApifyValue(video, 'views'),
                                likes: getApifyValue(video, 'likes'),
                                video: video // Include full video object for metadata
                              });
                            }
                          }}
                          className={`relative w-20 h-28 rounded-lg overflow-hidden ${isDark ? 'bg-zinc-800' : 'bg-gray-100'} hover:ring-2 ring-cyan-500/50 transition-all cursor-pointer`}
                        >
                          {videoUrl ? (
                            <>
                              <video
                                src={videoUrl}
                                className="w-full h-full object-cover"
                                muted
                                loop
                                playsInline
                                onMouseEnter={(e) => {
                                  const video = e.target;
                                  const playPromise = video.play();
                                  if (playPromise !== undefined) {
                                    playPromise.catch((error) => {
                                      // Play was interrupted or failed, ignore error
                                      // This is expected when pause() is called quickly
                                    });
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const video = e.target;
                                  // Pause the video
                                  const pausePromise = video.pause();
                                  if (pausePromise !== undefined) {
                                    pausePromise.catch(() => {
                                      // Pause error, ignore
                                    });
                                  }
                                  // Reset to beginning
                                  video.currentTime = 0;
                                }}
                              />
                              <div className="absolute top-1 left-1 z-10">
                                {video.platform === 'tiktok' ? (
                                  <div className="w-4 h-4 bg-black rounded flex items-center justify-center text-[8px] text-white">TT</div>
                                ) : (
                                  <div className="w-4 h-4 bg-gradient-to-br from-purple-500 to-pink-500 rounded flex items-center justify-center text-[8px] text-white">IG</div>
                                )}
                              </div>
                              {/* Fallback placeholder if video fails to load */}
                              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-xs text-white" style={{ display: 'none' }}>
                                {video.platform === 'tiktok' ? 'TT' : 'IG'}
                              </div>
                            </>
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-xs text-white">
                              {video.platform === 'tiktok' ? 'TT' : 'IG'}
                            </div>
                          )}
                        </button>
                        {/* Delete button - only visible to trend owner */}
                        {isOwner && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveVideoFromTrend(video.id, selectedTrend.id);
                            }}
                            className="absolute top-1 right-1 z-20 w-5 h-5 bg-red-600 hover:bg-red-700 rounded-full flex items-center justify-center transition-colors"
                            title="Remove video from trend"
                          >
                            <X className="h-3 w-3 text-white" />
                          </button>
                        )}
                      </div>
                      <div className="mt-2 text-xs text-zinc-400 truncate w-20">
                        {getCreatorUsername(video)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        );
      })()}

      {view === 'board' && (
        <div className="w-full px-3 sm:px-6 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-4">
            <div>
              <h2 className={`text-xl sm:text-2xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>Content Board</h2>
              <p className={`${isDark ? 'text-zinc-400' : 'text-gray-500'} text-sm`}>{briefs.length} total briefs</p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
              {/* Filter buttons */}
              <div className={`flex items-center gap-2 ${isDark ? 'bg-zinc-800' : 'bg-white border border-gray-200'} rounded-lg p-1 w-full sm:w-auto`}>
                <button
                  onClick={() => setBoardFilter('all')}
                  className={`flex-1 sm:flex-none px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                    boardFilter === 'all'
                      ? isDark ? 'bg-zinc-700 text-white' : 'bg-gray-100 text-gray-900'
                      : isDark ? 'text-zinc-400 hover:text-zinc-300' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setBoardFilter('mine')}
                  className={`flex-1 sm:flex-none px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                    boardFilter === 'mine'
                      ? isDark ? 'bg-zinc-700 text-white' : 'bg-gray-100 text-gray-900'
                      : isDark ? 'text-zinc-400 hover:text-zinc-300' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Mine
                </button>
                <button
                  onClick={() => setBoardFilter('shared')}
                  className={`flex-1 sm:flex-none px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                    boardFilter === 'shared'
                      ? isDark ? 'bg-zinc-700 text-white' : 'bg-gray-100 text-gray-900'
                      : isDark ? 'text-zinc-400 hover:text-zinc-300' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Shared
                </button>
              </div>
              <button
                data-tour="new-idea-button"
                onClick={() => {
                  if (userLimits.exceeded && (userLimits.reason === 'idea_limit' || userLimits.ideaLimitExceeded)) {
                    setShowIdeaLimitModal(true);
                    return;
                  }
                  setSelectedBrief({ angle: '', shots: [], caption: '', notes: '', status: 'ideas', tags: [] });
                  setShowBriefModal(true);
                  setTagInput('');
                }}
                className="w-full sm:w-auto px-4 py-2 rounded-lg flex items-center justify-center gap-2 text-sm font-medium bg-cyan-500 hover:bg-cyan-400 text-white animate-[bounce_2s_ease-in-out_infinite]"
              >
                <Plus className="h-4 w-4" />
                New Brief
              </button>
            </div>
          </div>

          {/* Tag Search and Filter */}
          {(() => {
            // Get all unique tags from all briefs
            const allTags = new Set();
            briefs.forEach(brief => {
              if (Array.isArray(brief.tags)) {
                brief.tags.forEach(tag => allTags.add(tag));
              }
            });
            const uniqueTags = Array.from(allTags).sort();
            
            // Filter tags based on search input
            const filteredTags = tagSearch.trim()
              ? uniqueTags.filter(tag => tag.toLowerCase().includes(tagSearch.toLowerCase()))
              : uniqueTags;
            
            return (
              <div className="mb-4 sm:mb-6 space-y-3">
                {/* Tag Search Bar */}
                <div className="relative">
                  <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${isDark ? 'text-zinc-500' : 'text-gray-400'}`} />
                  <input
                    type="text"
                    value={tagSearch}
                    onChange={(e) => setTagSearch(e.target.value)}
                    placeholder="Search tags..."
                    className={`w-full pl-10 pr-4 py-2 ${isDark ? 'bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} border rounded-lg text-sm focus:outline-none focus:border-cyan-500`}
                  />
                  {tagSearch && (
                    <button
                      onClick={() => {
                        setTagSearch('');
                        setSelectedTags([]);
                      }}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                
                {/* Tag Filter Buttons */}
                {uniqueTags.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`text-sm ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>Filter by tags:</span>
                    {filteredTags.length > 0 ? (
                      filteredTags.map(tag => {
                        const isSelected = selectedTags.includes(tag);
                        return (
                          <button
                            key={tag}
                            onClick={() => {
                              if (isSelected) {
                                setSelectedTags(selectedTags.filter(t => t !== tag));
                              } else {
                                setSelectedTags([...selectedTags, tag]);
                              }
                            }}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                              isSelected
                                ? 'bg-cyan-500 text-white'
                                : isDark ? 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-300' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          >
                            {tag}
                          </button>
                        );
                      })
                    ) : (
                      <span className="text-sm text-zinc-500">No tags match "{tagSearch}"</span>
                    )}
                    {selectedTags.length > 0 && (
                      <button
                        onClick={() => {
                          setSelectedTags([]);
                          setTagSearch('');
                        }}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium ${isDark ? 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-300' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-gray-900'} transition-colors`}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Kanban Board */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {['ideas', 'inProgress', 'readyToPost', 'posted'].map((status) => {
              // Filter briefs based on boardFilter
              let filteredBriefs = briefs.filter(b => b.status === status);
              if (boardFilter === 'mine') {
                // Show only briefs owned by the user
                filteredBriefs = filteredBriefs.filter(b => b.user_id === userId);
              } else if (boardFilter === 'shared') {
                // Show briefs where user is a collaborator (not the owner)
                filteredBriefs = filteredBriefs.filter(b => 
                  b.user_id !== userId && 
                  b.collaborators?.some(c => c.user_id === userId)
                );
              } else if (boardFilter === 'all') {
                // Show all briefs: both owned by user AND shared with user
                // The backend already returns both, so we just need to ensure we're not filtering them out
                // No additional filtering needed - all briefs returned from backend are accessible
              }
              
              // Filter by selected tags
              if (selectedTags.length > 0) {
                filteredBriefs = filteredBriefs.filter(b => {
                  const briefTags = Array.isArray(b.tags) ? b.tags : [];
                  return selectedTags.some(tag => briefTags.includes(tag));
                });
              }
              
              // Filter by tag search input (matches briefs with tags containing the search text)
              if (tagSearch.trim()) {
                const searchLower = tagSearch.toLowerCase().trim();
                filteredBriefs = filteredBriefs.filter(b => {
                  const briefTags = Array.isArray(b.tags) ? b.tags : [];
                  return briefTags.some(tag => tag.toLowerCase().includes(searchLower));
                });
              }
              
              const statusBriefs = filteredBriefs;
              const statusLabels = {
                ideas: { title: '💡 Briefs', color: 'cyan' },
                inProgress: { title: '⏳ In Progress', color: 'yellow' },
                readyToPost: { title: '✅ Ready to Post', color: 'green' },
                posted: { title: '📮 Posted', color: 'zinc' }
              };
              const label = statusLabels[status];

              return (
                <div 
                  key={status} 
                  className={`${isDark ? 'bg-zinc-900/50 border-zinc-800' : 'bg-white border-gray-200'} rounded-xl border overflow-hidden transition-all ${
                    dragOverStatus === status ? 'ring-2 ring-cyan-500/50' : ''
                  }`}
                  onDragEnter={(e) => handleDragEnter(e, status)}
                  onDragLeave={handleDragLeave}
                  onDragOver={(e) => handleDragOver(e, status)}
                  onDrop={(e) => {
                    // Only handle drop on column if not dropping on a specific card
                    if (!dragOverBriefId) {
                      handleDrop(e, status);
                    }
                  }}
                >
                  <div className={`p-3 sm:p-4 border-b ${isDark ? 'border-zinc-800' : 'border-gray-200'} bg-${label.color}-500/10 transition-all ${
                    dragOverStatus === status ? 'bg-cyan-500/20' : ''
                  }`}>
                    <div className="flex items-center justify-between">
                      <h3 className={`text-sm sm:text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{label.title}</h3>
                      <span className={`${isDark ? 'text-zinc-500' : 'text-gray-500'} text-xs sm:text-sm`}>{statusBriefs.length}</span>
                    </div>
                  </div>
                  <div 
                    className={`p-2 sm:p-3 space-y-2 sm:space-y-3 min-h-[200px] sm:min-h-[400px] transition-all ${
                      dragOverStatus === status ? 'bg-cyan-500/10' : ''
                    }`}
                  >
                    {statusBriefs
                      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
                      .map(brief => (
                      <div
                        key={brief.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, brief)}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (draggedBrief && draggedBrief.id !== brief.id && draggedBrief.status === status) {
                            setDragOverBriefId(brief.id);
                          }
                        }}
                        onDragLeave={(e) => {
                          // Only clear if leaving this specific element
                          if (!e.currentTarget.contains(e.relatedTarget)) {
                            setDragOverBriefId(null);
                          }
                        }}
                        onDrop={(e) => {
                          if (draggedBrief && draggedBrief.status === status && draggedBrief.id !== brief.id) {
                            handleDrop(e, status, brief.id);
                          }
                        }}
                        className={`p-3 sm:p-4 ${isDark ? 'bg-zinc-800 hover:bg-zinc-750' : 'bg-white border border-gray-200 hover:bg-gray-50'} rounded-xl cursor-move hover:ring-1 hover:ring-cyan-500/30 transition-all relative group ${
                          draggedBrief?.id === brief.id ? 'opacity-50' : ''
                        } ${
                          dragOverBriefId === brief.id && draggedBrief?.status === status ? 'ring-2 ring-cyan-500/50 bg-cyan-500/10' : ''
                        }`}
                        onClick={(e) => {
                          // Prevent click if we just finished dragging
                          if (justDraggedRef.current || isDragging) {
                            e.preventDefault();
                            e.stopPropagation();
                            return;
                          }
                          setSelectedBrief(brief);
                          setBriefModalMode('review');
                          setShowBriefDetailModal(true);
                        }}
                      >
                        <button
                          onClick={(e) => handleDeleteBrief(brief.id, e)}
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-1.5 hover:bg-red-500/20 rounded-lg transition-opacity z-10"
                          title="Delete brief"
                        >
                          <Trash2 className="h-4 w-4 text-red-400" />
                        </button>
                        {brief.trend_id && (
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`w-4 h-4 ${isDark ? 'bg-zinc-700' : 'bg-gray-200'} rounded flex items-center justify-center`}>
                              <span className="text-[10px] text-white">🎬</span>
                            </div>
                            <span className={`${isDark ? 'text-zinc-400' : 'text-gray-600'} text-sm`}>
                              {trends.find(t => t.id === brief.trend_id)?.trend_name || 'Trend'}
                            </span>
                          </div>
                        )}
                        <p className={`font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{brief.angle}</p>
                        {brief.shots && brief.shots.length > 0 && (
                          <div className={`text-xs ${isDark ? 'text-zinc-500' : 'text-gray-600'} mb-2 space-y-0.5`}>
                            {brief.shots.slice(0, 2).map((shot, i) => (
                              <div key={i}>→ {shot}</div>
                            ))}
                            {brief.shots.length > 2 && (
                              <div className={isDark ? 'text-zinc-600' : 'text-gray-500'}>+{brief.shots.length - 2} more shots</div>
                            )}
                          </div>
                        )}
                        {brief.caption && (
                          <p className="text-xs text-cyan-400/70 truncate mb-2">"{brief.caption}"</p>
                        )}
                        {Array.isArray(brief.tags) && brief.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {brief.tags.slice(0, 3).map((tag, tagIdx) => (
                              <span
                                key={tagIdx}
                                className="px-2 py-0.5 bg-cyan-500/20 text-cyan-300 rounded text-xs"
                              >
                                {tag}
                              </span>
                            ))}
                            {brief.tags.length > 3 && (
                              <span className={`px-2 py-0.5 ${isDark ? 'bg-zinc-700 text-zinc-400' : 'bg-gray-200 text-gray-600'} rounded text-xs`}>
                                +{brief.tags.length - 3}
                              </span>
                            )}
                          </div>
                        )}
                        {brief.collaborators && brief.collaborators.length > 0 && (
                          <div className="flex items-center gap-1 mt-3">
                            {brief.collaborators.slice(0, 3).map((collab, i) => (
                              <div
                                key={i}
                                className={`w-6 h-6 ${isDark ? 'bg-zinc-700 border-zinc-800' : 'bg-gray-200 border-gray-300'} rounded-full flex items-center justify-center text-xs border-2`}
                                title={collab.user_id}
                              >
                                {collab.user_id.charAt(0).toUpperCase()}
                              </div>
                            ))}
                            {brief.collaborators.length > 3 && (
                              <div className={`w-6 h-6 ${isDark ? 'bg-zinc-700 border-zinc-800' : 'bg-gray-200 border-gray-300'} rounded-full flex items-center justify-center text-xs border-2`}>
                                +{brief.collaborators.length - 3}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                    {status === 'ideas' && (
                      <button
                        onClick={() => {
                          setSelectedBrief({ angle: '', shots: [], caption: '', notes: '', status: 'ideas', tags: [] });
                          setBriefModalMode('create');
                          setShowBriefModal(true);
                        }}
                        className={`w-full p-4 border-2 border-dashed ${isDark ? 'border-zinc-700' : 'border-gray-300'} rounded-xl text-center hover:border-cyan-500/50 hover:bg-cyan-500/5 transition-all`}
                      >
                        <span className={`${isDark ? 'text-zinc-500' : 'text-gray-500'} text-sm`}>+ Add brief</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Video Preview Modal - 4 Column Layout */}
      <VideoPreviewModal
        videoPreview={videoPreview}
        onClose={() => setVideoPreview(null)}
        formatNumber={formatNumber}
        onCreateBrief={videoPreview?.video?.id ? (video) => {
          setVideoPreview(null);
          const trendId = getVideoTrendId(video);
          if (trendId) runGenerateIdeas(trendId, { showLoadingToast: true });
          else runGenerateIdeasFromVideo(video);
        } : undefined}
        highlightTimestamps={(text) => {
          // Simple timestamp highlighting for TrendSpotter
          if (!text) return text;
          const timestampPattern = /(\*\*)?(\d+:\d+-\d+:\d+)(:\*\*)?/g;
          const parts = [];
          let lastIndex = 0;
          let keyCounter = 0;
          let match;

          while ((match = timestampPattern.exec(text)) !== null) {
            if (match.index > lastIndex) {
              parts.push(text.substring(lastIndex, match.index));
            }
            parts.push(
              <span key={`ts-${keyCounter++}`} className="font-semibold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                {match[2]}
              </span>
            );
            lastIndex = timestampPattern.lastIndex;
          }
          if (lastIndex < text.length) {
            parts.push(text.substring(lastIndex));
          }
          return parts.length > 0 ? parts : text;
        }}
      />

      {/* Brand Settings Modal */}
      {showBrandSettingsModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-2 sm:p-4">
          <div data-tour="brand-settings-modal" className={`rounded-2xl border w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'}`}>
            <div className={`p-4 sm:p-6 border-b flex items-center justify-between gap-2 ${isDark ? 'border-zinc-800' : 'border-gray-200'}`}>
              <div className="flex-1 min-w-0">
                <h2 className={`text-lg sm:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Brand Settings</h2>
                <p className={`text-xs sm:text-sm mt-1 ${isDark ? 'text-zinc-500' : 'text-gray-600'}`}>
                  Configure your brand details to personalize AI-generated content ideas
                </p>
                {brandSettingsModalSource === 'auto' && (
                  <p
                    className={`text-xs sm:text-sm mt-2 rounded-lg px-3 py-2 ${isDark ? 'bg-amber-500/15 text-amber-200 border border-amber-500/40' : 'bg-amber-50 text-amber-950 border border-amber-200'}`}
                  >
                    You&apos;ve signed in a few times without a complete brand profile. Add your brand name and description so briefs and ideas match your brand—you can close this for now, but it only takes a minute.
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => void closeBrandSettingsModal()}
                className={`p-2 rounded-lg flex-shrink-0 touch-manipulation ${isDark ? 'hover:bg-zinc-800 text-zinc-400' : 'hover:bg-gray-100 text-gray-600'}`}
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {/* Social Account Input */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-zinc-300' : 'text-gray-800'}`}>
                    Social Account (Optional)
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={brandSettings.social_platform || 'tiktok'}
                      onChange={(e) => setBrandSettings({ ...brandSettings, social_platform: e.target.value })}
                      disabled={populatingBrandSettings}
                      className={`px-3 py-2 border rounded-lg focus:outline-none focus:border-cyan-500 ${
                        populatingBrandSettings
                          ? `${isDark ? 'bg-zinc-700 border-zinc-600 text-zinc-400 cursor-not-allowed' : 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'}`
                          : `${isDark ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`
                      }`}
                    >
                      <option value="tiktok">TikTok</option>
                      <option value="instagram">Instagram</option>
                    </select>
                    <input
                      type="text"
                      value={brandSettings.social_handle || ''}
                      onChange={(e) => setBrandSettings({ ...brandSettings, social_handle: e.target.value })}
                      placeholder="e.g., @username or username"
                      disabled={populatingBrandSettings}
                      className={`flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:border-cyan-500 ${
                        populatingBrandSettings
                          ? `${isDark ? 'bg-zinc-700 border-zinc-600 text-zinc-400 placeholder-zinc-600 cursor-not-allowed' : 'bg-gray-100 border-gray-200 text-gray-400 placeholder-gray-300 cursor-not-allowed'}`
                          : `${isDark ? 'bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`
                      }`}
                    />
                    <button
                      onClick={async () => {
                        if (!brandSettings.social_handle?.trim()) {
                          toast.error('Please enter a social account handle');
                          return;
                        }
                        if (populatingBrandSettings) return;
                        
                        setPopulatingBrandSettings(true);
                        try {
                          const response = await fetch('/api/trend-spotter/brand-settings/populate', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              platform: brandSettings.social_platform || 'tiktok',
                              handle: brandSettings.social_handle.replace(/^@/, '').trim()
                            })
                          });
                          const result = await response.json();
                          if (result.success && result.suggestions) {
                            setBrandSettings({
                              ...brandSettings,
                              brand_name: result.suggestions.brand_name || brandSettings.brand_name,
                              brand_description: result.suggestions.brand_description || brandSettings.brand_description,
                              target_audience: result.suggestions.target_audience || brandSettings.target_audience,
                              content_themes: result.suggestions.content_themes || brandSettings.content_themes
                            });
                            toast.success('Brand settings populated from social account!');
                          } else {
                            toast.error(result.error || 'Failed to populate brand settings');
                          }
                        } catch (error) {
                          console.error('Error populating brand settings:', error);
                          toast.error('Failed to populate brand settings');
                        } finally {
                          setPopulatingBrandSettings(false);
                        }
                      }}
                      disabled={populatingBrandSettings}
                      className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${
                        populatingBrandSettings
                          ? 'bg-gray-400 cursor-not-allowed text-gray-600'
                          : 'bg-cyan-500 hover:bg-cyan-400 text-black'
                      }`}
                    >
                      {populatingBrandSettings ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Populating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4" />
                          Populate
                        </>
                      )}
                    </button>
                  </div>
                  <p className={`text-xs mt-1 ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>
                    Enter your TikTok or Instagram handle to auto-fill brand settings from your profile
                  </p>
                </div>

                {/* Brand Name */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-zinc-300' : 'text-gray-800'}`}>
                    Brand Name
                  </label>
                  <input
                    type="text"
                    value={brandSettings.brand_name}
                    onChange={(e) => setBrandSettings({ ...brandSettings, brand_name: e.target.value })}
                    placeholder="e.g., Frost Buddy"
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:border-cyan-500 ${isDark ? 'bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`}
                  />
                </div>

                {/* Brand Description */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-zinc-300' : 'text-gray-800'}`}>
                    Brand Description
                  </label>
                  <textarea
                    value={brandSettings.brand_description}
                    onChange={(e) => setBrandSettings({ ...brandSettings, brand_description: e.target.value })}
                    placeholder="Describe your brand, products, and what makes it unique..."
                    rows={4}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:border-cyan-500 resize-none ${isDark ? 'bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`}
                  />
                </div>

                {/* Target Audience */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-zinc-300' : 'text-gray-800'}`}>
                    Target Audience
                  </label>
                  <textarea
                    value={brandSettings.target_audience}
                    onChange={(e) => setBrandSettings({ ...brandSettings, target_audience: e.target.value })}
                    placeholder="Describe your target audience (demographics, interests, behaviors)..."
                    rows={3}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:border-cyan-500 resize-none ${isDark ? 'bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`}
                  />
                </div>

                {/* Content Themes/Style */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-zinc-300' : 'text-gray-800'}`}>
                    Content Themes / Style
                  </label>
                  <textarea
                    value={brandSettings.content_themes}
                    onChange={(e) => setBrandSettings({ ...brandSettings, content_themes: e.target.value })}
                    placeholder="Describe your content tone, style, themes, and messaging approach (e.g., Humor, self-aware, casual, relatable)..."
                    rows={3}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:border-cyan-500 resize-none ${isDark ? 'bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`}
                  />
                </div>
              </div>
            </div>
            
            <div className={`p-6 border-t flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${isDark ? 'border-zinc-800' : 'border-gray-200'}`}>
              <p className={`text-xs order-2 sm:order-1 ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>
                Changes save automatically while you type.
              </p>
              <div className="flex justify-end gap-3 order-1 sm:order-2">
                <button
                  type="button"
                  onClick={() => void closeBrandSettingsModal()}
                  className={`px-4 py-2 rounded-lg ${isDark ? 'bg-zinc-800 hover:bg-zinc-700 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
                >
                  {brandSettingsModalSource === 'auto' ? 'Close for now' : 'Cancel'}
                </button>
                <button
                  type="button"
                  onClick={() => void handleBrandSettingsDone()}
                  className="px-6 py-2 bg-cyan-500 hover:bg-cyan-400 rounded-lg font-medium flex items-center gap-2 text-black"
                >
                  <CheckCircle className="h-5 w-5" />
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showManualAddModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 sm:p-6">
          <div className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'} rounded-2xl border w-full max-w-lg max-h-[90vh] flex flex-col`}>
            <div className={`p-4 sm:p-6 border-b ${isDark ? 'border-zinc-800' : 'border-gray-200'} flex-shrink-0`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h2 className={`text-lg sm:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Add Videos Manually</h2>
                  <p className={`${isDark ? 'text-zinc-500' : 'text-gray-500'} text-xs sm:text-sm mt-1`}>Paste TikTok or Instagram video links (separated by tabs, newlines, or spaces)</p>
                </div>
                <button
                  onClick={() => { setShowManualAddModal(false); setManualLinks(''); }}
                  className={`p-2 ${isDark ? 'hover:bg-zinc-800' : 'hover:bg-gray-100'} rounded-lg flex-shrink-0 touch-manipulation`}
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="p-4 sm:p-6 overflow-y-auto flex-1">
              <textarea
                value={manualLinks}
                onChange={(e) => setManualLinks(e.target.value)}
                placeholder="https://www.tiktok.com/@user/video/123...&#10;https://www.instagram.com/reel/ABC...&#10;&#10;Or paste tab-separated URLs"
                rows={10}
                className={`w-full px-4 py-3 ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-300'} border rounded-xl focus:outline-none focus:border-cyan-500 resize-none font-mono text-xs sm:text-sm`}
              />
            </div>
            <div className={`p-4 sm:p-6 border-t ${isDark ? 'border-zinc-800' : 'border-gray-200'} flex gap-3 flex-shrink-0`}>
              <button
                onClick={() => { setShowManualAddModal(false); setManualLinks(''); }}
                className={`flex-1 px-4 py-3 ${isDark ? 'bg-zinc-800 hover:bg-zinc-700' : 'bg-gray-100 hover:bg-gray-200'} rounded-xl text-sm sm:text-base ${isDark ? 'text-white' : 'text-gray-900'}`}
              >
                Cancel
              </button>
              <button
                onClick={handleManualAdd}
                disabled={isLoading || !manualLinks.trim()}
                className={`flex-1 px-4 py-3 bg-cyan-500 hover:bg-cyan-400 ${isDark ? 'disabled:bg-zinc-700 disabled:text-zinc-500' : 'disabled:bg-gray-300 disabled:text-gray-500'} rounded-xl font-medium text-sm sm:text-base`}
              >
                {isLoading ? 'Adding...' : 'Add Videos'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Status Modal */}
      {showSubscriptionModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className={`rounded-2xl border w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'}`}>
            <div className={`p-4 sm:p-6 border-b flex items-center justify-between gap-2 ${isDark ? 'border-zinc-800' : 'border-gray-200'}`}>
              <div className="flex-1 min-w-0">
                <h2 className={`text-lg sm:text-xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <CreditCard className="h-5 w-5" />
                  Subscription Status
                </h2>
                <p className={`text-xs sm:text-sm mt-1 ${isDark ? 'text-zinc-500' : 'text-gray-600'}`}>
                  View your payment and subscription information
                </p>
              </div>
              <button
                onClick={() => setShowSubscriptionModal(false)}
                className={`p-2 rounded-lg flex-shrink-0 touch-manipulation ${isDark ? 'hover:bg-zinc-800 text-zinc-400' : 'hover:bg-gray-100 text-gray-600'}`}
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              {loadingSubscriptionStatus ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-cyan-500" />
                </div>
              ) : subscriptionStatus ? (
                <div className="space-y-6">
                  {/* Payment Status */}
                  <div className={`rounded-lg p-4 border ${isDark ? 'bg-zinc-800/50 border-zinc-700' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className={`text-sm font-semibold ${isDark ? 'text-zinc-300' : 'text-gray-800'}`}>Payment Status</h3>
                      {subscriptionStatus.hasPaid ? (
                        <span className={`px-3 py-1 bg-green-500/20 rounded-full text-xs font-medium flex items-center gap-1 ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                          <CheckCircle className="h-3 w-3" />
                          {subscriptionStatus.isDesignerPartner ? 'Design Partner' : subscriptionStatus.isWhitelisted ? 'Unlimited Access' : 'Active'}
                        </span>
                      ) : subscriptionStatus.gracePeriodActive ? (
                        <span className={`px-3 py-1 bg-yellow-500/20 rounded-full text-xs font-medium flex items-center gap-1 ${isDark ? 'text-yellow-400' : 'text-yellow-700'}`}>
                          <AlertCircle className="h-3 w-3" />
                          Grace Period
                        </span>
                      ) : (
                        <span className={`px-3 py-1 bg-red-500/20 rounded-full text-xs font-medium flex items-center gap-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                          <AlertCircle className="h-3 w-3" />
                          Inactive
                        </span>
                      )}
                    </div>
                    {subscriptionStatus.isWhitelisted && (
                      <p className={`text-xs mt-2 ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>
                        You have unlimited access to all features.
                      </p>
                    )}
                    {subscriptionStatus.isDesignerPartner && (
                      <p className={`text-xs mt-2 ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>
                        You have unlimited access as a Design Partner.
                      </p>
                    )}
                  </div>

                  {/* Designer Partner Benefits */}
                  {subscriptionStatus.isDesignerPartner && subscriptionStatus.designerPartnerBenefitsUntil && (
                    <div className="bg-purple-500/10 rounded-lg p-4 border border-purple-500/20">
                      <div className="flex items-center gap-2 mb-3">
                        <Sparkles className={`h-4 w-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                        <h3 className={`text-sm font-semibold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>Design Partner Benefits</h3>
                      </div>
                      <p className={`text-sm mb-3 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
                        You have unlimited access to all features including unlimited video uploads and idea generation.
                      </p>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className={isDark ? 'text-zinc-400' : 'text-gray-600'}>Benefits Until:</span>
                          <span className={`font-medium ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                            {new Date(subscriptionStatus.designerPartnerBenefitsUntil).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className={isDark ? 'text-zinc-400' : 'text-gray-600'}>Days Remaining:</span>
                          <span className={`font-medium ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                            {Math.ceil((new Date(subscriptionStatus.designerPartnerBenefitsUntil) - new Date()) / (1000 * 60 * 60 * 24))} days
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Grace Period */}
                  {subscriptionStatus.gracePeriodActive && (
                    <div className="bg-yellow-500/10 rounded-lg p-4 border border-yellow-500/20">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className={`h-4 w-4 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`} />
                        <h3 className={`text-sm font-semibold ${isDark ? 'text-yellow-400' : 'text-yellow-700'}`}>Grace Period Active</h3>
                      </div>
                      <p className={`text-sm mb-3 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
                        Your subscription has been cancelled, but you still have access until your next billing date.
                      </p>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className={isDark ? 'text-zinc-400' : 'text-gray-600'}>Days Remaining:</span>
                          <span className={`font-medium ${isDark ? 'text-yellow-400' : 'text-yellow-700'}`}>
                            {subscriptionStatus.gracePeriodDays} {subscriptionStatus.gracePeriodDays === 1 ? 'day' : 'days'}
                          </span>
                        </div>
                        {subscriptionStatus.nextBillingDate && (
                          <div className="flex justify-between">
                            <span className={isDark ? 'text-zinc-400' : 'text-gray-600'}>Access Until:</span>
                            <span className={isDark ? 'text-zinc-300' : 'text-gray-800'}>
                              {new Date(subscriptionStatus.nextBillingDate).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Next Billing Date - Only show for active paid subscriptions (not cancelled / not grace period) */}
                  {subscriptionStatus.hasPaid && subscriptionStatus.nextBillingDate && !subscriptionStatus.gracePeriodActive && !subscriptionStatus.cancelAtPeriodEnd && !subscriptionStatus.isWhitelisted && (
                    <div className={`rounded-lg p-4 border ${isDark ? 'bg-zinc-800/50 border-zinc-700' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <Calendar className={`h-4 w-4 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />
                        <h3 className={`text-sm font-semibold ${isDark ? 'text-zinc-300' : 'text-gray-800'}`}>Next Billing Date</h3>
                      </div>
                      <p className={`text-sm mb-4 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
                        {new Date(subscriptionStatus.nextBillingDate).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                      
                      {/* Cancel Subscription Button */}
                      <button
                        onClick={async () => {
                          if (!window.confirm('Are you sure you want to cancel your subscription? You will retain access until your next billing date.')) {
                            return;
                          }

                          setCancellingSubscription(true);
                          try {
                            const response = await fetch('/api/payment/cancel-subscription', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              credentials: 'include'
                            });

                            const result = await response.json();
                            if (result.success) {
                              toast.success('Subscription cancelled. You will retain access until your next billing date.');
                              // Wait a moment for webhook to process, then reload subscription status
                              await new Promise(resolve => setTimeout(resolve, 1000));
                              // Force reload subscription status (bypass cache)
                              await loadSubscriptionStatus();
                              // Reload again after a short delay to ensure webhook has processed
                              setTimeout(async () => {
                                await loadSubscriptionStatus();
                                await checkUserLimits();
                              }, 2000);
                            } else {
                              toast.error(result.error || 'Failed to cancel subscription');
                            }
                          } catch (error) {
                            console.error('Error cancelling subscription:', error);
                            toast.error('Failed to cancel subscription');
                          } finally {
                            setCancellingSubscription(false);
                          }
                        }}
                        disabled={cancellingSubscription}
                        className={`w-full px-4 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/50 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm font-medium ${isDark ? 'disabled:bg-zinc-700 disabled:text-zinc-500 text-red-400' : 'disabled:bg-gray-300 disabled:text-gray-500 text-red-600'}`}
                      >
                        {cancellingSubscription ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Cancelling...</span>
                          </>
                        ) : (
                          <>
                            <X className="h-4 w-4" />
                            <span>Cancel Subscription</span>
                          </>
                        )}
                      </button>
                      <p className={`text-xs text-center mt-2 ${isDark ? 'text-zinc-500' : 'text-gray-600'}`}>
                        You will retain access until {new Date(subscriptionStatus.nextBillingDate).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  )}


                  {/* Payment Options - Show if user hasn't paid (including grace period) */}
                  {!subscriptionStatus.hasPaid && (
                    <div className={`rounded-lg p-4 border mt-6 ${isDark ? 'bg-zinc-800/50 border-zinc-700' : 'bg-gray-50 border-gray-200'}`}>
                      <h3 className={`text-sm font-semibold mb-4 ${isDark ? 'text-zinc-300' : 'text-gray-800'}`}>
                        {subscriptionStatus.gracePeriodActive ? 'Renew Subscription' : 'Upgrade to Continue'}
                      </h3>
                      {subscriptionStatus.gracePeriodActive && (
                        <p className={`text-xs mb-4 ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>
                          Your subscription will expire in {subscriptionStatus.gracePeriodDays} {subscriptionStatus.gracePeriodDays === 1 ? 'day' : 'days'}. Renew now to continue your access.
                        </p>
                      )}
                      
                      {/* Programmatic Checkout Button */}
                      <div className="mb-2">
                        <button
                          onClick={handleSubscribe}
                          disabled={creatingCheckout || !user}
                          className={`w-full px-6 py-3 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 ${isDark ? 'disabled:bg-zinc-700 disabled:text-zinc-500' : 'disabled:bg-gray-300 disabled:text-gray-500'}`}
                        >
                          {creatingCheckout ? (
                            <>
                              <Loader2 className="h-5 w-5 animate-spin" />
                              <span>Loading...</span>
                            </>
                          ) : (
                            <>
                              <CreditCard className="h-5 w-5" />
                              <span>Subscribe Now</span>
                            </>
                          )}
                        </button>
                      </div>
                      <p className={`text-sm text-center mt-3 mb-2 ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>
                        New users get 7 days of unlimited ideas. After that, free users can upload up to 30 videos in the library and create up to 5 ideas per day.
                      </p>
                      <p className={`text-sm text-center mb-4 ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>
                        Subscribe to unlock unlimited ideas and full access.
                      </p>
                      <p className={`text-xs text-center mt-3 ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>
                        Your payment will be processed automatically. The subscription status will update shortly after payment completion.
                      </p>
                      <p className={`text-xs text-center mt-2 ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>
                        Your ShowStop account email ({user?.email}) will be pre-filled in the checkout form.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <AlertCircle className={`h-12 w-12 mx-auto mb-4 ${isDark ? 'text-zinc-500' : 'text-gray-400'}`} />
                  <p className={isDark ? 'text-zinc-400' : 'text-gray-600'}>Failed to load subscription status</p>
                </div>
              )}
            </div>
            
            <div className={`p-6 border-t flex justify-end gap-3 ${isDark ? 'border-zinc-800' : 'border-gray-200'}`}>
              <button
                onClick={() => setShowSubscriptionModal(false)}
                className={`px-6 py-2 rounded-lg font-medium ${isDark ? 'bg-zinc-800 hover:bg-zinc-700 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showSyncModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'} rounded-2xl border w-full max-w-md`}>
            <div className={`p-4 sm:p-6 border-b ${isDark ? 'border-zinc-800' : 'border-gray-200'} flex items-center justify-between gap-2`}>
              <div className="flex-1 min-w-0">
                <h2 className={`text-lg sm:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Sync Bookmarks</h2>
                <p className={`${isDark ? 'text-zinc-500' : 'text-gray-500'} text-xs sm:text-sm mt-1`}>Use the Chrome extension to sync your saved videos</p>
              </div>
              <button
                onClick={() => setShowSyncModal(false)}
                className={`p-2 ${isDark ? 'hover:bg-zinc-800' : 'hover:bg-gray-100'} rounded-lg flex-shrink-0 touch-manipulation`}
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              <button
                onClick={handleSyncBookmarks}
                className="w-full px-4 py-3 bg-cyan-500 hover:bg-cyan-400 rounded-xl font-medium"
              >
                Open Chrome Extension
              </button>
            </div>
            <div className={`p-6 border-t ${isDark ? 'border-zinc-800' : 'border-gray-200'}`}>
              <button
                onClick={() => setShowSyncModal(false)}
                className={`w-full px-4 py-3 ${isDark ? 'bg-zinc-800 hover:bg-zinc-700' : 'bg-gray-100 hover:bg-gray-200'} rounded-xl ${isDark ? 'text-white' : 'text-gray-900'}`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Idea limit upgrade popup — only when user clicks New Brief and is over limit */}
      {showIdeaLimitModal && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowIdeaLimitModal(false)} aria-hidden="true" />
          <div className={`relative rounded-2xl border w-full max-w-md p-5 sm:p-6 ${isDark ? 'bg-zinc-900 border-zinc-700' : 'bg-white border-gray-200'} shadow-xl`}>
            <button
              onClick={() => setShowIdeaLimitModal(false)}
              className={`absolute top-3 right-3 p-1.5 rounded-lg ${isDark ? 'text-zinc-400 hover:bg-zinc-800' : 'text-gray-500 hover:bg-gray-100'}`}
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
            <p className={`text-sm pr-8 ${isDark ? 'text-zinc-200' : 'text-gray-800'}`}>
              You&apos;ve used all 5 of today&apos;s briefs! Unlock unlimited briefs for $30/month — cancel anytime, no questions asked. Or wait until tomorrow for your limit to reset.
            </p>
            <button
              onClick={() => {
                const returnPath = encodeURIComponent(window.location.pathname + window.location.search);
                window.location.href = `/payment?reason=idea_limit&return=${returnPath}`;
              }}
              className="mt-4 w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg text-base transition-colors"
            >
              Unlock unlimited — $30/month
            </button>
          </div>
        </div>
      )}

      {showBriefModal && selectedBrief && (
        <div className={`fixed inset-0 flex items-center justify-center z-50 p-2 sm:p-4 ${isDark ? 'bg-black/80' : 'bg-gray-900/50'}`}>
          <div className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'} rounded-2xl border w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] flex flex-col shadow-xl`}>
            <div className={`p-4 sm:p-6 border-b ${isDark ? 'border-zinc-800' : 'border-gray-200'} flex-shrink-0`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h2 className={`text-lg sm:text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>New Content Brief</h2>
                  <p className={`${isDark ? 'text-zinc-400' : 'text-gray-500'} text-xs sm:text-sm`}>Add a new brief to your content board</p>
                </div>
                <button
                  onClick={() => {
                    setShowBriefModal(false);
                    setSelectedBrief(null);
                    setShareEmail('');
                    setShareError('');
                    setTagInput('');
                  }}
                  className={`p-2 ${isDark ? 'hover:bg-zinc-800' : 'hover:bg-gray-100'} rounded-lg flex-shrink-0 touch-manipulation`}
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1">
              {/* Link to Trend */}
              <div>
                <label className={`${isDark ? 'text-zinc-300' : 'text-gray-700'} text-sm mb-2 block`}>Link to Trend (optional)</label>
                <select
                  value={selectedBrief.trend_id || ''}
                  onChange={(e) => setSelectedBrief({ ...selectedBrief, trend_id: e.target.value ? parseInt(e.target.value) : null })}
                  className={`w-full px-4 py-3 ${isDark ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300 text-black'} border rounded-xl focus:outline-none focus:border-cyan-500 text-sm`}
                >
                  <option value="">No trend</option>
                  {trends.filter(t => t.status === 'approved' || t.status === 'pending').map(trend => {
                    const maxLength = 50;
                    const displayName = trend.trend_name.length > maxLength 
                      ? trend.trend_name.substring(0, maxLength) + '...' 
                      : trend.trend_name;
                    return (
                      <option key={trend.id} value={trend.id} title={trend.trend_name}>
                        {displayName}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Select Videos from Library */}
              <div>
                <label className={`${isDark ? 'text-zinc-300' : 'text-gray-700'} text-sm mb-2 block`}>Select Videos from Library (optional)</label>
                <button
                  type="button"
                  onClick={() => setShowBriefVideoSelectionModal(true)}
                  className={`w-full px-4 py-3 ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-300'} border rounded-xl hover:border-cyan-500 focus:outline-none focus:border-cyan-500 text-sm flex items-center justify-between`}
                >
                  <span className={isDark ? 'text-zinc-300' : 'text-gray-700'}>
                    {Array.isArray(selectedBrief.video_ids) && selectedBrief.video_ids.length > 0
                      ? `${selectedBrief.video_ids.length} video${selectedBrief.video_ids.length !== 1 ? 's' : ''} selected`
                      : 'Click to browse and select videos'}
                  </span>
                  <span className="text-cyan-400">Browse →</span>
                </button>
                
                {/* Selected Videos Preview */}
                {Array.isArray(selectedBrief.video_ids) && selectedBrief.video_ids.length > 0 && (() => {
                  const selectedVideos = libraryVideos.filter(v => selectedBrief.video_ids.includes(v.id));
                  if (selectedVideos.length === 0) return null;
                  
                  return (
                    <div className="mt-3">
                      <div className="flex gap-3 overflow-x-auto pb-2">
                        {selectedVideos.map((video) => {
                          // Get video URL
                          let videoUrl = video.video_url;
                          if (!videoUrl && video.apify_metadata) {
                            const meta = typeof video.apify_metadata === 'string' 
                              ? JSON.parse(video.apify_metadata) 
                              : video.apify_metadata;
                            videoUrl = meta.videoUrl || meta.video_url || meta.mediaUrls?.[0] || meta.webVideoUrl;
                          }
                          
                          return (
                            <div
                              key={video.id}
                              className={`relative flex-shrink-0 w-24 rounded-lg overflow-hidden border-2 border-cyan-500/50 ${isDark ? 'bg-zinc-800' : 'bg-gray-100'} group cursor-pointer`}
                              onClick={() => {
                                if (videoUrl) {
                                  setVideoPreview({
                                    videoUrl: videoUrl,
                                    creator: getCreatorUsername(video),
                                    platform: video.platform || 'unknown',
                                    views: getApifyValue(video, 'views'),
                                    likes: getApifyValue(video, 'likes'),
                                    video: video // Include full video object for metadata
                                  });
                                }
                              }}
                            >
                              {videoUrl ? (
                                <video
                                  src={videoUrl}
                                  className="w-full aspect-[9/16] object-cover"
                                  muted
                                  loop
                                  playsInline
                                  onMouseEnter={(e) => {
                                    const videoEl = e.target;
                                    if (videoEl.paused) {
                                      const playPromise = videoEl.play();
                                      if (playPromise !== undefined) {
                                        playPromise.catch(() => {
                                          // Ignore play errors
                                        });
                                      }
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    const videoEl = e.target;
                                    if (!videoEl.paused) {
                                      const pausePromise = videoEl.pause();
                                      if (pausePromise !== undefined) {
                                        pausePromise.catch(() => {
                                          // Ignore pause errors
                                        });
                                      }
                                    }
                                    e.target.currentTime = 0;
                                  }}
                                />
                              ) : (
                                <div className={`w-full aspect-[9/16] ${isDark ? 'bg-zinc-700' : 'bg-gray-200'} flex items-center justify-center`}>
                                  <Video className={`h-6 w-6 ${isDark ? 'text-zinc-500' : 'text-gray-400'}`} />
                                </div>
                              )}
                              
                              {/* Remove button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const updatedVideoIds = selectedBrief.video_ids.filter(id => id !== video.id);
                                  setSelectedBrief({ ...selectedBrief, video_ids: updatedVideoIds });
                                }}
                                className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 bg-red-500 hover:bg-red-400 rounded-full p-1 transition-opacity"
                                title="Remove video"
                              >
                                <X className="h-3 w-3 text-white" />
                              </button>
                              
                              {/* Video info overlay */}
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1">
                                <p className="text-[10px] text-white truncate">
                                  {video.apify_author_username || 'Unknown'}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Angle / Concept */}
              <div>
                <label className={`${isDark ? 'text-zinc-300' : 'text-gray-700'} text-sm mb-2 block`}>
                  Angle / Concept <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={selectedBrief.angle || ''}
                  onChange={(e) => setSelectedBrief({ ...selectedBrief, angle: e.target.value })}
                  placeholder="e.g., Making your whole personality the brand"
                  className={`w-full px-4 py-3 ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-300'} border rounded-xl focus:outline-none focus:border-cyan-500 text-sm`}
                />
              </div>

              {/* Shot List */}
              <div>
                <label className={`${isDark ? 'text-zinc-300' : 'text-gray-700'} text-sm mb-2 block`}>Shot List</label>
                <div className="space-y-2">
                  {Array.isArray(selectedBrief.shots) && selectedBrief.shots.length > 0 ? (
                    selectedBrief.shots.map((shot, shotIdx) => (
                      <div key={shotIdx} className="flex items-start gap-2">
                        <span className={`w-6 h-6 ${isDark ? 'bg-zinc-700' : 'bg-gray-200'} rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 mt-1`}>{shotIdx + 1}</span>
                        <input
                          type="text"
                          value={shot}
                          onChange={(e) => {
                            const updatedShots = [...selectedBrief.shots];
                            updatedShots[shotIdx] = e.target.value;
                            setSelectedBrief({ ...selectedBrief, shots: updatedShots });
                          }}
                          className={`flex-1 px-3 py-2 ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-300'} border rounded-lg text-sm focus:outline-none focus:border-cyan-500`}
                          placeholder={`Shot ${shotIdx + 1}`}
                        />
                        <button
                          onClick={() => {
                            const updatedShots = selectedBrief.shots.filter((_, idx) => idx !== shotIdx);
                            setSelectedBrief({ ...selectedBrief, shots: updatedShots });
                          }}
                          className="p-2 text-red-400 hover:text-red-300 mt-1"
                          title="Remove shot"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))
                  ) : (
                    <p className={`${isDark ? 'text-zinc-500' : 'text-gray-500'} text-sm`}>No shots added yet</p>
                  )}
                  <button
                    onClick={() => {
                      const updatedShots = Array.isArray(selectedBrief.shots) ? [...selectedBrief.shots, ''] : [''];
                      setSelectedBrief({ ...selectedBrief, shots: updatedShots });
                    }}
                    className={`w-full px-3 py-2 ${isDark ? 'bg-zinc-800 border-zinc-600 text-zinc-400' : 'bg-white border-gray-300 text-gray-600'} border border-dashed hover:border-cyan-500 rounded-lg text-sm hover:text-cyan-400 flex items-center justify-center gap-2`}
                  >
                    <Plus className="h-4 w-4" />
                    Add Shot
                  </button>
                </div>
              </div>

              {/* Caption */}
              <div>
                <label className={`${isDark ? 'text-zinc-300' : 'text-gray-700'} text-sm mb-2 block`}>Caption</label>
                <input
                  type="text"
                  value={selectedBrief.caption || ''}
                  onChange={(e) => setSelectedBrief({ ...selectedBrief, caption: e.target.value })}
                  placeholder="POV: Your caption here 📸"
                  className={`w-full px-4 py-3 ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-300'} border rounded-xl focus:outline-none focus:border-cyan-500 text-sm`}
                />
              </div>

              {/* Sound Link */}
              <div>
                <label className={`${isDark ? 'text-zinc-300' : 'text-gray-700'} text-sm mb-2 block`}>Audio Track (optional)</label>
                <input
                  type="text"
                  value={selectedBrief.sound_link || ''}
                  onChange={(e) => setSelectedBrief({ ...selectedBrief, sound_link: e.target.value })}
                  placeholder="Enter song name/keyword (e.g., 'Ordinary') or paste TikTok/Instagram sound link"
                  className={`w-full px-4 py-3 ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-300'} border rounded-xl focus:outline-none focus:border-cyan-500 text-sm`}
                />
                <p className={`${isDark ? 'text-zinc-500' : 'text-gray-500'} text-xs mt-1`}>Enter a song name/keyword or link to the sound track from TikTok or Instagram</p>
                {(() => {
                  const trendId = selectedBrief.trend_id ? parseInt(selectedBrief.trend_id) : null;
                  if (!trendId) return null;
                  
                  const popularSounds = getTrendPopularSounds(trendId);
                  if (popularSounds.length === 0) return null;
                  
                  return (
                    <div className="mt-3 space-y-2">
                      <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>Popular sounds from this trend:</p>
                      <div className="space-y-1.5 max-h-48 overflow-y-auto">
                        {popularSounds.map((sound, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => {
                              setSelectedBrief({ ...selectedBrief, sound_link: sound.url });
                            }}
                            className={`w-full text-left px-3 py-2 ${isDark ? 'bg-zinc-800 hover:bg-zinc-700 border-zinc-700' : 'bg-white hover:bg-gray-50 border-gray-300'} rounded-lg border transition-colors ${
                              selectedBrief.sound_link === sound.url
                                ? 'border-cyan-500 bg-cyan-500/10'
                                : ''
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <span className="text-xs">
                                  {sound.platform === 'tiktok' ? '🎵' : '🎵'}
                                </span>
                                <span className={`text-xs ${isDark ? 'text-zinc-300' : 'text-gray-700'} truncate`}>
                                  {sound.platform === 'tiktok' ? 'TikTok' : 'Instagram'} Sound
                                </span>
                              </div>
                              <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-gray-500'} ml-2`}>
                                {sound.count} video{sound.count !== 1 ? 's' : ''}
                              </span>
                            </div>
                            <div className="mt-1">
                              <a
                                href={sound.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-xs text-cyan-400 hover:text-cyan-300 underline truncate block"
                              >
                                {sound.url}
                              </a>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Tags */}
              <div>
                <label className={`${isDark ? 'text-zinc-300' : 'text-gray-700'} text-sm mb-2 block`}>Tags (optional)</label>
                <p className={`${isDark ? 'text-zinc-500' : 'text-gray-500'} text-xs mb-2`}>Add tags like "Holiday", "Event: XYZ", "Seasonal" to organize your briefs</p>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(() => {
                    const tags = Array.isArray(selectedBrief.tags) ? selectedBrief.tags : (selectedBrief.tags ? [selectedBrief.tags] : []);
                    return tags.length > 0 ? (
                      tags.map((tag, tagIdx) => (
                      <div
                        key={tagIdx}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-500/20 text-cyan-300 rounded-lg text-sm"
                      >
                        <span>{tag}</span>
                        <button
                          onClick={() => {
                            const currentTags = Array.isArray(selectedBrief.tags) ? selectedBrief.tags : [];
                            const updatedTags = currentTags.filter((_, i) => i !== tagIdx);
                            setSelectedBrief({ ...selectedBrief, tags: updatedTags });
                          }}
                          className="hover:text-red-400 transition-colors"
                          type="button"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))
                    ) : null;
                  })()}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => { setTagInput(e.target.value); setShareError(''); }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && tagInput.trim()) {
                        e.preventDefault();
                        handleAddTag();
                      }
                    }}
                    placeholder="Type tag and press Enter or click Add"
                    className={`flex-1 px-3 py-2 ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-300'} border rounded-lg text-sm focus:outline-none focus:border-cyan-500`}
                  />
                  <button
                    type="button"
                    onClick={handleAddTag}
                    disabled={!tagInput.trim()}
                    className={`px-4 py-2 bg-cyan-500 hover:bg-cyan-400 ${isDark ? 'disabled:bg-zinc-700 disabled:text-zinc-500' : 'disabled:bg-gray-300 disabled:text-gray-500'} disabled:cursor-not-allowed rounded-lg text-sm font-medium transition-colors`}
                  >
                    Add
                  </button>
                </div>
                {shareError && shareError.includes('Tag') && <p className="text-red-400 text-xs mt-1">{shareError}</p>}
              </div>

              {/* Share with Team */}
              <div>
                <label className={`${isDark ? 'text-zinc-300' : 'text-gray-700'} text-sm mb-2 block`}>Share with Team (optional)</label>
                <div className="space-y-2">
                  {/* Collaborator list */}
                  {selectedBrief.collaborators && selectedBrief.collaborators.length > 0 && (
                    <div className="space-y-2 mb-2">
                      {selectedBrief.collaborators.map((collab, idx) => (
                        <div key={idx} className={`flex items-center gap-2 p-2 ${isDark ? 'bg-zinc-800' : 'bg-gray-50'} rounded-lg`}>
                          <div className="flex-1 min-w-0">
                            <div className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{collab.user_id || collab.email}</div>
                            <div className={`text-xs ${isDark ? 'text-zinc-500' : 'text-gray-500'} capitalize`}>{collab.permission || 'view'}</div>
                          </div>
                          <select
                            value={collab.permission || 'view'}
                            onChange={(e) => {
                              const updated = [...selectedBrief.collaborators];
                              updated[idx] = { ...collab, permission: e.target.value };
                              setSelectedBrief({ ...selectedBrief, collaborators: updated });
                            }}
                            className={`text-xs ${isDark ? 'bg-zinc-700' : 'bg-white border-gray-300'} border rounded px-2 py-1 focus:outline-none`}
                          >
                            <option value="view">Can view</option>
                            <option value="comment">Can comment</option>
                            <option value="edit">Can edit</option>
                          </select>
                          <button
                            onClick={() => {
                              const updated = selectedBrief.collaborators.filter((_, i) => i !== idx);
                              setSelectedBrief({ ...selectedBrief, collaborators: updated });
                            }}
                            className={`p-1 ${isDark ? 'hover:bg-zinc-700 text-zinc-500 hover:text-red-400' : 'hover:bg-gray-200 text-gray-500 hover:text-red-600'} rounded`}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Add collaborator */}
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={shareEmail}
                      onChange={(e) => { setShareEmail(e.target.value); setShareError(''); }}
                      placeholder="Email address"
                      className={`flex-1 px-3 py-2 ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-300'} border rounded-lg text-sm focus:outline-none focus:border-cyan-500`}
                    />
                    <select
                      value={sharePermission}
                      onChange={(e) => setSharePermission(e.target.value)}
                      className={`px-3 py-2 ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-300'} border rounded-lg text-sm focus:outline-none focus:border-cyan-500`}
                    >
                      <option value="view">Can view</option>
                      <option value="comment">Can comment</option>
                      <option value="edit">Can edit</option>
                    </select>
                    <button
                      onClick={() => {
                        if (!shareEmail.trim()) {
                          setShareError('Please enter an email address');
                          return;
                        }
                        const email = shareEmail.trim().toLowerCase();
                        if (email === userId?.toLowerCase()) {
                          setShareError('Cannot add yourself as a collaborator');
                          return;
                        }
                        const existing = selectedBrief.collaborators || [];
                        if (existing.some(c => (c.user_id || c.email)?.toLowerCase() === email)) {
                          setShareError('User already added');
                          return;
                        }
                        setSelectedBrief({
                          ...selectedBrief,
                          collaborators: [...existing, { user_id: email, email, permission: sharePermission }]
                        });
                        setShareEmail('');
                        setShareError('');
                      }}
                      className={`px-4 py-2 ${isDark ? 'bg-zinc-700 hover:bg-zinc-600' : 'bg-gray-200 hover:bg-gray-300'} rounded-lg text-sm transition-colors ${isDark ? 'text-white' : 'text-gray-900'}`}
                    >
                      Add
                    </button>
                  </div>
                  {shareError && <p className="text-red-400 text-xs">{shareError}</p>}
                </div>
              </div>
            </div>
            <div className={`p-4 sm:p-6 border-t ${isDark ? 'border-zinc-800' : 'border-gray-200'} flex gap-3 flex-shrink-0`}>
              <button
                onClick={() => {
                  setShowBriefModal(false);
                  setSelectedBrief(null);
                  setShareEmail('');
                  setShareError('');
                  setTagInput('');
                }}
                className={`flex-1 px-4 py-3 ${isDark ? 'bg-zinc-800 hover:bg-zinc-700' : 'bg-gray-100 hover:bg-gray-200'} rounded-xl ${isDark ? 'text-white' : 'text-gray-900'}`}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!selectedBrief.angle || !selectedBrief.angle.trim()) {
                    toast.error('Angle / Concept is required');
                    return;
                  }
                  handleSaveBrief(selectedBrief);
                }}
                className="flex-1 px-4 py-3 bg-cyan-500 hover:bg-cyan-400 rounded-xl font-medium"
              >
                Add to Board
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Brief Detail/Review Modal - for viewing and commenting */}
      {showBriefDetailModal && selectedBrief && (
        <div className={`fixed inset-0 flex items-center justify-center z-50 p-2 sm:p-4 ${isDark ? 'bg-black/80' : 'bg-gray-900/50'}`}>
          <div className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'} rounded-2xl border w-full max-w-6xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col shadow-xl`}>
            <div className={`p-4 sm:p-6 border-b ${isDark ? 'border-zinc-800' : 'border-gray-200'} flex items-start justify-between flex-shrink-0`}>
              <div className="flex-1">
                {(() => {
                  const trendId = selectedBrief.trend_id ? parseInt(selectedBrief.trend_id) : null;
                  const associatedTrend = trendId ? trends.find(t => t.id === trendId) : null;
                  return associatedTrend ? (
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-4 h-4 ${isDark ? 'bg-zinc-700' : 'bg-gray-200'} rounded flex items-center justify-center`}>
                        <span className="text-[10px] text-white">🎬</span>
                      </div>
                      <span className={`${isDark ? 'text-zinc-400' : 'text-gray-600'} text-sm`}>
                        {associatedTrend.trend_name}
                      </span>
                    </div>
                  ) : null;
                })()}
                {(() => {
                  const userPermission = getUserPermission(selectedBrief);
                  if (isEditingAngle) {
                    return (
                      <input
                        type="text"
                        value={selectedBrief.angle || ''}
                        onChange={(e) => setSelectedBrief({ ...selectedBrief, angle: e.target.value })}
                        onBlur={async () => {
                          try {
                            const response = await fetch(`/api/trend-spotter/content-board/briefs/${selectedBrief.id}?userId=${encodeURIComponent(userId)}`, {
                              method: 'PUT',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                userId,
                                angle: selectedBrief.angle || ''
                              })
                            });
                            const result = await response.json();
                            if (result.success) {
                              setIsEditingAngle(false);
                              const updatedBrief = { ...selectedBrief, angle: result.brief.angle };
                              setSelectedBrief(updatedBrief);
                              setBriefs(prevBriefs => prevBriefs.map(b => b.id === selectedBrief.id ? updatedBrief : b));
                              loadBriefs(true);
                              toast.success('Angle updated');
                            } else {
                              toast.error(result.error || 'Failed to update angle');
                            }
                          } catch (err) {
                            console.error('Error updating angle:', err);
                            toast.error('Failed to update angle');
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.target.blur();
                          }
                        }}
                        className={`text-2xl font-bold mb-1 ${isDark ? 'bg-zinc-800 border-cyan-500' : 'bg-white border-gray-300'} border rounded-lg px-3 py-2 focus:outline-none focus:border-cyan-400 w-full`}
                        autoFocus
                      />
                    );
                  } else {
                    const canEdit = userPermission === 'owner' || userPermission === 'edit';
                    return (
                      <h2 
                        className={`text-2xl font-bold mb-1 ${canEdit ? 'cursor-pointer hover:text-cyan-400 transition-colors' : ''}`}
                        onClick={() => {
                          if (canEdit) {
                            setIsEditingAngle(true);
                          }
                        }}
                        title={canEdit ? 'Click to edit' : ''}
                      >
                        {selectedBrief.angle || 'Content Brief'}
                      </h2>
                    );
                  }
                })()}
                <p className={`${isDark ? 'text-zinc-400' : 'text-gray-500'} text-sm`}>
                  Created {selectedBrief.created_at ? new Date(selectedBrief.created_at).toLocaleDateString() : 'recently'} by {selectedBrief.user_id || userId || 'You'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {(() => {
                  const userPermission = getUserPermission(selectedBrief);
                  // Only show share button if user has permission to share (not view-only)
                  if (userPermission !== 'view') {
                    return (
                      <button 
                        onClick={() => {
                          // Set default share permission based on user's permission
                          const userPerm = getUserPermission(selectedBrief);
                          if (userPerm === 'view') {
                            setSharePermission('view');
                          } else if (userPerm === 'comment') {
                            setSharePermission('view'); // Comment can share with view or comment, default to view
                          } else if (userPerm === 'edit') {
                            setSharePermission('view'); // Edit can share with view or edit, default to view
                          } else {
                            setSharePermission('view'); // Owner can share with any, default to view
                          }
                          setShowShareModal(true);
                        }}
                        className="px-3 sm:px-4 py-2 bg-cyan-500 hover:bg-cyan-400 rounded-lg flex items-center gap-2 text-sm font-medium"
                      >
                        <Share2 className="h-4 w-4" />
                        <span className="hidden sm:inline">Share</span>
                      </button>
                    );
                  }
                  return null;
                })()}
                <button
                  onClick={() => {
                    setShowBriefDetailModal(false);
                    setSelectedBrief(null);
                  }}
                  className="p-2 hover:bg-zinc-800 rounded-lg flex-shrink-0 touch-manipulation"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-6">
                {/* Main content - 3 cols */}
                <div className="lg:col-span-3 space-y-4 sm:space-y-6">
                  {/* Shot List */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className={`text-sm font-medium ${isDark ? 'text-zinc-400' : 'text-gray-600'} uppercase tracking-wider`}>SHOT LIST</h3>
                      {(() => {
                        const userPermission = getUserPermission(selectedBrief);
                        // Hide edit button for view and comment roles (only owner and edit can edit)
                        if (userPermission === 'view' || userPermission === 'comment') return null;
                        return !isEditingShots ? (
                          <button 
                            onClick={() => setIsEditingShots(true)}
                            className="text-xs text-cyan-400 hover:text-cyan-300"
                          >
                            Edit
                          </button>
                        ) : (
                          <button 
                            onClick={async () => {
                              try {
                                const response = await fetch(`/api/trend-spotter/content-board/briefs/${selectedBrief.id}?userId=${encodeURIComponent(userId)}`, {
                                  method: 'PUT',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({
                                    userId,
                                    shots: selectedBrief.shots || []
                                  })
                                });
                                const result = await response.json();
                                if (result.success) {
                                  setIsEditingShots(false);
                                  const updatedBrief = { ...selectedBrief, shots: result.brief.shots };
                                  setSelectedBrief(updatedBrief);
                                  setBriefs(prevBriefs => prevBriefs.map(b => b.id === selectedBrief.id ? updatedBrief : b));
                                  loadBriefs(true);
                                  toast.success('Shot list updated');
                                } else {
                                  toast.error(result.error || 'Failed to update shot list');
                                }
                              } catch (err) {
                                console.error('Error updating shot list:', err);
                                toast.error('Failed to update shot list');
                              }
                            }}
                            className="text-xs text-cyan-400 hover:text-cyan-300"
                          >
                            Save
                          </button>
                        );
                      })()}
                    </div>
                    {isEditingShots ? (
                      <div className="space-y-2">
                        {Array.isArray(selectedBrief.shots) && selectedBrief.shots.length > 0 ? (
                          selectedBrief.shots.map((shot, i) => (
                            <div key={i} className="flex items-start gap-3">
                              <span className={`w-6 h-6 ${isDark ? 'bg-zinc-700' : 'bg-gray-200'} rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 mt-2`}>{i + 1}</span>
                              <textarea
                                value={shot}
                                onChange={(e) => {
                                  const newShots = [...(selectedBrief.shots || [])];
                                  newShots[i] = e.target.value;
                                  setSelectedBrief({ ...selectedBrief, shots: newShots });
                                }}
                                className={`flex-1 px-3 py-2 ${isDark ? 'bg-zinc-800 border-cyan-500' : 'bg-white border-gray-300'} border rounded-lg text-sm focus:outline-none focus:border-cyan-400 resize-none`}
                                rows={2}
                              />
                              <button
                                onClick={() => {
                                  const newShots = [...(selectedBrief.shots || [])];
                                  newShots.splice(i, 1);
                                  setSelectedBrief({ ...selectedBrief, shots: newShots });
                                }}
                                className="p-2 text-red-400 hover:text-red-300 mt-1"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ))
                        ) : null}
                        <button
                          onClick={() => {
                            setSelectedBrief({ 
                              ...selectedBrief, 
                              shots: [...(selectedBrief.shots || []), ''] 
                            });
                          }}
                          className={`w-full px-4 py-2 ${isDark ? 'bg-zinc-800 border-zinc-600 text-zinc-400' : 'bg-white border-gray-300 text-gray-700'} border border-dashed hover:border-cyan-500 rounded-lg text-sm hover:text-cyan-400 flex items-center justify-center gap-2`}
                        >
                          <Plus className="h-4 w-4" />
                          Add Shot
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {Array.isArray(selectedBrief.shots) && selectedBrief.shots.length > 0 ? (
                          selectedBrief.shots.map((shot, i) => (
                            <div key={i} className={`flex items-start gap-3 p-3 ${isDark ? 'bg-zinc-800' : 'bg-gray-50'} rounded-lg`}>
                              <span className={`w-6 h-6 ${isDark ? 'bg-zinc-700' : 'bg-gray-200'} rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0`}>{i + 1}</span>
                              <span className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{shot}</span>
                            </div>
                          ))
                        ) : (
                          <p className={`${isDark ? 'text-zinc-500' : 'text-gray-500'} text-sm`}>No shots defined</p>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Template: Text overlay, People/characters, Locations, Script, Sound effects (editable) */}
                  {selectedBrief.template != null && (
                    <div className={`space-y-4 pt-4 border-t ${isDark ? 'border-zinc-700' : 'border-gray-200'}`}>
                      {(() => {
                        const t = selectedBrief.template || {};
                        const visual = t.visual || {};
                        const sound = t.sound || {};
                        const voiceover = sound.voiceover || {};
                        const scriptArr = Array.isArray(voiceover.script) ? voiceover.script : [];
                        const effectsArr = Array.isArray(sound.sound_effects) ? sound.sound_effects : [];
                        const canEdit = getUserPermission(selectedBrief) !== 'view' && getUserPermission(selectedBrief) !== 'comment';
                        const saveTemplate = async (updatedTemplate) => {
                          try {
                            const response = await fetch(`/api/trend-spotter/content-board/briefs/${selectedBrief.id}?userId=${encodeURIComponent(userId)}`, {
                              method: 'PUT',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ userId, template: updatedTemplate })
                            });
                            const result = await response.json();
                            if (result.success) {
                              setSelectedBrief({ ...selectedBrief, template: result.brief.template || updatedTemplate });
                              setBriefs(prev => prev.map(b => b.id === selectedBrief.id ? { ...b, template: result.brief.template || updatedTemplate } : b));
                              setEditingTemplateField(null);
                              toast.success('Updated');
                            } else toast.error(result.error || 'Failed to update');
                          } catch (err) {
                            console.error(err);
                            toast.error('Failed to update');
                          }
                        };
                        return (
                          <>
                            {/* Text overlay */}
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <h3 className={`text-sm font-medium ${isDark ? 'text-zinc-400' : 'text-gray-600'} uppercase tracking-wider`}>TEXT OVERLAY</h3>
                                {canEdit && (editingTemplateField === 'text_overlay' ? (
                                  <div className="flex gap-2">
                                    <button onClick={() => setEditingTemplateField(null)} className="text-xs text-zinc-400 hover:text-zinc-300">Cancel</button>
                                    <button onClick={() => saveTemplate({ ...t, text_overlay: selectedBrief.template?.text_overlay ?? '' })} className="text-xs text-cyan-400 hover:text-cyan-300">Save</button>
                                  </div>
                                ) : (
                                  <button onClick={() => setEditingTemplateField('text_overlay')} className="text-xs text-cyan-400 hover:text-cyan-300">Edit</button>
                                ))}
                              </div>
                              {editingTemplateField === 'text_overlay' ? (
                                <textarea
                                  value={t.text_overlay || ''}
                                  onChange={(e) => setSelectedBrief({ ...selectedBrief, template: { ...t, text_overlay: e.target.value } })}
                                  placeholder="Repeatable text overlay structure with [VARIABLES]"
                                  rows={2}
                                  className={`w-full px-4 py-3 ${isDark ? 'bg-zinc-800 border-cyan-500' : 'bg-white border-gray-300'} border rounded-lg text-sm focus:outline-none focus:border-cyan-400 resize-none`}
                                  autoFocus
                                />
                              ) : (
                                <p className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{t.text_overlay || '—'}</p>
                              )}
                            </div>
                            {/* People / Characters */}
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <h3 className={`text-sm font-medium ${isDark ? 'text-zinc-400' : 'text-gray-600'} uppercase tracking-wider`}>PEOPLE / CHARACTERS</h3>
                                {canEdit && (editingTemplateField === 'people_characters' ? (
                                  <div className="flex gap-2">
                                    <button onClick={() => setEditingTemplateField(null)} className="text-xs text-zinc-400 hover:text-zinc-300">Cancel</button>
                                    <button onClick={() => saveTemplate({ ...t, visual: { ...visual, people_characters: selectedBrief.template?.visual?.people_characters ?? '' } })} className="text-xs text-cyan-400 hover:text-cyan-300">Save</button>
                                  </div>
                                ) : (
                                  <button onClick={() => setEditingTemplateField('people_characters')} className="text-xs text-cyan-400 hover:text-cyan-300">Edit</button>
                                ))}
                              </div>
                              {editingTemplateField === 'people_characters' ? (
                                <textarea
                                  value={visual.people_characters || ''}
                                  onChange={(e) => setSelectedBrief({ ...selectedBrief, template: { ...t, visual: { ...visual, people_characters: e.target.value } } })}
                                  placeholder="Who is in the video"
                                  rows={2}
                                  className={`w-full px-4 py-3 ${isDark ? 'bg-zinc-800 border-cyan-500' : 'bg-white border-gray-300'} border rounded-lg text-sm focus:outline-none focus:border-cyan-400 resize-none`}
                                  autoFocus
                                />
                              ) : (
                                <p className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{visual.people_characters || '—'}</p>
                              )}
                            </div>
                            {/* Locations */}
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <h3 className={`text-sm font-medium ${isDark ? 'text-zinc-400' : 'text-gray-600'} uppercase tracking-wider`}>LOCATIONS</h3>
                                {canEdit && (editingTemplateField === 'locations' ? (
                                  <div className="flex gap-2">
                                    <button onClick={() => setEditingTemplateField(null)} className="text-xs text-zinc-400 hover:text-zinc-300">Cancel</button>
                                    <button onClick={() => saveTemplate({ ...t, visual: { ...visual, locations: selectedBrief.template?.visual?.locations ?? '' } })} className="text-xs text-cyan-400 hover:text-cyan-300">Save</button>
                                  </div>
                                ) : (
                                  <button onClick={() => setEditingTemplateField('locations')} className="text-xs text-cyan-400 hover:text-cyan-300">Edit</button>
                                ))}
                              </div>
                              {editingTemplateField === 'locations' ? (
                                <textarea
                                  value={visual.locations || ''}
                                  onChange={(e) => setSelectedBrief({ ...selectedBrief, template: { ...t, visual: { ...visual, locations: e.target.value } } })}
                                  placeholder="Where the video is filmed"
                                  rows={2}
                                  className={`w-full px-4 py-3 ${isDark ? 'bg-zinc-800 border-cyan-500' : 'bg-white border-gray-300'} border rounded-lg text-sm focus:outline-none focus:border-cyan-400 resize-none`}
                                  autoFocus
                                />
                              ) : (
                                <p className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{visual.locations || '—'}</p>
                              )}
                            </div>
                            {/* Script (voiceover) */}
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <h3 className={`text-sm font-medium ${isDark ? 'text-zinc-400' : 'text-gray-600'} uppercase tracking-wider`}>SCRIPT (VOICEOVER)</h3>
                                {canEdit && (editingTemplateField === 'voiceover_script' ? (
                                  <div className="flex gap-2">
                                    <button onClick={() => setEditingTemplateField(null)} className="text-xs text-zinc-400 hover:text-zinc-300">Cancel</button>
                                    <button onClick={() => saveTemplate({ ...t, sound: { ...sound, voiceover: { script: selectedBrief.template?.sound?.voiceover?.script ?? [] }, sound_effects: effectsArr } })} className="text-xs text-cyan-400 hover:text-cyan-300">Save</button>
                                  </div>
                                ) : (
                                  <button onClick={() => setEditingTemplateField('voiceover_script')} className="text-xs text-cyan-400 hover:text-cyan-300">Edit</button>
                                ))}
                              </div>
                              {editingTemplateField === 'voiceover_script' ? (
                                <textarea
                                  value={(selectedBrief.template?.sound?.voiceover?.script ?? []).join('\n')}
                                  onChange={(e) => setSelectedBrief({ ...selectedBrief, template: { ...t, sound: { ...sound, voiceover: { script: e.target.value.split('\n').filter(Boolean) }, sound_effects: effectsArr } } })}
                                  placeholder="One line per voiceover line"
                                  rows={4}
                                  className={`w-full px-4 py-3 ${isDark ? 'bg-zinc-800 border-cyan-500' : 'bg-white border-gray-300'} border rounded-lg text-sm focus:outline-none focus:border-cyan-400 resize-none`}
                                  autoFocus
                                />
                              ) : (
                                scriptArr.length > 0 ? (
                                  <ul className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'} list-disc list-inside space-y-1`}>
                                    {scriptArr.map((line, i) => <li key={i}>{line}</li>)}
                                  </ul>
                                ) : <p className={`text-sm ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>—</p>
                              )}
                            </div>
                            {/* Sound effects */}
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <h3 className={`text-sm font-medium ${isDark ? 'text-zinc-400' : 'text-gray-600'} uppercase tracking-wider`}>SOUND EFFECTS</h3>
                                {canEdit && (editingTemplateField === 'sound_effects' ? (
                                  <div className="flex gap-2">
                                    <button onClick={() => setEditingTemplateField(null)} className="text-xs text-zinc-400 hover:text-zinc-300">Cancel</button>
                                    <button onClick={() => saveTemplate({ ...t, sound: { ...sound, voiceover: { script: scriptArr }, sound_effects: selectedBrief.template?.sound?.sound_effects ?? [] } })} className="text-xs text-cyan-400 hover:text-cyan-300">Save</button>
                                  </div>
                                ) : (
                                  <button onClick={() => setEditingTemplateField('sound_effects')} className="text-xs text-cyan-400 hover:text-cyan-300">Edit</button>
                                ))}
                              </div>
                              {editingTemplateField === 'sound_effects' ? (
                                <textarea
                                  value={(selectedBrief.template?.sound?.sound_effects ?? []).join('\n')}
                                  onChange={(e) => setSelectedBrief({ ...selectedBrief, template: { ...t, sound: { ...sound, voiceover: { script: scriptArr }, sound_effects: e.target.value.split('\n').filter(Boolean) } } })}
                                  placeholder="One line per sound effect"
                                  rows={3}
                                  className={`w-full px-4 py-3 ${isDark ? 'bg-zinc-800 border-cyan-500' : 'bg-white border-gray-300'} border rounded-lg text-sm focus:outline-none focus:border-cyan-400 resize-none`}
                                  autoFocus
                                />
                              ) : (
                                effectsArr.length > 0 ? (
                                  <ul className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'} list-disc list-inside space-y-1`}>
                                    {effectsArr.map((effect, i) => <li key={i}>{effect}</li>)}
                                  </ul>
                                ) : <p className={`text-sm ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>—</p>
                              )}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}
                  
                  {/* Caption */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className={`text-sm font-medium ${isDark ? 'text-zinc-400' : 'text-gray-600'} uppercase tracking-wider`}>CAPTION</h3>
                      {(() => {
                        const userPermission = getUserPermission(selectedBrief);
                        // Hide edit button for view and comment roles
                        if (userPermission === 'view' || userPermission === 'comment') return null;
                        return !isEditingCaption ? (
                          <button 
                            onClick={() => setIsEditingCaption(true)}
                            className="text-xs text-cyan-400 hover:text-cyan-300"
                          >
                            Edit
                          </button>
                        ) : (
                        <button 
                          onClick={async () => {
                            try {
                              const response = await fetch(`/api/trend-spotter/content-board/briefs/${selectedBrief.id}?userId=${encodeURIComponent(userId)}`, {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                  userId,
                                  caption: selectedBrief.caption || ''
                                })
                              });
                              const result = await response.json();
                              if (result.success) {
                                setIsEditingCaption(false);
                                const updatedBrief = { ...selectedBrief, caption: result.brief.caption };
                                setSelectedBrief(updatedBrief);
                                setBriefs(prevBriefs => prevBriefs.map(b => b.id === selectedBrief.id ? updatedBrief : b));
                                loadBriefs(true);
                                toast.success('Caption updated');
                              } else {
                                toast.error(result.error || 'Failed to update caption');
                              }
                            } catch (err) {
                              console.error('Error updating caption:', err);
                              toast.error('Failed to update caption');
                            }
                          }}
                          className="text-xs text-cyan-400 hover:text-cyan-300"
                        >
                          Save
                        </button>
                        );
                      })()}
                    </div>
                    {isEditingCaption ? (
                      <textarea
                        value={selectedBrief.caption || ''}
                        onChange={(e) => setSelectedBrief({ ...selectedBrief, caption: e.target.value })}
                        placeholder="Enter caption..."
                        rows={4}
                        className={`w-full px-4 py-3 ${isDark ? 'bg-zinc-800 border-cyan-500' : 'bg-white border-gray-300'} border rounded-lg text-sm focus:outline-none focus:border-cyan-400 resize-none`}
                        autoFocus
                      />
                    ) : (
                      <div className={`p-4 ${isDark ? 'bg-zinc-800' : 'bg-gray-50'} rounded-lg`}>
                        <p className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedBrief.caption || 'No caption'}</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Production Notes */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className={`text-sm font-medium ${isDark ? 'text-zinc-400' : 'text-gray-600'} uppercase tracking-wider`}>PRODUCTION NOTES</h3>
                      {(() => {
                        const userPermission = getUserPermission(selectedBrief);
                        // Hide edit button for view and comment roles
                        if (userPermission === 'view' || userPermission === 'comment') return null;
                        return !isEditingNotes ? (
                          <button 
                            onClick={() => setIsEditingNotes(true)}
                            className="text-xs text-cyan-400 hover:text-cyan-300"
                          >
                            Edit
                          </button>
                        ) : (
                        <button 
                          onClick={async () => {
                            try {
                              const response = await fetch(`/api/trend-spotter/content-board/briefs/${selectedBrief.id}?userId=${encodeURIComponent(userId)}`, {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                  userId,
                                  notes: selectedBrief.notes || ''
                                })
                              });
                              const result = await response.json();
                              if (result.success) {
                                setIsEditingNotes(false);
                                const updatedBrief = { ...selectedBrief, notes: result.brief.notes };
                                setSelectedBrief(updatedBrief);
                                setBriefs(prevBriefs => prevBriefs.map(b => b.id === selectedBrief.id ? updatedBrief : b));
                                loadBriefs(true);
                                toast.success('Production notes updated');
                              } else {
                                toast.error(result.error || 'Failed to update notes');
                              }
                            } catch (err) {
                              console.error('Error updating notes:', err);
                              toast.error('Failed to update notes');
                            }
                          }}
                          className="text-xs text-cyan-400 hover:text-cyan-300"
                        >
                          Save
                        </button>
                        );
                      })()}
                    </div>
                    {isEditingNotes ? (
                      <textarea
                        value={selectedBrief.notes || ''}
                        onChange={(e) => setSelectedBrief({ ...selectedBrief, notes: e.target.value })}
                        placeholder="Add production notes..."
                        rows={4}
                        className={`w-full px-4 py-3 ${isDark ? 'bg-zinc-800 border-cyan-500' : 'bg-white border-gray-300'} border rounded-lg text-sm focus:outline-none focus:border-cyan-400 resize-none`}
                        autoFocus
                      />
                    ) : (
                      <div className={`p-4 ${isDark ? 'bg-zinc-800' : 'bg-gray-50'} rounded-lg`}>
                        <p className={`text-sm ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>{selectedBrief.notes || 'No production notes'}</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Sound Link */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className={`text-sm font-medium ${isDark ? 'text-zinc-400' : 'text-gray-600'} uppercase tracking-wider`}>AUDIO TRACK</h3>
                      {(() => {
                        const userPermission = getUserPermission(selectedBrief);
                        // Hide edit button for view and comment roles
                        if (userPermission === 'view' || userPermission === 'comment') return null;
                        return !isEditingSoundLink ? (
                          <button 
                            onClick={() => setIsEditingSoundLink(true)}
                            className="text-xs text-cyan-400 hover:text-cyan-300"
                          >
                            Edit
                          </button>
                        ) : (
                        <button 
                          onClick={async () => {
                            try {
                              const response = await fetch(`/api/trend-spotter/content-board/briefs/${selectedBrief.id}?userId=${encodeURIComponent(userId)}`, {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                  userId,
                                  sound_link: selectedBrief.sound_link || ''
                                })
                              });
                              const result = await response.json();
                              if (result.success) {
                                setIsEditingSoundLink(false);
                                const updatedBrief = { ...selectedBrief, sound_link: result.brief.sound_link };
                                setSelectedBrief(updatedBrief);
                                setBriefs(prevBriefs => prevBriefs.map(b => b.id === selectedBrief.id ? updatedBrief : b));
                                loadBriefs(true);
                                toast.success('Sound link updated');
                              } else {
                                toast.error(result.error || 'Failed to update sound link');
                              }
                            } catch (err) {
                              console.error('Error updating sound link:', err);
                              toast.error('Failed to update sound link');
                            }
                          }}
                          className="text-xs text-cyan-400 hover:text-cyan-300"
                        >
                          Save
                        </button>
                        );
                      })()}
                    </div>
                    {isEditingSoundLink ? (
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={selectedBrief.sound_link || ''}
                          onChange={(e) => setSelectedBrief({ ...selectedBrief, sound_link: e.target.value })}
                          placeholder="Enter song name/keyword (e.g., 'Ordinary') or paste TikTok/Instagram sound link"
                          className={`w-full px-4 py-3 ${isDark ? 'bg-zinc-800 border-cyan-500' : 'bg-white border-gray-300'} border rounded-lg text-sm focus:outline-none focus:border-cyan-400`}
                          autoFocus
                        />
                        {(() => {
                          const trendId = selectedBrief.trend_id ? parseInt(selectedBrief.trend_id) : null;
                          if (!trendId) return null;
                          
                          const popularSounds = getTrendPopularSounds(trendId);
                          if (popularSounds.length === 0) return null;
                          
                          return (
                            <div className="space-y-2">
                              <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>Popular sounds from this trend:</p>
                              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                                {popularSounds.map((sound, idx) => (
                                  <button
                                    key={idx}
                                    onClick={async () => {
                                      try {
                                        const response = await fetch(`/api/trend-spotter/content-board/briefs/${selectedBrief.id}?userId=${encodeURIComponent(userId)}`, {
                                          method: 'PUT',
                                          headers: { 'Content-Type': 'application/json' },
                                          body: JSON.stringify({
                                            userId,
                                            sound_link: sound.url
                                          })
                                        });
                                        const result = await response.json();
                                        if (result.success) {
                                          setIsEditingSoundLink(false);
                                          setSelectedBrief({ ...selectedBrief, sound_link: sound.url });
                                          loadBriefs();
                                          toast.success('Sound link updated');
                                        } else {
                                          toast.error(result.error || 'Failed to update sound link');
                                        }
                                      } catch (err) {
                                        console.error('Error updating sound link:', err);
                                        toast.error('Failed to update sound link');
                                      }
                                    }}
                                    className={`w-full text-left px-3 py-2 ${isDark ? 'bg-zinc-800 hover:bg-zinc-700 border-zinc-700' : 'bg-white hover:bg-gray-50 border-gray-300'} rounded-lg border transition-colors ${
                                      selectedBrief.sound_link === sound.url
                                        ? 'border-cyan-500 bg-cyan-500/10'
                                        : ''
                                    }`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <span className="text-xs">
                                          {sound.platform === 'tiktok' ? '🎵' : '🎵'}
                                        </span>
                                        <span className={`text-xs ${isDark ? 'text-zinc-300' : 'text-gray-700'} truncate`}>
                                          {sound.platform === 'tiktok' ? 'TikTok' : 'Instagram'} Sound
                                        </span>
                                      </div>
                                      <span className="text-xs text-zinc-500 ml-2">
                                        {sound.count} video{sound.count !== 1 ? 's' : ''}
                                      </span>
                                    </div>
                                    <div className="mt-1 flex items-center gap-1">
                                      <span
                                        className="text-xs text-cyan-400 truncate flex-1"
                                      >
                                        {sound.url}
                                      </span>
                                      <a
                                        href={sound.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="text-cyan-400 hover:text-cyan-300 flex-shrink-0"
                                        title="Open in new tab"
                                      >
                                        <ExternalLink className="h-3 w-3" />
                                      </a>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className={`p-4 ${isDark ? 'bg-zinc-800' : 'bg-gray-50'} rounded-lg`}>
                          {selectedBrief.sound_link ? (
                            (() => {
                              // Check if it's a URL (starts with http:// or https://)
                              const isUrl = selectedBrief.sound_link.trim().toLowerCase().startsWith('http://') || 
                                           selectedBrief.sound_link.trim().toLowerCase().startsWith('https://');
                              if (isUrl) {
                                return (
                                  <a
                                    href={selectedBrief.sound_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-cyan-400 hover:text-cyan-300 underline break-all"
                                  >
                                    {selectedBrief.sound_link}
                                  </a>
                                );
                              } else {
                                // It's a keyword, display as plain text
                                return (
                                  <p className="text-sm text-cyan-300">
                                    {selectedBrief.sound_link}
                                  </p>
                                );
                              }
                            })()
                          ) : (
                            <p className={`text-sm ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>No audio track specified</p>
                          )}
                        </div>
                        {(() => {
                          const trendId = selectedBrief.trend_id ? parseInt(selectedBrief.trend_id) : null;
                          if (!trendId) return null;
                          
                          const popularSounds = getTrendPopularSounds(trendId);
                          if (popularSounds.length === 0) return null;
                          
                          const userPermission = getUserPermission(selectedBrief);
                          const canEdit = userPermission === 'owner' || userPermission === 'edit';
                          
                          return (
                            <div className="space-y-2">
                              <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>Popular sounds from this trend:</p>
                              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                                {popularSounds.map((sound, idx) => (
                                  <button
                                    key={idx}
                                    onClick={canEdit ? async () => {
                                      try {
                                        const response = await fetch(`/api/trend-spotter/content-board/briefs/${selectedBrief.id}?userId=${encodeURIComponent(userId)}`, {
                                          method: 'PUT',
                                          headers: { 'Content-Type': 'application/json' },
                                          body: JSON.stringify({
                                            userId,
                                            sound_link: sound.url
                                          })
                                        });
                                        const result = await response.json();
                                        if (result.success) {
                                          setSelectedBrief({ ...selectedBrief, sound_link: sound.url });
                                          loadBriefs();
                                          toast.success('Sound link updated');
                                        } else {
                                          toast.error(result.error || 'Failed to update sound link');
                                        }
                                      } catch (err) {
                                        console.error('Error updating sound link:', err);
                                        toast.error('Failed to update sound link');
                                      }
                                    } : undefined}
                                    disabled={!canEdit}
                                    className={`w-full text-left px-3 py-2 ${isDark ? 'bg-zinc-800 hover:bg-zinc-700' : 'bg-white hover:bg-gray-50 border-gray-300'} rounded-lg border transition-colors ${
                                      canEdit ? 'cursor-pointer' : 'cursor-default opacity-75'
                                    } ${
                                      selectedBrief.sound_link === sound.url
                                        ? 'border-cyan-500 bg-cyan-500/10'
                                        : 'border-zinc-700'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <span className="text-xs">
                                          {sound.platform === 'tiktok' ? '🎵' : '🎵'}
                                        </span>
                                        <span className="text-xs text-zinc-300 truncate">
                                          {sound.platform === 'tiktok' ? 'TikTok' : 'Instagram'} Sound
                                        </span>
                                      </div>
                                      <span className="text-xs text-zinc-500 ml-2">
                                        {sound.count} video{sound.count !== 1 ? 's' : ''}
                                      </span>
                                    </div>
                                    <div className="mt-1 flex items-center gap-1">
                                      <span
                                        className="text-xs text-cyan-400 truncate flex-1"
                                      >
                                        {sound.url}
                                      </span>
                                      <a
                                        href={sound.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="text-cyan-400 hover:text-cyan-300 flex-shrink-0"
                                        title="Open in new tab"
                                      >
                                        <ExternalLink className="h-3 w-3" />
                                      </a>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                  
                  {/* Tags */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className={`text-sm font-medium ${isDark ? 'text-zinc-400' : 'text-gray-600'} uppercase tracking-wider`}>TAGS</h3>
                      {/* Save button removed - tags now save automatically on Enter */}
                    </div>
                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-2">
                        {Array.isArray(selectedBrief.tags) && selectedBrief.tags.length > 0 ? (
                          selectedBrief.tags.map((tag, tagIdx) => (
                            <div
                              key={tagIdx}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-500/20 text-cyan-300 rounded-lg text-sm"
                            >
                              <span>{tag}</span>
                              {(() => {
                                const userPermission = getUserPermission(selectedBrief);
                                if (userPermission === 'view' || userPermission === 'comment') return null;
                                return (
                                  <button
                                    onClick={() => {
                                      const updatedTags = selectedBrief.tags.filter((_, i) => i !== tagIdx);
                                      setSelectedBrief({ ...selectedBrief, tags: updatedTags });
                                    }}
                                    className="hover:text-red-400 transition-colors"
                                    type="button"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                );
                              })()}
                            </div>
                          ))
                        ) : (
                          <p className={`text-sm ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>No tags added</p>
                        )}
                      </div>
                      {(() => {
                        const userPermission = getUserPermission(selectedBrief);
                        if (userPermission === 'view' || userPermission === 'comment') return null;
                        return (
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={tagInput}
                              onChange={(e) => { setTagInput(e.target.value); setShareError(''); }}
                              onKeyDown={async (e) => {
                                if (e.key === 'Enter' && tagInput.trim()) {
                                  e.preventDefault();
                                  const newTag = tagInput.trim();
                                  const currentTags = Array.isArray(selectedBrief.tags) ? selectedBrief.tags : [];
                                  if (!currentTags.includes(newTag)) {
                                    const updatedTags = [...currentTags, newTag];
                                    // Update state immediately
                                    const updatedBrief = { ...selectedBrief, tags: updatedTags };
                                    setSelectedBrief(updatedBrief);
                                    setTagInput('');
                                    setShareError('');
                                    
                                    // Save to server automatically
                                    try {
                                      const response = await fetch(`/api/trend-spotter/content-board/briefs/${selectedBrief.id}?userId=${encodeURIComponent(userId)}`, {
                                        method: 'PUT',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({
                                          userId,
                                          tags: updatedTags
                                        })
                                      });
                                      const result = await response.json();
                                      if (result.success) {
                                        // Update briefs state
                                        setBriefs(prevBriefs => prevBriefs.map(b => b.id === selectedBrief.id ? updatedBrief : b));
                                        loadBriefs(true);
                                        toast.success('Tag added');
                                      } else {
                                        // Revert on error
                                        setSelectedBrief({ ...selectedBrief, tags: currentTags });
                                        toast.error(result.error || 'Failed to save tag');
                                      }
                                    } catch (err) {
                                      console.error('Error saving tag:', err);
                                      // Revert on error
                                      setSelectedBrief({ ...selectedBrief, tags: currentTags });
                                      toast.error('Failed to save tag');
                                    }
                                  } else {
                                    setShareError('Tag already exists');
                                  }
                                }
                              }}
                              placeholder="Type tag and press Enter"
                              className={`flex-1 px-3 py-2 ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-300'} border rounded-lg text-sm focus:outline-none focus:border-cyan-500`}
                            />
                          </div>
                        );
                      })()}
                      {shareError && shareError.includes('Tag') && <p className="text-red-400 text-xs mt-1">{shareError}</p>}
                    </div>
                  </div>
                  
                  {/* Comments - Hide for view-only users */}
                  {(() => {
                    const userPermission = getUserPermission(selectedBrief);
                    if (userPermission === 'view') return null; // Hide comments section for view-only
                    return (
                      <div>
                        <h3 className={`text-sm font-medium ${isDark ? 'text-zinc-400' : 'text-gray-600'} uppercase tracking-wider mb-3`}>
                          COMMENTS ({selectedBrief.comments?.length || 0})
                        </h3>
                        <div className="space-y-3">
                          {[...(selectedBrief.comments || [])]
                            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                            .map(comment => {
                            // Use user_id (email) if user_name is not available
                            const displayName = comment.user_name || comment.user_id || 'User';
                            const displayInitial = displayName.charAt(0).toUpperCase();
                            return (
                              <div key={comment.id} className="flex gap-3">
                                <div className="w-8 h-8 bg-cyan-500 rounded-full flex items-center justify-center text-sm flex-shrink-0">
                                  {displayInitial}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{displayName}</span>
                                    <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>{new Date(comment.created_at).toLocaleDateString()}</span>
                                  </div>
                                  <p className={`text-sm ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>{comment.comment_text}</p>
                                </div>
                              </div>
                            );
                          })}
                          
                          {/* Add comment input */}
                          <div className="flex gap-3 mt-4">
                            <div className="w-8 h-8 bg-cyan-500 rounded-full flex items-center justify-center text-sm flex-shrink-0">
                              {userId?.charAt(0) || 'U'}
                            </div>
                            <div className="flex-1 flex gap-2">
                              <input
                                ref={(el) => setCommentInputRef(el)}
                                type="text"
                                placeholder="Add a comment..."
                                onKeyDown={async (e) => {
                                  if (e.key === 'Enter' && e.target.value.trim()) {
                                    await handlePostComment(e.target);
                                  }
                                }}
                                className={`flex-1 px-3 py-2 ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-300'} border rounded-lg text-sm focus:outline-none focus:border-cyan-500`}
                              />
                              <button 
                                onClick={async () => {
                                  if (commentInputRef && commentInputRef.value.trim()) {
                                    await handlePostComment(commentInputRef);
                                  }
                                }}
                                className="px-4 py-2 bg-cyan-500 hover:bg-cyan-400 rounded-lg text-sm font-medium"
                              >
                                Post
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
                
                {/* Sidebar - 2 cols */}
                <div className="lg:col-span-2 space-y-4 sm:space-y-6">
                  {/* People */}
                  <div>
                    <h3 className={`text-sm font-medium ${isDark ? 'text-zinc-400' : 'text-gray-600'} uppercase tracking-wider mb-3`}>PEOPLE</h3>
                    <div className="space-y-2">
                      {/* Owner */}
                      <div className={`flex items-center gap-3 p-2 ${isDark ? 'bg-zinc-800' : 'bg-gray-50'} rounded-lg`}>
                        <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-sm">
                          {selectedBrief.user_id?.charAt(0) || 'U'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedBrief.user_id || 'Owner'}</div>
                          <div className={`text-xs ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>Owner</div>
                        </div>
                      </div>
                      
                      {/* Collaborators */}
                      {selectedBrief.collaborators?.map(collab => (
                        <div key={collab.id} className={`flex items-center gap-3 p-2 ${isDark ? 'bg-zinc-800' : 'bg-gray-50'} rounded-lg group`}>
                          <div className={`w-8 h-8 ${collab.permission === 'edit' ? 'bg-cyan-500' : collab.permission === 'comment' ? 'bg-green-500' : isDark ? 'bg-zinc-700' : 'bg-gray-300'} rounded-full flex items-center justify-center text-sm`}>
                            {collab.user_id?.charAt(0) || 'U'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{collab.user_id || 'User'}</div>
                            <div className={`text-xs ${isDark ? 'text-zinc-500' : 'text-gray-500'} capitalize`}>{collab.permission || 'view'}</div>
                          </div>
                          {(() => {
                            const userPermission = getUserPermission(selectedBrief);
                            // Only owner can remove collaborators
                            if (userPermission === 'owner') {
                              return (
                                <button
                                  onClick={() => handleRemoveCollaborator(selectedBrief.id, collab.user_id)}
                                  className={`opacity-0 group-hover:opacity-100 p-1 ${isDark ? 'hover:bg-zinc-700 text-zinc-500 hover:text-red-400' : 'hover:bg-gray-200 text-gray-500 hover:text-red-600'} rounded transition-all`}
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      ))}
                      
                      {/* Add people button - only show if user can share */}
                      {(() => {
                        const userPermission = getUserPermission(selectedBrief);
                        if (userPermission !== 'view') {
                          return (
                            <button
                              onClick={() => {
                                // Set default share permission based on user's permission
                                const userPerm = getUserPermission(selectedBrief);
                                if (userPerm === 'view') {
                                  setSharePermission('view');
                                } else if (userPerm === 'comment') {
                                  setSharePermission('view');
                                } else if (userPerm === 'edit') {
                                  setSharePermission('view');
                                } else {
                                  setSharePermission('view');
                                }
                                setShowShareModal(true);
                              }}
                              className={`w-full p-2 border border-dashed rounded-lg text-sm transition-colors ${
                                isDark
                                  ? 'border-zinc-700 text-zinc-500 hover:border-cyan-500/50 hover:text-cyan-400'
                                  : 'border-gray-300 text-gray-600 hover:border-cyan-500/50 hover:text-cyan-600'
                              }`}
                            >
                              + Add people
                            </button>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                  
                  {/* Details */}
                  <div>
                    <h3 className={`text-sm font-medium uppercase tracking-wider mb-3 ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>DETAILS</h3>
                    <div className={`space-y-2 text-sm ${isDark ? 'text-zinc-200' : 'text-gray-900'}`}>
                      {selectedBrief.created_at && (
                        <div className="flex justify-between">
                          <span className={isDark ? 'text-zinc-500' : 'text-gray-500'}>Created</span>
                          <span>{new Date(selectedBrief.created_at).toLocaleDateString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className={isDark ? 'text-zinc-500' : 'text-gray-500'}>Your role</span>
                        <span className="capitalize">
                          {selectedBrief.collaborators?.find(c => c.user_id === userId)?.permission || (selectedBrief.user_id === userId ? 'Owner' : 'View')}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className={isDark ? 'text-zinc-500' : 'text-gray-500'}>Trend</span>
                        {(() => {
                          const userPermission = getUserPermission(selectedBrief);
                          if (userPermission === 'view' || userPermission === 'comment') {
                            return (
                              <span className={isDark ? 'text-white' : 'text-gray-900'}>{selectedBrief.trend_id ? (trends.find(t => t.id === selectedBrief.trend_id)?.trend_name || 'N/A') : 'No trend'}</span>
                            );
                          }
                          return (
                            <select
                              value={selectedBrief.trend_id || ''}
                              onChange={(e) => {
                                const newTrendId = e.target.value === '' ? null : parseInt(e.target.value);
                                handleUpdateBriefTrend(selectedBrief.id, newTrendId);
                              }}
                              className={`px-3 py-1.5 ${isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-300' : 'bg-white border-gray-300 text-black'} border rounded-lg text-sm focus:outline-none focus:border-cyan-500 max-w-xs`}
                            >
                              <option value="">No trend</option>
                              {trends.filter(t => t.status === 'approved' || t.status === 'pending').map(trend => {
                                const maxLength = 40;
                                const displayName = trend.trend_name.length > maxLength 
                                  ? trend.trend_name.substring(0, maxLength) + '...' 
                                  : trend.trend_name;
                                return (
                                  <option key={trend.id} value={trend.id} title={trend.trend_name}>
                                    {displayName}
                                  </option>
                                );
                              })}
                            </select>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                  
                  {/* Videos Section - Selected Videos + Trend Examples */}
                  {(() => {
                    const trendId = selectedBrief.trend_id ? parseInt(selectedBrief.trend_id) : null;
                    // When user has videos in library, don't show default trend examples (only their selected videos)
                    const showTrendExamples = libraryVideos.length === 0;
                    const hasTrendVideos = trendId && trendExampleVideos.length > 0 && showTrendExamples;
                    const hasSelectedVideos = selectedBriefVideos.length > 0;
                    const userPermission = getUserPermission(selectedBrief);
                    const canEdit = userPermission !== 'view' && userPermission !== 'comment';
                    
                    // Show section if there are videos OR if user can edit (so they can add videos)
                    if (!hasTrendVideos && !hasSelectedVideos && !canEdit) return null;
                    
                    // Combine: selected videos first; trend examples only when library is empty
                    const allVideos = [
                      ...selectedBriefVideos.map(v => ({ ...v, source: 'selected' })),
                      ...(showTrendExamples ? trendExampleVideos.map(v => ({ ...v, source: 'trend' })) : [])
                    ];
                    
                    return (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className={`text-sm font-medium uppercase tracking-wider ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>
                            {hasSelectedVideos && hasTrendVideos 
                              ? 'VIDEOS' 
                              : hasSelectedVideos 
                                ? 'SELECTED VIDEOS' 
                                : hasTrendVideos
                                  ? 'TREND EXAMPLES'
                                  : 'VIDEOS'}
                          </h3>
                          {(() => {
                            const userPermission = getUserPermission(selectedBrief);
                            if (userPermission === 'view' || userPermission === 'comment') return null;
                            return (
                              <button
                                onClick={() => {
                                  // Open video selection modal for editing
                                  setShowBriefVideoSelectionModal(true);
                                }}
                                className={`text-xs flex items-center gap-1 ${isDark ? 'text-cyan-400 hover:text-cyan-300' : 'text-cyan-600 hover:text-cyan-700'}`}
                                title={allVideos.length > 0 ? "Edit videos" : "Add videos"}
                              >
                                <Edit className="h-3 w-3" />
                                <span>{allVideos.length > 0 ? "Edit" : "Add"}</span>
                              </button>
                            );
                          })()}
                        </div>
                        {loadingTrendExamples && !hasSelectedVideos ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 className={`h-5 w-5 animate-spin ${isDark ? 'text-zinc-500' : 'text-gray-400'}`} />
                          </div>
                        ) : allVideos.length > 0 ? (
                          <div className="flex gap-3 overflow-x-auto pb-2">
                            {allVideos.map((video) => {
                              const videoUrl = video.video_url || video.source_url;
                              const views = getApifyValue(video, 'views');
                              const likes = getApifyValue(video, 'likes');
                              
                              return (
                                <div
                                  key={video.id}
                                  className="relative group cursor-pointer bg-zinc-800 rounded-lg overflow-hidden hover:bg-zinc-700 transition-colors flex-1"
                                  onClick={() => {
                                    setVideoPreview({
                                      videoUrl: videoUrl,
                                      creator: getCreatorUsername(video),
                                      platform: video.platform || 'unknown',
                                      views: getApifyValue(video, 'views'),
                                      likes: getApifyValue(video, 'likes'),
                                      video: video // Include full video object for metadata
                                    });
                                  }}
                                >
                                  {videoUrl ? (
                                    <video
                                      src={videoUrl}
                                      className="w-full aspect-[9/16] object-cover"
                                      muted
                                      loop
                                      playsInline
                                      onMouseEnter={(e) => {
                                        const video = e.target;
                                        const playPromise = video.play();
                                        if (playPromise !== undefined) {
                                          playPromise.catch((error) => {
                                            // Play was interrupted or failed, ignore error
                                            // This is expected when pause() is called quickly
                                          });
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        const video = e.target;
                                        // Pause the video
                                        const pausePromise = video.pause();
                                        if (pausePromise !== undefined) {
                                          pausePromise.catch(() => {
                                            // Pause error, ignore
                                          });
                                        }
                                        // Reset to beginning
                                        video.currentTime = 0;
                                      }}
                                    />
                                  ) : (
                                    <div className="w-full aspect-[9/16] bg-zinc-700 flex items-center justify-center">
                                      <Video className="h-8 w-8 text-zinc-500" />
                                    </div>
                                  )}
                                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                                    <div className="flex items-center justify-between text-xs text-white">
                                      <span className="truncate">{getCreatorUsername(video)}</span>
                                      <div className="flex items-center gap-2 ml-2">
                                        {video.source === 'selected' && (
                                          <span className="text-cyan-400 text-[10px] px-1.5 py-0.5 bg-cyan-500/20 rounded">
                                            SELECTED
                                          </span>
                                        )}
                                        {views > 0 && (
                                          <span className="text-zinc-300">
                                            {formatNumber(views)} views
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-sm text-zinc-500 py-4 text-center">
                            No videos available
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && selectedBrief && (() => {
        const userPermission = getUserPermission(selectedBrief);
        return (
          <div key="share-modal" className={`fixed inset-0 flex items-center justify-center z-[60] p-2 sm:p-4 ${isDark ? 'bg-black/80' : 'bg-gray-900/50'}`}>
            <div className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'} rounded-2xl border w-full max-w-md overflow-hidden shadow-xl`}>
              <div className={`p-4 sm:p-6 border-b ${isDark ? 'border-zinc-800' : 'border-gray-200'} flex items-center justify-between gap-2`}>
                <div className="flex-1 min-w-0">
                  <h2 className={`text-lg sm:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Share &ldquo;{selectedBrief.angle || 'Brief'}&rdquo;
                  </h2>
                  <p className={`text-xs sm:text-sm mt-1 ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>Add collaborators to this brief</p>
                </div>
                <button
                  onClick={() => {
                    setShowShareModal(false);
                    setShareEmail('');
                    setShareError('');
                    if (userPermission === 'view') {
                      setSharePermission('view');
                    } else if (userPermission === 'edit') {
                      setSharePermission('view');
                    } else {
                      setSharePermission('view');
                    }
                  }}
                  className={`p-2 rounded-lg flex-shrink-0 touch-manipulation ${isDark ? 'hover:bg-zinc-800 text-zinc-300' : 'hover:bg-gray-100 text-gray-700'}`}
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                {/* Add people */}
                <div>
                  <label className={`text-sm mb-2 block ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>Add people</label>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={shareEmail}
                      onChange={(e) => { setShareEmail(e.target.value); setShareError(''); }}
                      placeholder="Email address"
                      className={`flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 ${isDark ? 'bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`}
                    />
                    <select
                      value={sharePermission}
                      onChange={(e) => setSharePermission(e.target.value)}
                      className={`px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 ${isDark ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    >
                      {(() => {
                        const options = [];
                        if (canShareWithPermission(userPermission, 'view')) {
                          options.push(<option key="view" value="view">Can view</option>);
                        }
                        if (canShareWithPermission(userPermission, 'comment')) {
                          options.push(<option key="comment" value="comment">Can comment</option>);
                        }
                        if (canShareWithPermission(userPermission, 'edit')) {
                          options.push(<option key="edit" value="edit">Can edit</option>);
                        }
                        // If no options available, default to view
                        if (options.length === 0) {
                          options.push(<option key="view" value="view">Can view</option>);
                        }
                        return options;
                      })()}
                    </select>
                  </div>
                  {shareError && <p className={`text-xs mt-2 ${isDark ? 'text-red-400' : 'text-red-600'}`}>{shareError}</p>}
                  <button
                    onClick={async () => {
                      setShareError('');
                      if (!shareEmail.trim()) {
                        setShareError('Please enter an email address');
                        return;
                      }
                      const email = shareEmail.trim().toLowerCase();
                      if (email === userId?.toLowerCase()) {
                        setShareError('Cannot add yourself as a collaborator');
                        return;
                      }
                    const success = await handleAddCollaborator(selectedBrief.id, email, sharePermission, userId);
                    if (success) {
                      setShareEmail('');
                      // Reset to default based on user permission
                      if (userPermission === 'view') {
                        setSharePermission('view');
                      } else if (userPermission === 'comment') {
                        setSharePermission('view');
                      } else if (userPermission === 'edit') {
                        setSharePermission('view');
                      } else {
                        setSharePermission('view');
                      }
                    } else {
                      setShareError('Failed to add collaborator. User may not exist.');
                    }
                    }}
                    className="mt-3 w-full px-4 py-2 bg-cyan-500 hover:bg-cyan-400 text-white rounded-lg font-medium transition-colors"
                  >
                    Add
                  </button>
                </div>
              
              {/* Permission levels explained */}
              <div className={`p-4 rounded-lg ${isDark ? 'bg-zinc-800/50' : 'bg-gray-100 border border-gray-200'}`}>
                <div className={`text-xs space-y-2 ${isDark ? 'text-zinc-500' : 'text-gray-600'}`}>
                  <div className="flex items-start gap-2">
                    <span className={`font-medium w-20 shrink-0 ${isDark ? 'text-zinc-400' : 'text-gray-700'}`}>View</span>
                    <span>Can see the brief and comments</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className={`font-medium w-20 shrink-0 ${isDark ? 'text-zinc-400' : 'text-gray-700'}`}>Comment</span>
                    <span>Can view and add comments</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className={`font-medium w-20 shrink-0 ${isDark ? 'text-zinc-400' : 'text-gray-700'}`}>Edit</span>
                    <span>Can edit all content, add comments, and move between stages</span>
                  </div>
                </div>
              </div>
              
              {/* Current access list */}
              <div>
                <label className={`text-sm mb-2 block ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>People with access</label>
                <div className="space-y-2">
                  {/* Owner */}
                  <div className={`flex items-center gap-3 p-2 rounded-lg ${isDark ? 'bg-zinc-800' : 'bg-gray-100 border border-gray-200'}`}>
                    <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-sm text-white">
                      {selectedBrief.user_id?.charAt(0) || 'U'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium truncate ${isDark ? 'text-zinc-100' : 'text-gray-900'}`}>{selectedBrief.user_id || 'Owner'}</div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${isDark ? 'text-zinc-400 bg-zinc-700' : 'text-gray-600 bg-gray-200'}`}>Owner</span>
                  </div>
                  
                  {/* Collaborators */}
                  {selectedBrief.collaborators?.map(collab => (
                    <div key={collab.id} className={`flex items-center gap-3 p-2 rounded-lg ${isDark ? 'bg-zinc-800' : 'bg-gray-100 border border-gray-200'}`}>
                      <div className={`w-8 h-8 ${collab.permission === 'edit' ? 'bg-cyan-500' : collab.permission === 'comment' ? 'bg-green-500' : isDark ? 'bg-zinc-700' : 'bg-gray-300'} rounded-full flex items-center justify-center text-sm text-white`}>
                        {collab.user_id?.charAt(0) || 'U'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-medium truncate ${isDark ? 'text-zinc-100' : 'text-gray-900'}`}>{collab.user_id || 'User'}</div>
                      </div>
                      {(() => {
                        const userPermission = getUserPermission(selectedBrief);
                        // Only owner can edit permissions
                        if (userPermission === 'owner') {
                          return (
                            <>
                              <select
                                value={collab.permission}
                                onChange={async (e) => {
                                  const newPermission = e.target.value;
                                  const success = await handleAddCollaborator(selectedBrief.id, collab.user_id, newPermission, userId);
                                  if (success) {
                                    // Update selectedBrief immediately with the new permission
                                    setSelectedBrief(prev => ({
                                      ...prev,
                                      collaborators: prev.collaborators?.map(c => 
                                        c.user_id === collab.user_id 
                                          ? { ...c, permission: newPermission }
                                          : c
                                      ) || []
                                    }));
                                    // Also reload briefs to ensure consistency
                                    await loadBriefs();
                                  }
                                }}
                                className={`text-xs border rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 ${isDark ? 'bg-zinc-700 border-zinc-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                              >
                                <option value="view">Can view</option>
                                <option value="comment">Can comment</option>
                                <option value="edit">Can edit</option>
                              </select>
                              <button
                                onClick={() => handleRemoveCollaborator(selectedBrief.id, collab.user_id)}
                                className={`p-1 rounded ${isDark ? 'hover:bg-zinc-600 text-zinc-500 hover:text-red-400' : 'hover:bg-gray-200 text-gray-500 hover:text-red-600'}`}
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </>
                          );
                        }
                        // Non-owners can only see the permission, not edit
                        return (
                          <span className={`text-xs capitalize ${isDark ? 'text-zinc-500' : 'text-gray-600'}`}>{collab.permission || 'view'}</span>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className={`p-6 border-t ${isDark ? 'border-zinc-800' : 'border-gray-200'}`}>
              <button
                onClick={() => {
                  setShowShareModal(false);
                  setShareEmail('');
                  setShareError('');
                  // Reset sharePermission to view when closing
                  if (userPermission === 'view') {
                    setSharePermission('view');
                  } else if (userPermission === 'edit') {
                    setSharePermission('view');
                  } else {
                    setSharePermission('view');
                  }
                }}
                className={`w-full px-4 py-3 rounded-xl font-medium transition-colors border ${isDark ? 'bg-zinc-800 hover:bg-zinc-700 border-zinc-700 text-white' : 'bg-white hover:bg-gray-50 border-gray-300 text-gray-900 shadow-sm'}`}
              >
                Done
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Video Selection Modal */}
      {/* Brief Video Selection Modal */}
      {showBriefVideoSelectionModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'} rounded-2xl border w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col`}>
            <div className={`p-4 sm:p-6 border-b ${isDark ? 'border-zinc-800' : 'border-gray-200'} flex items-center justify-between gap-2`}>
              <div className="flex-1 min-w-0">
                <h2 className={`text-lg sm:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Select Videos for Brief</h2>
                <p className={`${isDark ? 'text-zinc-500' : 'text-gray-500'} text-xs sm:text-sm mt-1`}>
                  Browse and select videos from your library to attach to this brief
                  {libraryPagination.total > 0 && (
                    <span className="ml-2">({libraryPagination.total} total)</span>
                  )}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowBriefVideoSelectionModal(false);
                }}
                className={`p-2 ${isDark ? 'hover:bg-zinc-800' : 'hover:bg-gray-100'} rounded-lg flex-shrink-0 touch-manipulation`}
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div 
              className="flex-1 overflow-y-auto p-6"
              onScroll={(e) => {
                const container = e.target;
                const scrollBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
                // Load more when within 200px of bottom
                if (scrollBottom < 200 && libraryPagination.hasMore && !libraryPagination.loadingMore) {
                  loadMoreLibrary();
                }
              }}
            >
              {/* Selection counter */}
              {(() => {
                const currentVideoIds = Array.isArray(selectedBrief.video_ids) ? selectedBrief.video_ids : [];
                const selectedCount = currentVideoIds.length;
                return (
                  <div className="mb-4 p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/30">
                    <div className="flex items-center justify-between">
                      <span className="text-cyan-400">
                        {selectedCount} video{selectedCount !== 1 ? 's' : ''} selected
                        {libraryPagination.total > 0 && (
                          <span className={`${isDark ? 'text-zinc-500' : 'text-gray-500'} ml-2`}>
                            ({libraryVideos.length} of {libraryPagination.total} loaded)
                          </span>
                        )}
                      </span>
                      {selectedCount > 0 && (
                        <button
                          onClick={() => {
                            setSelectedBrief({ ...selectedBrief, video_ids: [] });
                          }}
                          className="text-xs text-red-400 hover:text-red-300"
                        >
                          Clear all
                        </button>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Video grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {libraryVideos.map((video) => {
                  // Get video URL
                  let videoUrl = video.video_url;
                  if (!videoUrl && video.apify_metadata) {
                    const meta = typeof video.apify_metadata === 'string' 
                      ? JSON.parse(video.apify_metadata) 
                      : video.apify_metadata;
                    videoUrl = meta.videoUrl || meta.video_url || meta.mediaUrls?.[0] || meta.webVideoUrl;
                  }
                  
                  const currentVideoIds = Array.isArray(selectedBrief.video_ids) ? selectedBrief.video_ids : [];
                  const isSelected = currentVideoIds.includes(video.id);
                  
                  return (
                    <div
                      key={video.id}
                      className={`relative rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                        isSelected 
                          ? 'border-cyan-500 ring-2 ring-cyan-500/50' 
                          : isDark ? 'border-zinc-700 hover:border-zinc-600' : 'border-gray-300 hover:border-gray-400'
                      }`}
                      onClick={() => {
                        const updatedVideoIds = isSelected
                          ? currentVideoIds.filter(id => id !== video.id)
                          : [...currentVideoIds, video.id];
                        setSelectedBrief({ ...selectedBrief, video_ids: updatedVideoIds });
                      }}
                    >
                      {/* Video thumbnail */}
                      {videoUrl ? (
                        <video
                          src={videoUrl}
                          className="w-full aspect-[9/16] object-cover"
                          muted
                          playsInline
                          onMouseEnter={(e) => {
                            const video = e.target;
                            if (video.paused) {
                              const playPromise = video.play();
                              if (playPromise !== undefined) {
                                playPromise.catch(() => {
                                  // Ignore play errors (e.g., if video is already playing or interrupted)
                                });
                              }
                            }
                          }}
                          onMouseLeave={(e) => {
                            const video = e.target;
                            if (!video.paused) {
                              const pausePromise = video.pause();
                              if (pausePromise !== undefined) {
                                pausePromise.catch(() => {
                                  // Ignore pause errors
                                });
                              }
                            }
                            video.currentTime = 0;
                          }}
                        />
                      ) : (
                        <div className={`w-full aspect-[9/16] ${isDark ? 'bg-zinc-800' : 'bg-gray-100'} flex items-center justify-center`}>
                          <Video className={`h-8 w-8 ${isDark ? 'text-zinc-600' : 'text-gray-400'}`} />
                        </div>
                      )}
                      
                      {/* Selection indicator */}
                      {isSelected && (
                        <div className="absolute top-2 right-2 bg-cyan-500 rounded-full p-1.5">
                          <CheckCircle className="h-4 w-4 text-white" />
                        </div>
                      )}
                      
                      {/* Video info overlay */}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                        <p className="text-xs text-white truncate">
                          {video.apify_author_username || 'Unknown'}
                        </p>
                        <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-gray-300'} truncate`}>
                          {getApifyValue(video, 'views') ? `${formatNumber(getApifyValue(video, 'views'))} views` : ''}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {libraryVideos.length === 0 && (
                <div className="text-center py-12">
                  <Video className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-zinc-500">No videos in your library</p>
                </div>
              )}
              
              {/* Loading more indicator */}
              {libraryPagination.loadingMore && (
                <div className="col-span-full text-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-cyan-400 mx-auto" />
                  <p className="text-zinc-500 text-sm mt-2">Loading more videos...</p>
                </div>
              )}
              
              {/* End of list indicator */}
              {!libraryPagination.hasMore && libraryVideos.length > 0 && (
                <div className="col-span-full text-center py-4">
                  <p className="text-zinc-500 text-sm">All videos loaded</p>
                </div>
              )}
            </div>
            
            <div className="p-4 sm:p-6 border-t border-zinc-800 flex items-center justify-end gap-3">
              <button
                onClick={async () => {
                  // If editing an existing brief (not creating a new one), save the video changes
                  if (selectedBrief && selectedBrief.id) {
                    const currentVideoIds = Array.isArray(selectedBrief.video_ids) ? selectedBrief.video_ids : [];
                    await handleUpdateBriefVideos(selectedBrief.id, currentVideoIds);
                  }
                  setShowBriefVideoSelectionModal(false);
                }}
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm font-medium transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {showVideoSelectionModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'} rounded-2xl border w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col`}>
            <div className={`p-4 sm:p-6 border-b ${isDark ? 'border-zinc-800' : 'border-gray-200'} flex items-center justify-between gap-2`}>
              <div className="flex-1 min-w-0">
                <h2 className={`text-lg sm:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {videoSelectionMode === 'new-trend' 
                    ? 'Select Videos for New Trend' 
                    : 'Add Videos to Trend'}
                </h2>
                <p className={`${isDark ? 'text-zinc-500' : 'text-gray-500'} text-xs sm:text-sm mt-1`}>
                  {videoSelectionMode === 'new-trend' 
                    ? 'Select at least 3 videos to create a new trend' 
                    : 'Select videos to add to this trend'}
                </p>
                {videoSelectionMode === 'new-trend' && (
                  <div className="mt-4">
                    <label className={`${isDark ? 'text-zinc-300' : 'text-gray-700'} text-sm mb-2 block`}>Trend Name</label>
                    <input
                      type="text"
                      value={trendName}
                      onChange={(e) => setTrendName(e.target.value)}
                      placeholder="Enter a name for this trend"
                      className={`w-full px-4 py-2 ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-300'} border rounded-lg focus:outline-none focus:border-cyan-500 text-sm`}
                    />
                  </div>
                )}
              </div>
              <button
                onClick={() => {
                  setShowVideoSelectionModal(false);
                  setVideoSelectionMode(null);
                  setSelectedVideosForModal(new Set());
                }}
                className={`p-2 ${isDark ? 'hover:bg-zinc-800' : 'hover:bg-gray-100'} rounded-lg flex-shrink-0 touch-manipulation`}
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div 
              ref={videoSelectionModalScrollRef}
              className="flex-1 overflow-y-auto p-6"
            >
              {/* Selection counter */}
              <div className={`mb-4 p-4 rounded-xl ${
                selectedVideosForModal.size >= 3 
                  ? 'bg-green-500/10 border border-green-500/30' 
                  : 'bg-amber-500/10 border border-amber-500/30'
              }`}>
                <div className="flex items-center justify-between">
                  <span className={selectedVideosForModal.size >= 3 ? 'text-green-400' : 'text-amber-400'}>
                    {selectedVideosForModal.size} video{selectedVideosForModal.size !== 1 ? 's' : ''} selected
                    {videoSelectionMode === 'new-trend' && selectedVideosForModal.size < 3 && (
                      <span className="ml-2">(need {3 - selectedVideosForModal.size} more)</span>
                    )}
                  </span>
                  {selectedVideosForModal.size >= 3 && videoSelectionMode === 'new-trend' && (
                    <span className="text-green-400">✓</span>
                  )}
                </div>
              </div>

              {/* Video grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {libraryVideos.map((video) => {
                  // Get video URL
                  let videoUrl = video.video_url;
                  if (!videoUrl && video.apify_metadata) {
                    const meta = typeof video.apify_metadata === 'string' 
                      ? JSON.parse(video.apify_metadata) 
                      : video.apify_metadata;
                    videoUrl = meta.videoUrl || meta.video_url || meta.mediaUrls?.[0] || meta.webVideoUrl;
                  }
                  
                  const isSelected = selectedVideosForModal.has(video.id);
                  
                  return (
                    <div
                      key={video.id}
                      data-video-id={video.id}
                      className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                        isSelected 
                          ? 'border-cyan-500 ring-2 ring-cyan-500/50' 
                          : isDark ? 'border-zinc-700 hover:border-zinc-600' : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {/* Video preview on click */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (videoUrl) {
                            setVideoPreview({
                              videoUrl,
                              creator: getCreatorUsername(video),
                              platform: video.platform,
                              views: getApifyValue(video, 'views'),
                              likes: getApifyValue(video, 'likes'),
                              video: video // Include full video object for metadata
                            });
                          }
                        }}
                        className="w-full"
                      >
                        <div className={`aspect-[9/16] ${isDark ? 'bg-zinc-800' : 'bg-gray-100'} relative`}>
                          {videoUrl ? (
                            <video
                              src={videoUrl}
                              className="w-full h-full object-cover"
                              muted
                              loop
                              playsInline
                              onMouseEnter={(e) => {
                                const video = e.target;
                                const playPromise = video.play();
                                if (playPromise !== undefined) {
                                  playPromise.catch((error) => {
                                    // Play was interrupted or failed, ignore error
                                    // This is expected when pause() is called quickly
                                  });
                                }
                              }}
                              onMouseLeave={(e) => {
                                const video = e.target;
                                // Pause the video
                                const pausePromise = video.pause();
                                if (pausePromise !== undefined) {
                                  pausePromise.catch(() => {
                                    // Pause error, ignore
                                  });
                                }
                                // Reset to beginning
                                video.currentTime = 0;
                              }}
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                              <Video className="h-8 w-8 text-white/30" />
                            </div>
                          )}
                          <div className="absolute top-1 left-1 z-10">
                            {video.platform === 'tiktok' ? (
                              <div className="w-3 h-3 bg-black rounded flex items-center justify-center text-[6px] text-white">TT</div>
                            ) : (
                              <div className="w-3 h-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded flex items-center justify-center text-[6px] text-white">IG</div>
                            )}
                          </div>
                        </div>
                        <div className={`p-2 ${isDark ? 'bg-zinc-900' : 'bg-gray-50'}`}>
                          <div className={`text-xs font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>@{getCreatorUsername(video)}</div>
                          <div className={`text-xs ${isDark ? 'text-zinc-500' : 'text-gray-500'} truncate`}>
                            {formatNumber(getApifyValue(video, 'views'))} views
                          </div>
                        </div>
                      </button>
                      {/* Selection checkbox overlay */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedVideosForModal(prev => {
                            const next = new Set(prev);
                            if (next.has(video.id)) {
                              next.delete(video.id);
                            } else {
                              next.add(video.id);
                            }
                            return next;
                          });
                        }}
                        className={`absolute top-2 right-2 z-20 w-6 h-6 ${isDark ? 'bg-zinc-900/80 hover:bg-zinc-800' : 'bg-white/80 hover:bg-gray-100'} rounded flex items-center justify-center transition-colors`}
                        title="Select video"
                      >
                        {isSelected ? (
                          <CheckCircle className="h-5 w-5 text-cyan-500" />
                        ) : (
                          <div className={`w-4 h-4 border-2 ${isDark ? 'border-zinc-400' : 'border-gray-400'} rounded`} />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className={`p-6 border-t ${isDark ? 'border-zinc-800' : 'border-gray-200'} flex gap-3`}>
              <button
                onClick={() => {
                  setShowVideoSelectionModal(false);
                  setVideoSelectionMode(null);
                  setSelectedVideosForModal(new Set());
                  setTrendName('');
                }}
                className={`flex-1 px-4 py-3 ${isDark ? 'bg-zinc-800 hover:bg-zinc-700' : 'bg-gray-100 hover:bg-gray-200'} rounded-xl ${isDark ? 'text-white' : 'text-gray-900'}`}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (videoSelectionMode === 'new-trend') {
                    if (selectedVideosForModal.size < 3) {
                      toast.error('Please select at least 3 videos');
                      return;
                    }
                    // Create new trend with selected videos
                    setIsLoading(true);
                      const videoIds = Array.from(selectedVideosForModal);
                    // Start 20 second timer for trend generation
                    let trendTimerSeconds = 20;
                    setTrendTimer(trendTimerSeconds);
                    let trendTimerInterval = setInterval(() => {
                      trendTimerSeconds--;
                      if (trendTimerSeconds > 0) {
                        setTrendTimer(trendTimerSeconds);
                      } else {
                        clearInterval(trendTimerInterval);
                        setTrendTimer(null);
                      }
                    }, 1000);
                    
                    try {
                      const response = await fetch('/api/trend-spotter/submit', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          videoIds,
                          userId,
                          trendName: trendName.trim() || undefined
                        })
                      });
                      const result = await response.json();
                      if (result.success) {
                        // Don't clear timer yet - keep it running until job completes
                        toast.success('Creating trend... This may take a moment.');
                        // Poll for job status
                        const jobId = result.jobId;
                        const pollInterval = setInterval(async () => {
                          try {
                            const statusResponse = await fetch(`/api/trend-spotter/jobs/${jobId}`);
                            const statusResult = await statusResponse.json();
                            if (statusResult.success && statusResult.job) {
                              if (statusResult.job.status === 'completed') {
                                clearInterval(pollInterval);
                                clearInterval(trendTimerInterval);
                                setTrendTimer(null);
                                toast.success('Trend created! It will be pending until approved.');
                                await loadLibrary(true); // Force reload after creating trend
                                await loadTrends();
                                // If a trend ID was returned, reload its videos in case user views it
                                if (statusResult.job.templateId) {
                                  // Small delay to ensure database is updated
                                  setTimeout(() => {
                                    if (selectedTrend && selectedTrend.id === statusResult.job.templateId) {
                                      loadTrendVideos(statusResult.job.templateId);
                                    }
                                  }, 500);
                                }
                                setShowVideoSelectionModal(false);
                                setVideoSelectionMode(null);
                                setSelectedVideosForModal(new Set());
                                setTrendName('');
                                setView('trends');
                                setIsLoading(false);
                              } else if (statusResult.job.status === 'failed') {
                                clearInterval(pollInterval);
                                clearInterval(trendTimerInterval);
                                setTrendTimer(null);
                                toast.error(statusResult.job.error || 'Failed to create trend');
                                setIsLoading(false);
                              }
                            }
                          } catch (error) {
                            console.error('Error polling job status:', error);
                          }
                        }, 2000);
                        
                        // Stop polling after 5 minutes
                        setTimeout(() => {
                          clearInterval(pollInterval);
                          if (isLoading) {
                            setIsLoading(false);
                            toast('Trend creation is taking longer than expected. Check the Trends tab for updates.', { duration: 5000 });
                          }
                        }, 300000);
                      } else {
                        clearInterval(trendTimerInterval);
                        setTrendTimer(null);
                        toast.error(result.error || 'Failed to create trend');
                        setIsLoading(false);
                      }
                    } catch (error) {
                      console.error('Error creating trend:', error);
                      clearInterval(trendTimerInterval);
                      setTrendTimer(null);
                      toast.error('Failed to create trend');
                      setIsLoading(false);
                    }
                  } else if (videoSelectionMode?.type === 'add-to-trend') {
                    // Add videos to existing trend
                    setIsLoading(true);
                    try {
                      const videoIds = Array.from(selectedVideosForModal);
                      // Assign trend to selected videos
                      await Promise.all(
                        videoIds.map(videoId =>
                          fetch(`/api/trend-spotter/library/${videoId}/trend?userId=${encodeURIComponent(userId)}`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ trendId: videoSelectionMode.trendId })
                          })
                        )
                      );
                      toast.success(`Added ${videoIds.length} video(s) to trend`);
                      loadLibrary(true); // Force reload after adding videos to trend
                      loadTrends(false); // Refresh trends list (no top videos needed)
                      // Reload trend videos if this trend is currently selected
                      if (selectedTrend && selectedTrend.id === videoSelectionMode.trendId) {
                        await loadTrendVideos(videoSelectionMode.trendId, { skipScrollToTop: true });
                      }
                      setShowVideoSelectionModal(false);
                      setVideoSelectionMode(null);
                      setSelectedVideosForModal(new Set());
                    } catch (error) {
                      console.error('Error adding videos to trend:', error);
                      toast.error('Failed to add videos to trend');
                    } finally {
                      setIsLoading(false);
                    }
                  }
                }}
                disabled={
                  isLoading || 
                  (videoSelectionMode === 'new-trend' && selectedVideosForModal.size < 3) ||
                  selectedVideosForModal.size === 0
                }
                className="flex-1 px-4 py-3 bg-cyan-500 hover:bg-cyan-400 disabled:bg-zinc-700 disabled:text-zinc-500 rounded-xl font-medium flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <div className="flex flex-col items-center gap-2 w-full">
                    <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Creating...</span>
                    </div>
                    {trendTimer !== null && videoSelectionMode === 'new-trend' && (
                      <div className="w-full max-w-[200px]">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-cyan-300">{trendTimer}s remaining</span>
                          <span className="text-zinc-400">{Math.round(((20 - trendTimer) / 20) * 100)}%</span>
                        </div>
                        <div className="w-full bg-zinc-700 rounded-full h-1.5 overflow-hidden">
                          <div 
                            className="bg-cyan-500 h-full transition-all duration-300"
                            style={{ width: `${((20 - trendTimer) / 20) * 100}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  videoSelectionMode === 'new-trend'
                    ? `Create Trend (${selectedVideosForModal.size} videos)`
                    : `Add ${selectedVideosForModal.size} Video${selectedVideosForModal.size !== 1 ? 's' : ''}`
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generated Ideas Modal */}
      {showGeneratedIdeasModal && generatedIdeas.length > 0 && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className={`rounded-2xl border w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'}`}>
            <div className={`p-4 sm:p-6 border-b flex items-center justify-between gap-2 ${isDark ? 'border-zinc-800' : 'border-gray-200'}`}>
              <div className="flex-1 min-w-0">
                <h2 className={`text-lg sm:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Generated briefs</h2>
                <p className={`text-xs sm:text-sm mt-1 ${isDark ? 'text-zinc-500' : 'text-gray-600'}`}>
                  Review and edit briefs, then select which ones to add to your Content Board
                </p>
              </div>
              <button
                onClick={() => {
                  setShowGeneratedIdeasModal(false);
                  setGeneratedIdeas([]);
                  setSelectedIdeasForBoard(new Set());
                }}
                className={`p-2 rounded-lg flex-shrink-0 touch-manipulation ${isDark ? 'hover:bg-zinc-800 text-zinc-300' : 'hover:bg-gray-100 text-gray-600'}`}
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                {generatedIdeas.map((idea, idx) => {
                  const ideaId = idea.id || `generated-${idx}`;
                  const isSelected = selectedIdeasForBoard.has(ideaId);
                  const isEditing = editingIdeaField?.ideaId === ideaId;
                  const editingField = isEditing ? editingIdeaField.field : null;
                  
                  return (
                    <div
                      key={ideaId}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        isSelected
                          ? 'border-cyan-500 bg-cyan-500/10'
                          : isDark ? 'border-zinc-800 bg-zinc-800/50' : 'border-gray-200 bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            setSelectedIdeasForBoard(prev => {
                              const next = new Set(prev);
                              if (e.target.checked) {
                                next.add(ideaId);
                              } else {
                                next.delete(ideaId);
                              }
                              return next;
                            });
                          }}
                          className="mt-1 w-5 h-5 text-cyan-500 rounded focus:ring-cyan-500"
                        />
                        <div className="flex-1 space-y-3">
                          {/* Angle */}
                          <div>
                            {editingField === 'angle' ? (
                              <input
                                type="text"
                                value={idea.angle || ''}
                                onChange={(e) => {
                                  const updated = [...generatedIdeas];
                                  updated[idx].angle = e.target.value;
                                  setGeneratedIdeas(updated);
                                }}
                                onBlur={() => setEditingIdeaField(null)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.target.blur();
                                  }
                                }}
                                className={`text-lg font-semibold border border-cyan-500 rounded-lg px-3 py-2 w-full focus:outline-none focus:border-cyan-400 ${isDark ? 'bg-zinc-800' : 'bg-white border-gray-300'}`}
                                autoFocus
                              />
                            ) : (
                              <h3
                                className={`text-lg font-semibold cursor-pointer hover:text-cyan-500 transition-colors ${isDark ? 'text-white' : 'text-gray-900'}`}
                                onClick={() => setEditingIdeaField({ ideaId, field: 'angle' })}
                              >
                                {idea.angle || 'Untitled Idea'}
                              </h3>
                            )}
                          </div>
                          
                          {/* Shot List */}
                          <div>
                            <h4 className={`text-xs font-medium uppercase tracking-wider mb-2 ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>SHOT LIST</h4>
                            {editingField === 'shots' ? (
                              <div className="space-y-2">
                                {Array.isArray(idea.shots) && idea.shots.map((shot, shotIdx) => (
                                  <div key={shotIdx} className="flex items-start gap-2">
                                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 mt-1 ${isDark ? 'bg-zinc-700' : 'bg-gray-200 text-gray-700'}`}>{shotIdx + 1}</span>
                                    <textarea
                                      value={shot}
                                      onChange={(e) => {
                                        const updated = [...generatedIdeas];
                                        updated[idx].shots[shotIdx] = e.target.value;
                                        setGeneratedIdeas(updated);
                                      }}
                                      className={`flex-1 px-3 py-2 border border-cyan-500 rounded-lg text-sm focus:outline-none focus:border-cyan-400 resize-none ${isDark ? 'bg-zinc-800' : 'bg-white border-gray-300'}`}
                                      rows={2}
                                    />
                                    <button
                                      onClick={() => {
                                        const updated = [...generatedIdeas];
                                        updated[idx].shots.splice(shotIdx, 1);
                                        setGeneratedIdeas(updated);
                                      }}
                                      className="p-2 text-red-400 hover:text-red-300 mt-1"
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  </div>
                                ))}
                                <button
                                  onClick={() => {
                                    const updated = [...generatedIdeas];
                                    if (!updated[idx].shots) updated[idx].shots = [];
                                    updated[idx].shots.push('');
                                    setGeneratedIdeas(updated);
                                  }}
                                  className={`w-full px-3 py-2 border border-dashed rounded-lg text-sm flex items-center justify-center gap-2 ${isDark ? 'bg-zinc-800 border-zinc-600 text-zinc-400 hover:border-cyan-500 hover:text-cyan-400' : 'bg-gray-50 border-gray-300 text-gray-500 hover:border-cyan-500 hover:text-cyan-600'}`}
                                >
                                  <Plus className="h-4 w-4" />
                                  Add Shot
                                </button>
                                <button
                                  onClick={() => setEditingIdeaField(null)}
                                  className="w-full px-3 py-2 bg-cyan-500 hover:bg-cyan-400 rounded-lg text-sm font-medium text-white"
                                >
                                  Done Editing
                                </button>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {Array.isArray(idea.shots) && idea.shots.length > 0 ? (
                                  idea.shots.map((shot, shotIdx) => (
                                    <div key={shotIdx} className={`flex items-start gap-3 p-2 rounded-lg ${isDark ? 'bg-zinc-800/50' : 'bg-gray-100'}`}>
                                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 ${isDark ? 'bg-zinc-700' : 'bg-gray-200 text-gray-700'}`}>{shotIdx + 1}</span>
                                      <span className={`text-sm ${isDark ? 'text-zinc-300' : 'text-gray-800'}`}>{shot}</span>
                                    </div>
                                  ))
                                ) : (
                                  <p className={`text-sm ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>No shots defined</p>
                                )}
                                <button
                                  onClick={() => setEditingIdeaField({ ideaId, field: 'shots' })}
                                  className="text-xs text-cyan-500 hover:text-cyan-400"
                                >
                                  Edit Shots
                                </button>
                              </div>
                            )}
                          </div>
                          
                          {/* Template: Text overlay, People/characters, Locations, Script */}
                          {(idea.template?.text_overlay || idea.template?.visual?.people_characters || idea.template?.visual?.locations || (Array.isArray(idea.template?.sound?.voiceover?.script) && idea.template.sound.voiceover.script.length > 0) || (Array.isArray(idea.template?.sound?.sound_effects) && idea.template.sound.sound_effects.length > 0)) && (
                            <div className={`space-y-2 pt-2 border-t ${isDark ? 'border-zinc-700' : 'border-gray-200'}`}>
                              {idea.template?.text_overlay && (
                                <div>
                                  <h4 className={`text-xs font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>TEXT OVERLAY</h4>
                                  <p className={`text-sm ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>{idea.template.text_overlay}</p>
                                </div>
                              )}
                              {idea.template?.visual?.people_characters && (
                                <div>
                                  <h4 className={`text-xs font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>PEOPLE / CHARACTERS</h4>
                                  <p className={`text-sm ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>{idea.template.visual.people_characters}</p>
                                </div>
                              )}
                              {idea.template?.visual?.locations && (
                                <div>
                                  <h4 className={`text-xs font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>LOCATIONS</h4>
                                  <p className={`text-sm ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>{idea.template.visual.locations}</p>
                                </div>
                              )}
                              {Array.isArray(idea.template?.sound?.voiceover?.script) && idea.template.sound.voiceover.script.length > 0 && (
                                <div>
                                  <h4 className={`text-xs font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>SCRIPT (VOICEOVER)</h4>
                                  <ul className={`text-sm ${isDark ? 'text-zinc-300' : 'text-gray-700'} list-disc list-inside space-y-0.5`}>
                                    {idea.template.sound.voiceover.script.map((line, i) => (
                                      <li key={i}>{line}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {Array.isArray(idea.template?.sound?.sound_effects) && idea.template.sound.sound_effects.length > 0 && (
                                <div>
                                  <h4 className={`text-xs font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>SOUND EFFECTS</h4>
                                  <ul className={`text-sm ${isDark ? 'text-zinc-300' : 'text-gray-700'} list-disc list-inside space-y-0.5`}>
                                    {idea.template.sound.sound_effects.map((effect, i) => (
                                      <li key={i}>{effect}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Caption */}
                          <div>
                            <h4 className={`text-xs font-medium uppercase tracking-wider mb-2 ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>CAPTION</h4>
                            {editingField === 'caption' ? (
                              <textarea
                                value={idea.caption || ''}
                                onChange={(e) => {
                                  const updated = [...generatedIdeas];
                                  updated[idx].caption = e.target.value;
                                  setGeneratedIdeas(updated);
                                }}
                                onBlur={() => setEditingIdeaField(null)}
                                className={`w-full px-3 py-2 border border-cyan-500 rounded-lg text-sm focus:outline-none focus:border-cyan-400 resize-none ${isDark ? 'bg-zinc-800' : 'bg-white border-gray-300'}`}
                                rows={3}
                                autoFocus
                              />
                            ) : (
                              <div>
                                <p className={`text-sm mb-2 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>{idea.caption || 'No caption'}</p>
                                <button
                                  onClick={() => setEditingIdeaField({ ideaId, field: 'caption' })}
                                  className="text-xs text-cyan-500 hover:text-cyan-400"
                                >
                                  Edit Caption
                                </button>
                              </div>
                            )}
                          </div>
                          
                          {/* Production Notes */}
                          <div>
                            <h4 className={`text-xs font-medium uppercase tracking-wider mb-2 ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>PRODUCTION NOTES</h4>
                            {editingField === 'notes' ? (
                              <textarea
                                value={idea.notes || ''}
                                onChange={(e) => {
                                  const updated = [...generatedIdeas];
                                  updated[idx].notes = e.target.value;
                                  setGeneratedIdeas(updated);
                                }}
                                onBlur={() => setEditingIdeaField(null)}
                                className={`w-full px-3 py-2 border border-cyan-500 rounded-lg text-sm focus:outline-none focus:border-cyan-400 resize-none ${isDark ? 'bg-zinc-800' : 'bg-white border-gray-300'}`}
                                rows={2}
                                autoFocus
                              />
                            ) : (
                              <div>
                                <p className={`text-sm mb-2 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>{idea.notes || 'No production notes'}</p>
                                <button
                                  onClick={() => setEditingIdeaField({ ideaId, field: 'notes' })}
                                  className="text-xs text-cyan-500 hover:text-cyan-400"
                                >
                                  Edit Notes
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className={`p-6 border-t space-y-4 ${isDark ? 'border-zinc-800' : 'border-gray-200'}`}>
              <div>
                <label className={`text-sm mb-2 block ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>Give more direction (optional)</label>
                <textarea
                  value={regenerateFeedback}
                  onChange={(e) => setRegenerateFeedback(e.target.value)}
                  placeholder="e.g., Make it more humorous, focus on product benefits, use a different angle..."
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:border-cyan-500 resize-none text-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-300'}`}
                  rows={3}
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowGeneratedIdeasModal(false);
                    setGeneratedIdeas([]);
                    setSelectedIdeasForBoard(new Set());
                    setRegenerateFeedback('');
                  }}
                  className={`flex-1 px-4 py-3 rounded-xl ${isDark ? 'bg-zinc-800 hover:bg-zinc-700' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    setIsLoading(true);
                    try {
                      const response = await fetch('/api/trend-spotter/generate-ideas', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          trendId: selectedTrend?.id,
                          userId,
                          feedback: regenerateFeedback.trim() || undefined
                        })
                      });

                      const result = await response.json();
                      if (result.success && result.ideas) {
                        setGeneratedIdeas(result.ideas.map((idea, idx) => ({ ...idea, id: `generated-${idx}` })));
                        setSelectedIdeasForBoard(new Set(result.ideas.map((_, idx) => `generated-${idx}`)));
                        setRegenerateFeedback('');
                        toast.success('Briefs regenerated!');
                      } else {
                        toast.error(result.error || 'Failed to regenerate briefs');
                      }
                    } catch (error) {
                      console.error('Error regenerating briefs:', error);
                      toast.error('Failed to regenerate briefs');
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                  disabled={isLoading}
                  className={`px-4 py-3 bg-purple-500 hover:bg-purple-400 rounded-xl font-medium flex items-center justify-center gap-2 text-white disabled:opacity-70 ${isDark ? 'disabled:bg-zinc-700 disabled:text-zinc-400' : 'disabled:bg-gray-300 disabled:text-gray-600'}`}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Regenerating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      Regenerate briefs
                    </>
                  )}
                </button>
                <button
                  onClick={async () => {
                    if (selectedIdeasForBoard.size === 0) {
                      toast.error('Please select at least one idea to add to your board');
                      return;
                    }
                    
                    // Check limits before saving
                    if (userLimits.exceeded && (userLimits.reason === 'idea_limit' || userLimits.ideaLimitExceeded)) {
                      setShowIdeaLimitModal(true);
                      return;
                    }
                    
                    setIsLoading(true);
                    try {
                      let savedCount = 0;
                      let hitLimit = false;
                      for (const ideaId of selectedIdeasForBoard) {
                        // Check if we've hit the limit
                        if (hitLimit) break;
                        
                        const idea = generatedIdeas.find(i => (i.id || `generated-${generatedIdeas.indexOf(i)}`) === ideaId);
                        if (idea) {
                          try {
                            const response = await fetch('/api/trend-spotter/content-board/briefs', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                angle: idea.angle || '',
                                shots: idea.shots || [],
                                caption: idea.caption || '',
                                notes: idea.notes || '',
                                template: idea.template || null,
                                status: 'ideas',
                                trend_id: selectedTrend?.id,
                                userId,
                                creation_source:
                                  generatedIdeasSourceRef.current === 'single-video'
                                    ? 'video_generate'
                                    : 'trend_generate'
                              })
                            });
                            
                            const result = await response.json();
                            
                            // Check for paywall error
                            if (response.status === 403 || result.error === 'idea_limit_exceeded' || result.requiresPayment) {
                              hitLimit = true;
                              setShowGeneratedIdeasModal(false);
                              setShowIdeaLimitModal(true);
                              setIsLoading(false);
                              return;
                            }
                            
                            if (result.success) {
                              savedCount++;
                              // Add the new brief to the state immediately
                              const newBrief = {
                                ...result.brief,
                                trend_id: result.brief.trend_id ? (typeof result.brief.trend_id === 'string' ? parseInt(result.brief.trend_id) : result.brief.trend_id) : null,
                                tags: Array.isArray(result.brief.tags) ? result.brief.tags : [],
                                collaborators: result.brief.collaborators || []
                              };
                              setBriefs(prevBriefs => {
                                // Check if brief already exists (in case of duplicate)
                                const exists = prevBriefs.some(b => b.id === newBrief.id);
                                if (exists) {
                                  // Update existing brief
                                  return prevBriefs.map(b => b.id === newBrief.id ? newBrief : b);
                                } else {
                                  // Add new brief at the beginning
                                  return [newBrief, ...prevBriefs];
                                }
                              });
                            }
                          } catch (err) {
                            console.error('Error saving idea:', err);
                          }
                        }
                      }
                      
                      if (!hitLimit) {
                      toast.success(`Added ${savedCount} idea${savedCount !== 1 ? 's' : ''} to your Content Board!`);
                      if (generatedIdeasSourceRef.current === 'trend') completeOnboardingStage(2);
                      else if (generatedIdeasSourceRef.current === 'single-video') completeOnboardingStage(4);
                      setShowGeneratedIdeasModal(false);
                      setGeneratedIdeas([]);
                      setSelectedIdeasForBoard(new Set());
                      setRegenerateFeedback('');
                      // Switch to board view so users can continue editing
                      setView('board');
                      // Update URL to reflect board view
                      const newSearchParams = new URLSearchParams(searchParams);
                      newSearchParams.set('view', 'board');
                      setSearchParams(newSearchParams, { replace: true });
                      // Force reload to ensure we have the latest data (including any server-side updates)
                      loadBriefs(true);
                        // Refresh limits after saving
                        setTimeout(() => checkUserLimits(), 1000);
                      }
                    } catch (error) {
                      console.error('Error saving ideas:', error);
                      toast.error('Failed to save ideas');
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                  disabled={isLoading || selectedIdeasForBoard.size === 0}
                  className={`flex-1 px-4 py-3 bg-cyan-500 hover:bg-cyan-400 rounded-xl font-medium flex items-center justify-center gap-2 text-white disabled:opacity-70 ${isDark ? 'disabled:bg-zinc-700 disabled:text-zinc-400' : 'disabled:bg-gray-300 disabled:text-gray-600'}`}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <ClipboardList className="h-4 w-4" />
                      Send {selectedIdeasForBoard.size} Idea{selectedIdeasForBoard.size !== 1 ? 's' : ''} to My Idea Board
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-zinc-900 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-zinc-800">
            <div className="sticky top-0 bg-zinc-900 border-b border-zinc-800 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MessageCircle className="h-6 w-6 text-indigo-400" />
                <h2 className="text-xl font-bold text-white">We'd Love Your Feedback!</h2>
              </div>
              <button
                onClick={() => {
                  setShowFeedbackModal(false);
                  setFeedbackRating(0);
                  setFavoriteFeatures([]);
                  setTopAsks('');
                }}
                className="text-zinc-400 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Overall Rating */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-3">
                  How would you rate your overall experience? <span className="text-red-400">*</span>
                </label>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      onClick={() => setFeedbackRating(rating)}
                      className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                        feedbackRating === rating
                          ? 'border-indigo-500 bg-indigo-500/20 text-indigo-400'
                          : 'border-zinc-700 bg-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-300'
                      }`}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <Star 
                          className={`h-6 w-6 ${feedbackRating >= rating ? 'fill-current' : ''}`}
                        />
                        <span className="text-xs font-medium">{rating}</span>
                      </div>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-zinc-500 mt-2">
                  {feedbackRating === 0 && 'Select a rating from 1 (poor) to 5 (excellent)'}
                  {feedbackRating === 1 && '😞 We\'re sorry to hear that. We\'d love to improve!'}
                  {feedbackRating === 2 && '😕 Thanks for letting us know. We\'ll work on it!'}
                  {feedbackRating === 3 && '😐 We appreciate your feedback!'}
                  {feedbackRating === 4 && '😊 Great to hear! We\'re glad you\'re enjoying it!'}
                  {feedbackRating === 5 && '🎉 Amazing! Thank you so much!'}
                </p>
              </div>

              {/* Favorite Features */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-3">
                  Which features do you love the most? (Select all that apply)
                </label>
                <div className="space-y-2">
                  {[
                    { value: 'video_library', label: '📋 Video Library', description: 'Save and organize your favorite videos' },
                    { value: 'trend_library', label: '📈 Trend Library', description: 'Discover and analyze trending content' },
                    { value: 'content_brief', label: '📝 Content Brief', description: 'Create and manage your content ideas' }
                  ].map((feature) => (
                    <button
                      key={feature.value}
                      onClick={() => toggleFavoriteFeature(feature.value)}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                        favoriteFeatures.includes(feature.value)
                          ? 'border-indigo-500 bg-indigo-500/20'
                          : 'border-zinc-700 bg-zinc-800 hover:border-zinc-600'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-white">{feature.label}</div>
                          <div className="text-sm text-zinc-400 mt-1">{feature.description}</div>
                        </div>
                        {favoriteFeatures.includes(feature.value) && (
                          <CheckCircle className="h-5 w-5 text-indigo-400" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Top Asks */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-3">
                  What would make ShowStop even better for you?
                </label>
                <textarea
                  value={topAsks}
                  onChange={(e) => setTopAsks(e.target.value)}
                  placeholder="Share your ideas, suggestions, or feature requests... We're all ears! 👂"
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                  rows={4}
                />
                <p className="text-xs text-zinc-500 mt-2">
                  Your feedback helps us build features you'll actually use
                </p>
              </div>

              {/* Submit Button */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowFeedbackModal(false);
                    setFeedbackRating(0);
                    setFavoriteFeatures([]);
                    setTopAsks('');
                  }}
                  className="flex-1 px-4 py-3 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitFeedback}
                  disabled={submittingFeedback || feedbackRating === 0}
                  className="flex-1 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-zinc-700 disabled:text-zinc-500 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                  {submittingFeedback ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <MessageCircle className="h-4 w-4" />
                      Send Feedback
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Welcome Modal for New Users */}
      {showWelcomeModal && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/70"
            onClick={handleWelcomeModalClose}
          />
          
          {/* Modal */}
          <div className="relative bg-zinc-900 border-2 border-indigo-500 rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8">
            {/* Close Button */}
            <button
              onClick={handleWelcomeModalClose}
              className="absolute top-4 right-4 text-zinc-400 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Content */}
            <div className="text-center">
              <div className="mb-4 flex justify-center">
                <div className="w-16 h-16 bg-indigo-500/20 rounded-full flex items-center justify-center">
                  <Sparkles className="h-8 w-8 text-indigo-400" />
                </div>
              </div>
              
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                Welcome to Trend Spotter! 🎉
              </h2>
              
              <p className="text-zinc-300 mb-6 leading-relaxed">
                Discover trending videos, create content briefs, and generate AI-powered ideas for your brand.
              </p>
              
              <p className="text-zinc-400 text-sm mb-6">
                Complete 5 stages by doing each action—add brand details, generate a brief, save videos, create a brief from a video, and manage your board. We&apos;ll show one stage at a time; unlock the next when you complete the current one.
              </p>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleWelcomeModalClose}
                  className="flex-1 px-4 py-3 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg font-medium transition-colors"
                >
                  I'll explore on my own
                </button>
                <button
                  onClick={handleWelcomeModalStartTour}
                  className="flex-1 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <HelpCircle className="h-4 w-4" />
                  Start Guided Tour
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Guided Tour (current stage only; advance only on real action) */}
      <GuidedTour
        steps={activeTourSteps}
        isActive={showTour}
        onClose={handleTourClose}
        onComplete={handleTourComplete}
        currentView={view}
        onViewChange={handleTourViewChange}
        onStepChange={(stepIndex) => {
          if (stepIndex !== 0 && showUserMenu) setShowUserMenu(false);
        }}
        singleStepMode
        stepIndex={onboardingUnlockedStage >= 1 && onboardingUnlockedStage <= 5 ? onboardingUnlockedStage - 1 : 0}
        totalSteps={5}
        stepLabel="Stage"
      />
      <OnboardingCelebration
        stage={celebrationStage}
        isOpen={showCelebration}
        onClose={() => handleCelebrationContinue(celebrationStage)}
      />
    </div>
  );
};

export default TrendSpotter;

